import { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { Archive, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Columns3, Download, Eye, EyeOff, FileText, Filter, Loader2, MapPinned, MessageCircle, Phone, RefreshCw, RotateCcw, Save, Search, Trash2, Upload, UserPlus, X, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { toast } from 'sonner';
import { supplyService } from '../services/supplyService';
import { WorkiomTablePagination } from '../components/WorkiomTablePagination';
import { ShipmentWizardSection, ShipmentWizardStepTabs } from '../components/ShipmentWizardShared';
import { ShipmentWizardModal } from '../components/ShipmentWizardModal';
import { BulkShipmentImportModal } from '../components/BulkShipmentImportModal';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { shipmentService } from '../services/shipmentService';
import { vehicleService } from '../services/vehicleService';
import { driverService } from '../services/driverService';
import { warehouseService } from '../services/warehouseService';
import { shiftService } from '../services/shiftService';
import { documentService } from '../services/documentService';
import { taskNotificationService } from '../services/taskNotificationService';
import { bidService } from '../services/bidService';
import { settingsService } from '../services/settingsService';
import { accountingService } from '../services/accountingService';
import { archiveService } from '../services/archiveService';
import { userService } from '../services/userService';
import { FlexibleTracking } from './FlexibleTracking';
import { normalizeVehicleType, parseVehicleTypes } from '../constants/vehicleTypes';
import { useAuth } from '../contexts/AuthContext';
import { hasUserRole } from '../utils/userRoles';
import { staticDefinitionService } from '../services/staticDefinitionService';

const toNumber = (value: string | number | undefined) => Number(String(value || '0').replace(',', '.')) || 0;

const resolveFileUrl = (filePath?: string | null) => {
  if (!filePath) return '#';
  const pathStr = String(filePath);
  if (pathStr.startsWith('http://') || pathStr.startsWith('https://')) return pathStr;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${pathStr.startsWith('/') ? pathStr : `/${pathStr}`}`;
};

const sanitizeShipmentNote = (value?: string | null) => {
  const cleaned = String(value || '')
    .split(' | ')
    .map((part) => String(part || '').trim())
    .filter((part) => part && !/^(Sipariş Tarihi:|Termin:|Yükleme Ara Noktaları:|Boşaltma Ara Noktaları:)/.test(part))
    .join(' | ')
    .trim();
  return cleaned || '-';
};

const DEFAULT_SPOT_VEHICLE_TYPE_OPTIONS = [
  'Tır Tenteli Kayar Perde',
  'Tır Frigo',
  'Tır Havuz',
  'Tır Damper',
  'Kırkayak',
  'Kamyon Tenteli',
  'Kamyonet',
];

type OperationRow = {
  rowKey: string;
  id: string;
  archiveId?: string;
  archiveCode?: string;
  invoiceNumber?: string;
  evrakSiraNo?: string;
  hasFuelPurchase?: string;
  folderNumber?: string;
  folderName?: string;
  archivedAt?: string;
  archivedByName?: string;
  sevkKodu: string;
  planKodu?: string;
  cekici: string;
  dorse: string;
  surucuAdi: string;
  surucuTelefon: string;
  tcKimlikNo: string;
  faturaBasligi: string;
  disNakliye: string;
  yuklemeMusterisi: string;
  bosaltmaMusterisi: string;
  yuklemeIli: string;
  yuklemeIlcesi?: string;
  bosaltmaIli: string;
  teslimIlcesi?: string;
  aracAtamaTarihi: string;
  yuklemeTarihi: string;
  terminTarihi?: string;
  randevuluTeslim?: string;
  ugrmaSayisi: number;
  navlun: number;
  bazFiyat?: number;
  seferKm: number;
  tlKm: number;
  lojistikTuru: string;
  hizmetTuru: string;
  operasyonNot: string;
  nearestHub: string;
  aracDurum: string;
  islemDurum: string;
  durum: string;
  accountingStatus: string;
  assignmentResponseStatus: string;
  atamaYaniti: string;
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  yuklemeKonum?: string;
  yuklemeAdres?: string;
  teslimAdres?: string;
  yukAgirlik?: number;
  yukHacim?: number;
  palletCount?: number;
  istenenKasaTipi?: string;
  yuklemeCikisTarihi?: string;
  teslimVarisTarihi?: string;
  odemeTarihi?: string;
  createdAt?: string;
  updatedAt?: string;
  shipmentContactPhone?: string;
  araNoktalar?: string;
  routePoints?: any[];
  pickupWarehouseId?: string | null;
  pickupShiftId?: string | null;
  yuklenecekVardiya?: string;
  createdBy?: string | null;
  sonNot?: string;
  sonNotYazan?: string;
};

type ColumnDef = {
  key: keyof OperationRow;
  label: string;
  width?: string;
  render?: (row: OperationRow) => string | number | JSX.Element;
};

type ManualProcessStatus =
  | 'tedarik-bekleniyor'
  | 'spot-teklif-bekleniyor'
  | 'atandi-islem-bekliyor'
  | 'yukleme-bekliyor'
  | 'yukleme-devam'
  | 'yukleme-tamamlandi'
  | 'tasima-yolunda'
  | 'teslim-noktasina-ulasildi'
  | 'bosaltma-bekliyor'
  | 'bosaltma-devam'
  | 'bosaltma-tamamlandi';

type ManualAccountingStatus =
  | 'none'
  | 'delivery-approval-pending'
  | 'delivery-rejected'
  | 'invoice-waiting'
  | 'invoice-rejected'
  | 'invoice-approval-pending'
  | 'payment-pre-approval'
  | 'payment-pending'
  | 'payment-completed';

type TrackingOperationTab =
  | 'atama-bekliyor'
  | 'atandi-islem-bekliyor'
  | 'yukleme-bekliyor'
  | 'yukleme-devam'
  | 'yukleme-tamamlandi'
  | 'tasima-yolunda'
  | 'teslim-noktasina-ulasildi'
  | 'bosaltma-bekliyor'
  | 'bosaltma-devam'
  | 'bosaltma-tamamlandi'
  | 'fatura-bekleyen'
  | 'odeme-bekleyen'
  | 'odeme-gerceklesen'
  | 'arsiv';

const ACTIVE_ASSIGNMENT_STATUSES = new Set([
  'atandi-islem-bekliyor',
  'yukleme-bekliyor',
  'yukleme-devam',
  'yukleme-tamamlandi',
  'tasima-yolunda',
  'teslim-noktasina-ulasildi',
  'bosaltma-bekliyor',
  'bosaltma-devam',
]);

const TRACKING_VISIBLE_COLUMNS_KEY = 'trackingVisibleColumns';
const TRACKING_BASIC_VIEW_KEY = 'trackingBasicView';
const TRACKING_SAVED_TABS_KEY = 'trackingSavedTabs';
const TRACKING_SHARED_TABS_KEY = 'trackingSharedTabs';
const TRACKING_MAIN_TAB_ID = 'main';
const REQUIRED_TRACKING_COLUMNS = ['disNakliye', 'nearestHub'];
const DEFAULT_TRACKING_COLUMNS = [
  'sevkKodu',
  'planKodu',
  'aracAtamaTarihi',
  'yuklemeTarihi',
  'randevuluTeslim',
  'yuklenecekVardiya',
  'yuklemeMusterisi',
  'yuklemeIli',
  'bosaltmaIli',
  'nearestHub',
  'cekici',
  'dorse',
  'surucuAdi',
  'surucuTelefon',
  'tcKimlikNo',
  'faturaBasligi',
  'disNakliye',
  'bosaltmaMusterisi',
  'hizmetTuru',
  'lojistikTuru',
  'operasyonNot',
  'aracDurum',
];

const withRequiredTrackingColumns = (columns: string[]) => [...new Set([...columns, ...REQUIRED_TRACKING_COLUMNS])];

type TrackingSavedViewTab = {
  id: string;
  name: string;
  visibility: 'private' | 'public';
  operationTab: TrackingOperationTab;
  search: string;
  externalTransportFilter: 'all' | 'evet' | 'hayir';
  filters: TrackingFilterRule[];
  sortColumn: keyof OperationRow | null;
  sortDirection: 'asc' | 'desc';
  pageSize: number;
};

type TrackingFilterOperator = 'contains' | 'notContains' | 'equals' | 'notEquals' | 'empty' | 'notEmpty' | 'dateEquals' | 'dateOnOrAfter' | 'dateOnOrBefore';
type TrackingFilterJoin = 'and' | 'or';
type TrackingFilterRule = {
  id: string;
  join: TrackingFilterJoin;
  column: keyof OperationRow;
  operator: TrackingFilterOperator;
  value: string;
};

const makeTrackingViewId = () => `tracking_${Date.now()}_${Math.random().toString(16).slice(2)}`;
const makeTrackingFilterId = () => `tracking_filter_${Date.now()}_${Math.random().toString(16).slice(2)}`;
const TRACKING_DATE_FILTER_COLUMNS = new Set<keyof OperationRow>(['aracAtamaTarihi', 'yuklemeTarihi', 'terminTarihi', 'createdAt', 'updatedAt']);

const buildUserScopedStorageKey = (baseKey: string) => {
  try {
    const raw = localStorage.getItem('currentUser');
    const parsed = raw ? JSON.parse(raw) : null;
    return `${baseKey}:${parsed?.id || 'anon'}`;
  } catch {
    return `${baseKey}:anon`;
  }
};

const MANUAL_PROCESS_STATUS_OPTIONS: Array<{ value: ManualProcessStatus; label: string }> = [
  { value: 'tedarik-bekleniyor', label: 'Tedarik Bekleniyor' },
  { value: 'spot-teklif-bekleniyor', label: 'Spot Teklif Bekleniyor' },
  { value: 'atandi-islem-bekliyor', label: 'Atandı İşlem Bekliyor' },
  { value: 'yukleme-bekliyor', label: 'Yükleme Bekliyor' },
  { value: 'yukleme-devam', label: 'Yükleme Devam' },
  { value: 'yukleme-tamamlandi', label: 'Yükleme Tamamlandı' },
  { value: 'tasima-yolunda', label: 'Teslim Yolunda' },
  { value: 'teslim-noktasina-ulasildi', label: 'Teslim Noktasına Ulaşıldı' },
  { value: 'bosaltma-bekliyor', label: 'Boşaltma Bekliyor' },
  { value: 'bosaltma-devam', label: 'Boşaltma Devam' },
  { value: 'bosaltma-tamamlandi', label: 'Boşaltma Tamamlandı' },
];

const MANUAL_ACCOUNTING_STATUS_OPTIONS: Array<{ value: ManualAccountingStatus; label: string }> = [
  { value: 'none', label: 'Muhasebe Dışı' },
  { value: 'delivery-approval-pending', label: 'Teslim Onayı Bekliyor' },
  { value: 'delivery-rejected', label: 'Teslim Reddedildi' },
  { value: 'invoice-waiting', label: 'Fatura Bekliyor' },
  { value: 'invoice-rejected', label: 'Fatura Reddedildi' },
  { value: 'invoice-approval-pending', label: 'Fatura Onayı Bekliyor' },
  { value: 'payment-pre-approval', label: 'Fatura Kontrol' },
  { value: 'payment-pending', label: 'Ödeme Bekliyor' },
  { value: 'payment-completed', label: 'Ödeme Tamamlandı' },
];

const normalizePhone = (value?: string) => {
  const raw = String(value || '').replace(/\D/g, '');
  if (!raw) return '';
  if (raw.startsWith('90')) return `+${raw}`;
  if (raw.startsWith('0')) return `+9${raw}`;
  return `+90${raw}`;
};

const normalizePlate = (value?: string) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
const normalizeUpper = (value?: string) => String(value || '').toLocaleUpperCase('tr-TR');
const normalizePhoneInput = (value?: string) => String(value || '').replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
const isValidSpotPlate = (value?: string) => {
  const plate = normalizePlate(value);
  return !plate || (plate.length >= 6 && plate.length <= 9 && /^\d{2}[A-ZÇĞİÖŞÜ]{1,3}\d{2,4}$/.test(plate));
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const haversineKm = (lat1?: number | null, lon1?: number | null, lat2?: number | null, lon2?: number | null) => {
  if ([lat1, lon1, lat2, lon2].some((v) => v === null || v === undefined)) return null;
  const toRad = (value: number) => (value * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad((lat2 as number) - (lat1 as number));
  const dLon = toRad((lon2 as number) - (lon1 as number));
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1 as number)) * Math.cos(toRad(lat2 as number)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Number((R * c).toFixed(1));
};

const getDistanceBadgeClass = (distance?: number | null) => {
  if (distance == null) return 'bg-slate-800 text-slate-200';
  if (distance <= 5) return 'bg-emerald-950/60 text-emerald-300';
  if (distance <= 20) return 'bg-amber-950/60 text-amber-300';
  return 'bg-rose-950/60 text-rose-300';
};

const getSoftStatusClass = (value?: string) => {
  const normalized = String(value || '').toLocaleLowerCase('tr-TR');
  if (normalized.includes('tamam') || normalized.includes('teslim') || normalized.includes('gerçek') || normalized.includes('kabul') || normalized.includes('onay')) return 'bg-emerald-950/60 text-emerald-300 ring-emerald-800/60';
  if (normalized.includes('bekli') || normalized.includes('topla') || normalized.includes('hazır')) return 'bg-amber-950/60 text-amber-300 ring-amber-800/60';
  if (normalized.includes('yol') || normalized.includes('devam') || normalized.includes('yükleme')) return 'bg-blue-950/60 text-blue-300 ring-blue-800/60';
  if (normalized.includes('iptal') || normalized.includes('red')) return 'bg-rose-950/60 text-rose-300 ring-rose-800/60';
  return 'bg-slate-800 text-slate-200 ring-slate-700';
};

const renderSoftStatus = (value?: string) => (
  <span className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-medium ring-1 ${getSoftStatusClass(value)}`}>
    {value || '-'}
  </span>
);

const getTrackingExcelValue = (row: OperationRow, key: keyof OperationRow) => {
  switch (key) {
    case 'navlun':
    case 'seferKm':
    case 'tlKm':
    case 'ugrmaSayisi':
      return Number(row[key] || 0);
    case 'aracAtamaTarihi':
    case 'yuklemeTarihi':
    case 'terminTarihi':
    case 'randevuluTeslim':
    case 'archivedAt':
      return row[key] ? formatDateTime(String(row[key])) : '-';
    case 'folderNumber':
      return row.folderNumber ? `K${row.folderNumber}` : '-';
    default:
      return String(row[key] ?? '-');
  }
};

const normalizeTransportModel = (value?: string) => {
  const normalized = String(value || '').trim().toLocaleLowerCase('tr-TR');
  if (['own-fleet', 'filo', 'fleet', 'internal', 'internal-driver', 'ozmal', 'özmal'].includes(normalized)) return 'own-fleet';
  if (['spot', 'bid', 'external', 'external-driver', 'marketplace'].includes(normalized)) return 'spot';
  return normalized;
};

const normalizeSearchText = (value?: string) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/\s+/g, ' ')
  .trim();

const mapAssignmentResponseLabel = (value?: string) => {
  const normalized = String(value || '').trim().toLocaleLowerCase('tr-TR');
  if (normalized === 'pending') return 'Onay Bekliyor';
  if (normalized === 'accepted') return 'Kabul Edildi';
  if (normalized === 'rejected') return 'Reddedildi';
  return '-';
};

const isPendingOwnFleetAssignment = (row: Pick<OperationRow, 'lojistikTuru' | 'assignmentResponseStatus'>) => {
  return normalizeTransportModel(row.lojistikTuru) === 'own-fleet' && String(row.assignmentResponseStatus || '').trim() === 'pending';
};

const getEffectiveProcessStatus = (row: Pick<OperationRow, 'islemDurum' | 'durum' | 'lojistikTuru' | 'accountingStatus'>) => {
  const processStatus = String(row.islemDurum || '').trim();
  if (processStatus) return processStatus;

  const accountingStatus = String(row.accountingStatus || '').trim();
  if (ACCOUNTING_ONLY_FLOW.includes(accountingStatus as any)) return accountingStatus;

  const resolvedFromDurum = resolveTrackingOperationTabFromDurum(row.durum, row.lojistikTuru);
  if (resolvedFromDurum && TRACKING_PROCESS_TABS.includes(resolvedFromDurum as typeof TRACKING_PROCESS_TABS[number])) {
    return resolvedFromDurum;
  }

  return processStatus;
};

const extractIntermediateStops = (note?: string) => {
  const raw = String(note || '');
  const extractList = (label: string) => {
    const match = raw.match(new RegExp(`${label}:\\s*([^|]+)`, 'i'));
    if (!match?.[1]) return [] as string[];
    return match[1]
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const pickupStops = extractList('Yükleme Ara Noktaları');
  const deliveryStops = extractList('Boşaltma Ara Noktaları');
  const legacyStops = extractList('Ara Noktalar');

  if (pickupStops.length === 0 && deliveryStops.length === 0) return legacyStops;

  return [
    pickupStops.length > 0 ? `Yükleme: ${pickupStops.join(', ')}` : '',
    deliveryStops.length > 0 ? `Boşaltma: ${deliveryStops.join(', ')}` : '',
  ].filter(Boolean);
};

const extractIntermediateStopsFromRoutePoints = (routePoints?: any[]) => {
  if (!Array.isArray(routePoints) || routePoints.length === 0) return [] as string[];
  const deliveryStops = routePoints
    .filter((point) => String(point?.operation_type || '').trim().toLocaleLowerCase('tr-TR') === 'unloading')
    .map((point) => String(point?.customer_name || point?.location_name || '').trim())
    .filter(Boolean);
  if (deliveryStops.length === 0) return [] as string[];
  return [`Boşaltma: ${deliveryStops.join(', ')}`];
};

const buildRouteStopMapsUrl = (row: OperationRow) => {
  const encodeStop = (parts: Array<string | number | null | undefined>) => parts.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
  const origin = row.pickupLatitude != null && row.pickupLongitude != null
    ? `${row.pickupLatitude},${row.pickupLongitude}`
    : encodeStop([row.yuklemeMusterisi, row.yuklemeAdres, row.yuklemeIlcesi, row.yuklemeIli]);
  const destination = encodeStop([row.bosaltmaMusterisi, row.teslimAdres, row.teslimIlcesi, row.bosaltmaIli]);
  if (!origin || !destination) return '#';

  const waypointStops = (row.routePoints || []).map((point) => {
    if (point?.latitude != null && point?.longitude != null) return `${point.latitude},${point.longitude}`;
    return encodeStop([point?.customer_name || point?.location_name, point?.district, point?.city]);
  }).filter(Boolean);

  const params = new URLSearchParams({ api: '1', origin, destination, travelmode: 'driving' });
  if (waypointStops.length > 0) params.set('waypoints', waypointStops.join('|'));
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

const PROCESS_FLOW_ORDER = ['atandi-islem-bekliyor', 'yukleme-bekliyor', 'yukleme-devam', 'yukleme-tamamlandi', 'tasima-yolunda', 'teslim-noktasina-ulasildi', 'bosaltma-bekliyor', 'bosaltma-devam', 'bosaltma-tamamlandi', 'delivery-approval-pending', 'invoice-waiting', 'invoice-approval-pending', 'payment-pre-approval', 'payment-pending', 'payment-completed'] as const;

const ACCOUNTING_ONLY_FLOW = ['delivery-approval-pending', 'delivery-rejected', 'invoice-waiting', 'invoice-rejected', 'invoice-approval-pending', 'payment-pre-approval', 'payment-pending', 'payment-completed'];

const matchesRequestedVehicleTypes = (requestedValue?: string, candidateValue?: string) => {
  const requestedTypes = parseVehicleTypes(requestedValue).map((item) => normalizeVehicleType(item)).filter(Boolean);
  if (requestedTypes.length === 0) return true;
  const candidateType = normalizeVehicleType(candidateValue);
  if (!candidateType) return true;
  return requestedTypes.includes(candidateType);
};

const isDeliveryStage = (status?: string) => {
  const normalized = String(status || '').trim();
  return ['yukleme-tamamlandi', 'tasima-yolunda', 'teslim-noktasina-ulasildi', 'bosaltma-bekliyor', 'bosaltma-devam', 'bosaltma-tamamlandi', 'fatura-bekleyen', 'odeme-bekleyen', 'odeme-gerceklesen'].includes(normalized);
};

const buildGoogleMapsDirectionsUrl = ({
  originLat,
  originLng,
  destinationLat,
  destinationLng,
}: {
  originLat: number | null;
  originLng: number | null;
  destinationLat: number | null;
  destinationLng: number | null;
}) => {
  if (originLat == null || originLng == null || destinationLat == null || destinationLng == null) return '';
  const origin = `${originLat},${originLng}`;
  const destination = `${destinationLat},${destinationLng}`;
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
};

const buildUserScopedColumnsKey = () => {
  return buildUserScopedStorageKey(TRACKING_VISIBLE_COLUMNS_KEY);
};

const PROCESS_TAB_LABELS: Record<string, string> = {
  'atama-bekliyor': 'Sürücü Ataması Bekleyenler',
  'atandi-islem-bekliyor': 'Atandı İşlem Bekleyenler',
  'yukleme-bekliyor': 'Yükleme Bekleyenler',
  'yukleme-devam': 'Yüklemesi Devam Edenler',
  'yukleme-tamamlandi': 'Yüklemesi Tamamlananlar',
  'tasima-yolunda': 'Teslim Yolunda Olanlar',
  'teslim-noktasina-ulasildi': 'Teslim Noktasına Ulaşılanlar',
  'bosaltma-bekliyor': 'Boşaltma Bekleyenler',
  'bosaltma-devam': 'Boşaltması Devam Edenler',
  'bosaltma-tamamlandi': 'Boşaltması Tamamlananlar',
  'fatura-bekleyen': 'Fatura Bekleyenler',
  'odeme-bekleyen': 'Ödeme Bekleyenler',
  'odeme-gerceklesen': 'Ödemesi Gerçekleşenler',
  'arsiv': 'Filo Arşivi',
};

const PROCESS_PAGE_ORDER = [
  'atama-bekliyor',
  'atandi-islem-bekliyor',
  'yukleme-bekliyor',
  'yukleme-devam',
  'yukleme-tamamlandi',
  'tasima-yolunda',
  'teslim-noktasina-ulasildi',
  'bosaltma-bekliyor',
  'bosaltma-devam',
  'bosaltma-tamamlandi',
  'fatura-bekleyen',
  'odeme-bekleyen',
  'odeme-gerceklesen',
  'arsiv',
] as const;

const PROCESS_PAGE_COLUMNS: Record<string, string[]> = {
  'atama-bekliyor': ['sevkKodu', 'planKodu', 'durum', 'yuklemeTarihi', 'terminTarihi', 'randevuluTeslim', 'yuklenecekVardiya', 'yuklemeMusterisi', 'yuklemeIli', 'yuklemeIlcesi', 'yuklemeKonum', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'hizmetTuru', 'lojistikTuru', 'navlun', 'faturaBasligi', 'disNakliye', 'operasyonNot'],
  'atandi-islem-bekliyor': ['sevkKodu', 'planKodu', 'yuklemeTarihi', 'terminTarihi', 'randevuluTeslim', 'yuklenecekVardiya', 'yuklemeMusterisi', 'yuklemeIli', 'yuklemeIlcesi', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'teslimIlcesi', 'cekici', 'dorse', 'surucuAdi', 'surucuTelefon', 'hizmetTuru', 'lojistikTuru', 'navlun', 'seferKm', 'atamaYaniti', 'faturaBasligi', 'disNakliye', 'operasyonNot', 'aracDurum'],
  'yukleme-bekliyor': ['sevkKodu', 'planKodu', 'yuklemeTarihi', 'terminTarihi', 'randevuluTeslim', 'yuklenecekVardiya', 'yuklemeMusterisi', 'yuklemeIli', 'yuklemeIlcesi', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'teslimIlcesi', 'cekici', 'dorse', 'surucuAdi', 'surucuTelefon', 'faturaBasligi', 'disNakliye', 'operasyonNot', 'aracDurum'],
  'yukleme-devam': ['sevkKodu', 'planKodu', 'yuklemeTarihi', 'yuklenecekVardiya', 'yuklemeMusterisi', 'cekici', 'dorse', 'surucuAdi', 'surucuTelefon', 'hizmetTuru', 'faturaBasligi', 'disNakliye', 'operasyonNot', 'aracDurum'],
  'yukleme-tamamlandi': ['sevkKodu', 'planKodu', 'yuklemeTarihi', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'cekici', 'surucuAdi', 'navlun', 'seferKm', 'tlKm', 'faturaBasligi', 'disNakliye', 'operasyonNot'],
  'tasima-yolunda': ['sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'cekici', 'dorse', 'surucuAdi', 'surucuTelefon', 'seferKm', 'tlKm', 'faturaBasligi', 'disNakliye', 'aracDurum'],
  'teslim-noktasina-ulasildi': ['sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'cekici', 'dorse', 'surucuAdi', 'surucuTelefon', 'seferKm', 'tlKm', 'faturaBasligi', 'disNakliye', 'aracDurum'],
  'bosaltma-bekliyor': ['sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'cekici', 'dorse', 'surucuAdi', 'surucuTelefon', 'faturaBasligi', 'disNakliye', 'operasyonNot', 'aracDurum'],
  'bosaltma-devam': ['sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'cekici', 'surucuAdi', 'surucuTelefon', 'navlun', 'faturaBasligi', 'disNakliye', 'operasyonNot', 'aracDurum'],
  'bosaltma-tamamlandi': ['sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'surucuAdi', 'navlun', 'seferKm', 'tlKm', 'faturaBasligi', 'disNakliye', 'operasyonNot'],
  'fatura-bekleyen': ['sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'surucuAdi', 'faturaBasligi', 'disNakliye', 'navlun', 'operasyonNot'],
  'odeme-bekleyen': ['sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'surucuAdi', 'faturaBasligi', 'disNakliye', 'navlun', 'operasyonNot'],
  'odeme-gerceklesen': ['sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'surucuAdi', 'faturaBasligi', 'disNakliye', 'navlun', 'operasyonNot'],
  'arsiv': ['archiveCode', 'folderNumber', 'evrakSiraNo', 'invoiceNumber', 'hasFuelPurchase', 'sevkKodu', 'planKodu', 'bosaltmaMusterisi', 'bosaltmaIli', 'nearestHub', 'surucuAdi', 'faturaBasligi', 'disNakliye', 'navlun', 'operasyonNot'],
};

const PROCESS_BADGE_KEYS: Record<string, string> = {
  'atama-bekliyor': 'atamaBekliyor',
  'atandi-islem-bekliyor': 'atandiIslemBekliyor',
  'yukleme-bekliyor': 'yuklemeBekliyor',
  'yukleme-devam': 'yuklemeDevam',
  'yukleme-tamamlandi': 'yuklemeTamamlandi',
  'tasima-yolunda': 'yolda',
  'teslim-noktasina-ulasildi': 'teslimNoktasinaUlasildi',
  'bosaltma-bekliyor': 'bosaltmaBekliyor',
  'bosaltma-devam': 'bosaltmaDevam',
  'bosaltma-tamamlandi': 'tamamlanan',
  'fatura-bekleyen': 'faturaBekleyen',
  'odeme-bekleyen': 'odemeBekleyen',
  'odeme-gerceklesen': 'odemeGerceklesen',
  'arsiv': 'arsiv',
};

const PROCESS_DETAIL_STATUS_LABELS: Record<string, string> = {
  'tedarik-bekleniyor': 'Tedarik Bekleniyor',
  'spot-teklif-bekleniyor': 'Spot Teklif Bekleniyor',
  'atama-bekliyor': 'Atama Bekliyor',
  'atandi-islem-bekliyor': 'Atandı İşlem Bekliyor',
  'yukleme-bekliyor': 'Yükleme Bekliyor',
  'yukleme-devam': 'Yükleme Devam',
  'yukleme-tamamlandi': 'Yükleme Tamamlandı',
  'tasima-yolunda': 'Teslim Yolunda',
  'teslim-noktasina-ulasildi': 'Teslim Noktasına Ulaşıldı',
  'bosaltma-bekliyor': 'Boşaltma Bekliyor',
  'bosaltma-devam': 'Boşaltma Devam',
  'bosaltma-tamamlandi': 'Boşaltma Tamamlandı',
  'fatura-bekleyen': 'Fatura Bekliyor',
  'odeme-bekleyen': 'Ödeme Bekliyor',
  'odeme-gerceklesen': 'Ödeme Gerçekleşti',
};

const TRACKING_PROCESS_TABS: TrackingOperationTab[] = [
  'atandi-islem-bekliyor',
  'yukleme-bekliyor',
  'yukleme-devam',
  'yukleme-tamamlandi',
  'tasima-yolunda',
  'teslim-noktasina-ulasildi',
  'bosaltma-bekliyor',
  'bosaltma-devam',
  'bosaltma-tamamlandi',
];

const resolveTrackingOperationTabFromDurum = (durum?: string, lojistikTuru?: string): TrackingOperationTab | null => {
  const normalizedDurum = normalizeSearchText(durum);
  if (!normalizedDurum) return null;

  if (
    normalizedDurum.includes('spot-teklif-bekleniyor')
    || normalizedDurum.includes('spot teklif bekleniyor')
    || normalizedDurum.includes('teklif-topla')
    || normalizedDurum.includes('teklif topla')
    || normalizedDurum.includes('tedarik-bekleniyor')
    || normalizedDurum.includes('tedarik bekleniyor')
  ) {
    return 'atama-bekliyor';
  }

  if (
    normalizedDurum.includes('filo-tedarik-edildi')
    || normalizedDurum.includes('filo tedarik edildi')
    || (normalizeTransportModel(lojistikTuru) === 'own-fleet' && normalizedDurum.includes('tedarik-edildi'))
  ) {
    return 'atandi-islem-bekliyor';
  }

  const processMatchers: Array<[TrackingOperationTab, string[]]> = [
    ['yukleme-bekliyor', ['yukleme-bekliyor', 'yukleme bekliyor']],
    ['yukleme-devam', ['yukleme-devam', 'yukleme devam']],
    ['yukleme-tamamlandi', ['yukleme-tamamlandi', 'yukleme tamamlandi']],
    ['tasima-yolunda', ['tasima-yolunda', 'tasima yolunda', 'teslim yolunda']],
    ['teslim-noktasina-ulasildi', ['teslim-noktasina-ulasildi', 'teslim noktasina ulasildi']],
    ['bosaltma-bekliyor', ['bosaltma-bekliyor', 'bosaltma bekliyor']],
    ['bosaltma-devam', ['bosaltma-devam', 'bosaltma devam']],
    ['bosaltma-tamamlandi', ['bosaltma-tamamlandi', 'bosaltma tamamlandi']],
  ];

  for (const [tab, patterns] of processMatchers) {
    if (patterns.some((pattern) => normalizedDurum.includes(pattern))) return tab;
  }

  return null;
};

const resolveTrackingOperationTabForRow = (row: OperationRow, isArchived: boolean): TrackingOperationTab => {
  if (isArchived) return 'arsiv';
  const processStatus = String(getEffectiveProcessStatus(row) || '').trim() as TrackingOperationTab;
  if (TRACKING_PROCESS_TABS.includes(processStatus)) return processStatus;
  const resolvedFromDurum = resolveTrackingOperationTabFromDurum(row.durum, row.lojistikTuru);
  if (resolvedFromDurum) return resolvedFromDurum;
  const accountingStatus = String(row.accountingStatus || '').trim();
  if (['delivery-approval-pending', 'invoice-waiting', 'invoice-rejected'].includes(accountingStatus)) return 'fatura-bekleyen';
  if (['invoice-approval-pending', 'payment-pre-approval', 'payment-pending'].includes(accountingStatus)) return 'odeme-bekleyen';
  if (accountingStatus === 'payment-completed') return 'odeme-gerceklesen';
  return 'atama-bekliyor';
};

const isAtamaBekliyorRow = (row: OperationRow) => resolveTrackingOperationTabForRow(row, false) === 'atama-bekliyor';

function TrackingTabContent({ operationTab, waitingRows, liveTick, formatDateTime, shipmentId, completionDocuments, onPreviewImage, onUploadDocuments, onDeleteDocument, uploadingDocuments, deletingDocumentId, pickupDepartedAt, deliveryArrivedAt, paymentCompletedAt, paymentReceiptDocuments }: {
  operationTab: string;
  waitingRows: any[];
  liveTick: number;
  formatDateTime: (d: any) => string;
  shipmentId?: string;
  completionDocuments?: any[];
  onPreviewImage?: (url: string) => void;
  onUploadDocuments?: (files: FileList | null) => void;
  onDeleteDocument?: (documentId: string | number) => void;
  uploadingDocuments?: boolean;
  deletingDocumentId?: string | number | null;
  pickupDepartedAt?: string;
  deliveryArrivedAt?: string;
  paymentCompletedAt?: string;
  paymentReceiptDocuments?: any[];
}) {
  const isLoadingStage = operationTab === 'yukleme-bekliyor' || operationTab === 'yukleme-devam' || operationTab === 'yukleme-tamamlandi' || operationTab === 'tasima-yolunda' || operationTab === 'teslim-noktasina-ulasildi';
  const isUnloadingStage = operationTab === 'bosaltma-bekliyor' || operationTab === 'bosaltma-devam' || operationTab === 'bosaltma-tamamlandi';
  const completionDocumentTitle = isUnloadingStage ? 'Boşaltma Tamamlandı Evrakı' : 'Yükleme Tamamlandı Evrakı';
  const phaseFilter = isLoadingStage ? 'loading' : isUnloadingStage ? 'unloading' : '';
  const labelPrefix = isLoadingStage ? 'Yükleme' : isUnloadingStage ? 'Boşaltma' : '';

  const targetRow = phaseFilter ? waitingRows.find((item: any) =>
    String(item.process_phase || '') === phaseFilter ||
    String(item.location_type || item.waiting_type || '').toLocaleLowerCase('tr-TR') === labelPrefix.toLocaleLowerCase('tr-TR')
  ) : null;

  const waitingStart = targetRow?.start_time || targetRow?.waiting_start || targetRow?.reported_at || '';
  const operationStart = targetRow?.operation_started_at || '';
  const operationEnd = targetRow?.operation_completed_at || targetRow?.waiting_end || '';
  const actualOperationEnd = targetRow?.operation_completed_at || '';

  let waitingMinutes: string | number = '-';
  if (waitingStart && !targetRow?.waiting_end && String(targetRow?.status || '') === 'waiting') {
    const diffMs = Date.now() - new Date(waitingStart).getTime();
    waitingMinutes = String(Math.max(0, Math.round(diffMs / 60000)));
  } else if (targetRow?.duration_minutes != null) {
    waitingMinutes = String(targetRow.duration_minutes);
  } else if (targetRow?.duration_hours != null) {
    waitingMinutes = String(Math.round(Number(targetRow.duration_hours) * 60));
  }

  let grossOperationMinutes: string | number = '-';
  if (waitingStart) {
    const grossEndBase = actualOperationEnd || new Date().toISOString();
    const diffMs = new Date(grossEndBase).getTime() - new Date(waitingStart).getTime();
    grossOperationMinutes = String(Math.max(0, Math.round(diffMs / 60000)));
  }

  let netOperationMinutes: string | number = '-';
  if (operationStart) {
    const endBase = actualOperationEnd || new Date().toISOString();
    const diffMs = new Date(endBase).getTime() - new Date(operationStart).getTime();
    netOperationMinutes = String(Math.max(0, Math.round(diffMs / 60000)));
  } else if (grossOperationMinutes !== '-' && waitingMinutes !== '-') {
    const total = Number(grossOperationMinutes) - Number(waitingMinutes);
    netOperationMinutes = String(Math.max(0, total));
  } else if (grossOperationMinutes !== '-') {
    netOperationMinutes = grossOperationMinutes;
  }

  const items: [string, string][] = [];

  if (operationTab === 'teslim-noktasina-ulasildi' || operationTab === 'bosaltma-bekliyor') {
    items.push(['Teslim Noktasına Varış', deliveryArrivedAt ? formatDateTime(deliveryArrivedAt) : '-']);
  }

  if (operationTab === 'bosaltma-tamamlandi') {
    items.push(['Boşaltma Bitiş Saati', actualOperationEnd ? formatDateTime(actualOperationEnd) : '-']);
  }

  if (operationTab === 'tasima-yolunda' || operationTab === 'teslim-noktasina-ulasildi' || operationTab === 'bosaltma-bekliyor') {
    const departureTime = pickupDepartedAt || operationEnd || '';
    items.push(['Yükleme Noktasından Çıkış', departureTime ? formatDateTime(departureTime) : '-']);
  }

  if (isUnloadingStage && operationStart) {
    items.push(['Boşaltma Başlama Saati', formatDateTime(operationStart)]);
  }

  if (waitingStart) {
    items.push([`${labelPrefix} Noktasına Varış`, formatDateTime(waitingStart)]);
  }
  if (operationStart && !isUnloadingStage) {
    items.push([`${labelPrefix} Başlama Saati`, formatDateTime(operationStart)]);
  }
  if (operationTab !== 'bosaltma-tamamlandi' && (actualOperationEnd || operationTab === 'yukleme-tamamlandi' || operationTab === 'bosaltma-tamamlandi')) {
    items.push([`${labelPrefix} Bitiş Saati`, actualOperationEnd ? formatDateTime(actualOperationEnd) : '-']);
  }
  if (grossOperationMinutes !== '-') {
    items.push([`Beklemeli ${labelPrefix} Süresi`, `${grossOperationMinutes} dk`]);
  }
  if (netOperationMinutes !== '-') {
    items.push([`Beklemesiz ${labelPrefix} Süresi`, `${netOperationMinutes} dk`]);
  }
  if (waitingMinutes !== '-') {
    items.push([`${labelPrefix} Bekleme Süresi`, `${waitingMinutes} dk`]);
  }

  const renderDocumentGrid = (documents: any[], tone: 'blue' | 'emerald' | 'violet', emptyText: string, allowUpload = false) => {
    const toneClasses = {
      blue: {
        button: 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
        card: 'border-slate-700/30 bg-[#111111]',
        delete: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
      },
      emerald: {
        button: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
        card: 'border-slate-700/30 bg-[#111111]',
        delete: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
      },
      violet: {
        button: 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100',
        card: 'border-slate-700/30 bg-[#111111]',
        delete: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
      },
    }[tone];

    return documents && documents.length > 0 ? (
      <div className="grid grid-cols-2 gap-2">
        {documents.map((doc: any, index: number) => {
          const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
          const rawPath = doc.file_path || doc.file_url || doc.filePath || doc.url || '';
          const url = rawPath.startsWith('http') ? rawPath : `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
          const fileName = doc.file_name || doc.fileName || doc.name || `Evrak ${index + 1}`;
          const isImage = (doc.file_type || doc.mime_type || '').startsWith('image/') || fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i);

          return (
            <div key={doc.id || `${fileName}-${index}`} className={`rounded-lg border overflow-hidden ${toneClasses.card}`}>
              {isImage ? (
                <button type="button" onClick={() => onPreviewImage?.(url)} className="w-full text-left">
                  <img src={url} alt={fileName} className="h-32 w-full object-cover" />
                </button>
              ) : (
                <a href={url} target="_blank" rel="noopener noreferrer" className="flex h-32 items-center justify-center px-3 text-center text-sm font-medium text-blue-600 hover:text-blue-800">
                  {fileName}
                </a>
              )}
              <div className="flex items-center justify-between gap-2 px-2 py-1">
                <div className="min-w-0 truncate text-[10px] text-slate-400">{fileName}</div>
                {doc.id && (
                  <button
                    type="button"
                    onClick={() => onDeleteDocument?.(doc.id)}
                    disabled={deletingDocumentId === doc.id}
                    className={`inline-flex items-center rounded border px-1.5 py-1 text-[10px] font-semibold disabled:opacity-60 ${toneClasses.delete}`}
                  >
                    {deletingDocumentId === doc.id ? 'Siliniyor' : 'Sil'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="rounded-lg border border-dashed border-slate-700/40 bg-[#111111] px-3 py-6 text-center text-[11px] text-slate-400">
        {emptyText}
      </div>
    );
  };

  return (
    <div className="space-y-3 text-[12px]" key={liveTick}>
      {operationTab === 'odeme-gerceklesen' && (
        <>
          <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
            <div className="text-[11px] text-slate-400">Ödeme Tarihi</div>
            <div className="mt-1 font-semibold text-slate-100">{paymentCompletedAt ? formatDateTime(paymentCompletedAt) : '-'}</div>
          </div>
          <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
            <div className="mb-3 text-[11px] text-slate-400">Ödeme Dekontu</div>
            {paymentReceiptDocuments && paymentReceiptDocuments.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {paymentReceiptDocuments.map((doc: any) => {
                  const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
                  const rawPath = doc.file_path || doc.file_url || doc.url || '';
                  const url = rawPath.startsWith('http') ? rawPath : `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
                  const isImage = (doc.file_type || doc.mime_type || '').startsWith('image/') || (doc.file_name || '').match(/\.(jpg|jpeg|png|gif|webp)$/i);
                  return (
                    <div key={doc.id} className="rounded-lg border border-slate-700/30 bg-[#111111] overflow-hidden">
                      {isImage ? (
                        <button type="button" onClick={() => onPreviewImage?.(url)} className="w-full text-left">
                          <img src={url} alt={doc.file_name || doc.name || 'Dekont'} className="w-full h-32 object-cover" />
                        </button>
                      ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-32 text-sm font-medium text-blue-600 hover:text-blue-800">
                          {doc.file_name || doc.name || 'Dekont'}
                        </a>
                      )}
                      <div className="px-2 py-1 text-[10px] text-slate-400 truncate">{doc.file_name || doc.name || 'Dekont'}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-700/40 bg-[#111111] px-3 py-6 text-center text-[11px] text-slate-400">Henüz dekont yok.</div>
            )}
          </div>
        </>
      )}

      {items.length > 0 ? items.map(([label, value]) => (
        <div key={String(label)} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
          <div className="text-[11px] text-slate-400">{label}</div>
          <div className="mt-1 font-semibold text-slate-100">{value}</div>
        </div>
      )) : (
        <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3 text-slate-400">Kayıt bulunamadı</div>
      )}

      {((operationTab === 'yukleme-tamamlandi' || operationTab === 'tasima-yolunda' || operationTab === 'teslim-noktasina-ulasildi' || operationTab === 'bosaltma-bekliyor') || (completionDocuments && completionDocuments.length > 0)) && (
        <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-[11px] text-slate-400">{completionDocumentTitle}</div>
            {(operationTab === 'yukleme-tamamlandi' || operationTab === 'tasima-yolunda' || operationTab === 'teslim-noktasina-ulasildi' || operationTab === 'bosaltma-bekliyor') && (
              <label className={`inline-flex cursor-pointer items-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 ${uploadingDocuments ? 'pointer-events-none opacity-60' : ''}`}>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(event) => {
                    onUploadDocuments?.(event.target.files);
                    event.currentTarget.value = '';
                  }}
                />
                {uploadingDocuments ? 'Yükleniyor...' : 'Evrak Yükle'}
              </label>
            )}
          </div>
          {completionDocuments && completionDocuments.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {completionDocuments.map((doc: any) => {
              const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
              const rawPath = doc.file_path || doc.file_url || doc.url || '';
              const url = rawPath.startsWith('http') ? rawPath : `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
              const isImage = (doc.file_type || doc.mime_type || '').startsWith('image/') || (doc.file_name || '').match(/\.(jpg|jpeg|png|gif|webp)$/i);
              return (
                <div key={doc.id} className="rounded-lg border border-slate-700/30 bg-[#111111] overflow-hidden">
                  {isImage ? (
                    <button
                      type="button"
                      onClick={() => onPreviewImage?.(url)}
                      className="w-full text-left"
                    >
                      <img src={url} alt={doc.file_name || doc.name || 'Evrak'} className="w-full h-32 object-cover" />
                    </button>
                  ) : (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center h-32 text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      {doc.file_name || doc.name || 'Evrak'}
                    </a>
                  )}
                  <div className="flex items-center justify-between gap-2 px-2 py-1">
                    <div className="min-w-0 text-[10px] text-slate-400 truncate">{doc.file_name || doc.name || 'Evrak'}</div>
                    {(operationTab === 'yukleme-tamamlandi' || operationTab === 'tasima-yolunda' || operationTab === 'teslim-noktasina-ulasildi' || operationTab === 'bosaltma-bekliyor') && (
                      <button
                        type="button"
                        onClick={() => onDeleteDocument?.(doc.id)}
                        disabled={deletingDocumentId === doc.id}
                        className="inline-flex items-center rounded border border-red-200 bg-red-50 px-1.5 py-1 text-[10px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                      >
                        {deletingDocumentId === doc.id ? 'Siliniyor' : 'Sil'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-700/40 bg-[#111111] px-3 py-6 text-center text-[11px] text-slate-400">
              Henüz evrak yok.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Tracking() {
  const { user, impersonateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [trackingScope, setTrackingScope] = useState<{ allowedPickupWarehouseIds: string[]; canViewNonWarehousePickups: boolean }>({ allowedPickupWarehouseIds: [], canViewNonWarehousePickups: false });
  const [rows, setRows] = useState<OperationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [operationTab, setOperationTab] = useState<TrackingOperationTab>('atama-bekliyor');
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [detailRow, setDetailRow] = useState<OperationRow | null>(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [detailTab, setDetailTab] = useState<'info' | 'goods' | 'driver' | 'tracking' | 'documents'>('info');
  const [waitingRecordsByShipment, setWaitingRecordsByShipment] = useState<Record<string, any[]>>({});
  const [completionDocuments, setCompletionDocuments] = useState<any[]>([]);
  const [deliveryDocuments, setDeliveryDocuments] = useState<any[]>([]);
  const [shortageSurplusDocuments, setShortageSurplusDocuments] = useState<any[]>([]);
  const [incidentDocuments, setIncidentDocuments] = useState<any[]>([]);
  const [invoiceDocuments, setInvoiceDocuments] = useState<any[]>([]);
  const [paymentReceiptDocuments, setPaymentReceiptDocuments] = useState<any[]>([]);
  const [activeBidRequestShipmentIds, setActiveBidRequestShipmentIds] = useState<string[]>([]);
  const [offerCollectionLoading, setOfferCollectionLoading] = useState(false);
  const [offerCollectionLoadingCode, setOfferCollectionLoadingCode] = useState('');
  const [offerCollectionConfirmRows, setOfferCollectionConfirmRows] = useState<OperationRow[]>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [reverseGeocodedDriverAddress, setReverseGeocodedDriverAddress] = useState<string | null>(null);
  const [uploadingTrackingDocuments, setUploadingTrackingDocuments] = useState(false);
  const [deletingTrackingDocumentId, setDeletingTrackingDocumentId] = useState<string | number | null>(null);
  const [liveTick, setLiveTick] = useState(Date.now());
  const [archiveRows, setArchiveRows] = useState<any[]>([]);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveShipmentRow, setArchiveShipmentRow] = useState<OperationRow | null>(null);
  const [archiveForm, setArchiveForm] = useState({ folderName: '', evrakSiraNo: '', invoiceNumber: '', hasFuelPurchase: false });
  const [archiveFolderStartFrom, setArchiveFolderStartFrom] = useState('');
  const [archiveFolderOptions, setArchiveFolderOptions] = useState<string[]>([]);
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [archivePreviewCode, setArchivePreviewCode] = useState('');
  const [archiveModalMode, setArchiveModalMode] = useState<'create' | 'view' | 'edit'>('create');
  const [activeArchiveRecord, setActiveArchiveRecord] = useState<any | null>(null);
  const [archiveRecordByShipmentId, setArchiveRecordByShipmentId] = useState<Record<string, any>>({});
  const [archivePdfDocuments, setArchivePdfDocuments] = useState<Record<string, any[]>>({});
  const [uploadingArchivePdfId, setUploadingArchivePdfId] = useState<string | null>(null);
  const [previewArchivePdf, setPreviewArchivePdf] = useState<{ url: string; name: string } | null>(null);
  const [modalArchivePdfDocs, setModalArchivePdfDocs] = useState<any[]>([]);
  const [uploadingModalArchivePdf, setUploadingModalArchivePdf] = useState(false);
  const [pendingArchiveFile, setPendingArchiveFile] = useState<File | null>(null);
  const [editingWizardShipment, setEditingWizardShipment] = useState<any | null>(null);
  const [loadingEditShipmentId, setLoadingEditShipmentId] = useState<string | null>(null);
  const [recalculatingNearestHubs, setRecalculatingNearestHubs] = useState(false);

  const handleEditRow = async (row: OperationRow) => {
    try {
      setLoadingEditShipmentId(row.id);
      const response: any = await supplyService.getById(row.id);
      if (!response?.data) throw new Error('Sevk siparişi bulunamadı');
      setEditingWizardShipment(response.data);
      setWizardOpen(true);
    } catch (error: any) {
      toast.error(error?.message || 'Sevk siparişi açılamadı');
    } finally {
      setLoadingEditShipmentId(null);
    }
  };

  const handleRecalculateNearestHubs = async () => {
    try {
      setRecalculatingNearestHubs(true);
      const response: any = await supplyService.recalculateNearestHubs(true);
      toast.success(response?.message || 'En yakın hub bilgileri güncellendi');
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'En yakın hub hesaplanamadı');
    } finally {
      setRecalculatingNearestHubs(false);
    }
  };

  const loadArchiveRows = async () => {
    try {
      const res: any = await archiveService.getAll();
      const items = Array.isArray(res?.data) ? res.data : [];
      setArchiveRows(items);
      setArchiveRecordByShipmentId(items.reduce((acc: Record<string, any>, item: any) => {
        if (item?.shipmentId) acc[String(item.shipmentId)] = item;
        return acc;
      }, {}));
    } catch {
      setArchiveRows([]);
    }
  };

  const archiveDisplayRows = useMemo<OperationRow[]>(() => {
    const shipmentMap = new Map(rows.map((row) => [String(row.id), row]));
    return archiveRows
      .map((item: any) => {
        const shipmentRow = shipmentMap.get(String(item.shipmentId));
        if (!shipmentRow) return null;
        return {
          ...shipmentRow,
          rowKey: `archive-${item.id}-${shipmentRow.rowKey}`,
          archiveId: String(item.id),
          archiveCode: item.archiveCode || '-',
          invoiceNumber: item.invoiceNumber || '-',
          evrakSiraNo: item.evrakSiraNo ? String(item.evrakSiraNo) : item.folderNumber ? String(item.folderNumber) : '',
          hasFuelPurchase: item.hasFuelPurchase ? 'Var' : 'Yok',
          folderNumber: item.folderName || '-',
          folderName: item.folderName || '-',
          archivedAt: item.archivedAt || '',
          archivedByName: item.archivedByName || '-',
        };
      })
      .filter(Boolean) as OperationRow[];
  }, [archiveRows, rows]);

  const loadArchivePdfDocuments = async (archiveId: string) => {
    try {
      const res: any = await documentService.getDocuments({ entityType: 'archive', entityId: archiveId });
      const docs = Array.isArray(res?.data) ? res.data : [];
      setArchivePdfDocuments((prev) => ({ ...prev, [archiveId]: docs }));
    } catch {
      setArchivePdfDocuments((prev) => ({ ...prev, [archiveId]: [] }));
    }
  };

  useEffect(() => {
    if (operationTab === 'arsiv') {
      void loadArchiveRows();
    }
  }, [operationTab]);

  useEffect(() => {
    if (operationTab !== 'arsiv') return;
    const archiveIds = archiveRows.map((item: any) => String(item.id)).filter(Boolean);
    archiveIds.forEach((id) => { if (!archivePdfDocuments[id]) void loadArchivePdfDocuments(id); });
  }, [operationTab, archiveRows.length]);

  useEffect(() => {
    if (archiveModalOpen && activeArchiveRecord?.id) {
      documentService.getDocuments({ entityType: 'archive', entityId: String(activeArchiveRecord.id) })
        .then((res: any) => setModalArchivePdfDocs(Array.isArray(res?.data) ? res.data : []))
        .catch(() => setModalArchivePdfDocs([]));
    } else {
      setModalArchivePdfDocs([]);
    }
  }, [archiveModalOpen, activeArchiveRecord?.id]);

  const handleUploadArchivePdf = async (archiveId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploadingArchivePdfId(archiveId);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'archive');
        formData.append('entityId', archiveId);
        formData.append('documentType', 'archive-pdf');
        formData.append('name', file.name);
        await documentService.uploadDocument(formData);
        toast.success('PDF yüklendi');
        await loadArchivePdfDocuments(archiveId);
      } catch {
        toast.error('PDF yüklenemedi');
      } finally {
        setUploadingArchivePdfId(null);
      }
    };
    input.click();
  };

  useEffect(() => {
    if (!detailRow?.id || operationTab === 'arsiv' || archiveRecordByShipmentId[String(detailRow.id)]) return;
    archiveService.checkArchive(String(detailRow.id))
      .then((res: any) => {
        if (res?.data) {
          setArchiveRecordByShipmentId((current) => ({ ...current, [String(detailRow.id)]: res.data }));
        }
      })
      .catch(() => {});
  }, [detailRow?.id, operationTab, archiveRecordByShipmentId]);

  const showDocumentsTab = operationTab === 'fatura-bekleyen' || operationTab === 'odeme-bekleyen' || operationTab === 'odeme-gerceklesen' || operationTab === 'arsiv';
  const [sortColumn, setSortColumn] = useState<keyof OperationRow | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showTableFilters, setShowTableFilters] = useState(false);
  const [filters, setFilters] = useState<TrackingFilterRule[]>([]);
  const [externalTransportFilter, setExternalTransportFilter] = useState<'all' | 'evet' | 'hayir'>('all');
  const [viewLoaded, setViewLoaded] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [savedViewTabs, setSavedViewTabs] = useState<TrackingSavedViewTab[]>([]);
  const [activeViewTabId, setActiveViewTabId] = useState(TRACKING_MAIN_TAB_ID);
  const [newViewTabName, setNewViewTabName] = useState('');
  const [newViewTabVisibility, setNewViewTabVisibility] = useState<'private' | 'public'>('private');
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignRow, setAssignRow] = useState<OperationRow | null>(null);
  const [assignType, setAssignType] = useState<'own-fleet' | 'spot'>('own-fleet');
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [internalDrivers, setInternalDrivers] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [externalDrivers, setExternalDrivers] = useState<any[]>([]);
  const [corporateCarrierAccounts, setCorporateCarrierAccounts] = useState<any[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [selectedSpotCompanyId, setSelectedSpotCompanyId] = useState('all');
  const [agreedPrice, setAgreedPrice] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignmentSourcesLoading, setAssignmentSourcesLoading] = useState(false);
  const [fleetSearch, setFleetSearch] = useState('');
  const [showCreateSpotDriverModal, setShowCreateSpotDriverModal] = useState(false);
  const [creatingSpotDriver, setCreatingSpotDriver] = useState(false);
  const [newSpotDriverForm, setNewSpotDriverForm] = useState({
    full_name: '',
    phone: '',
    vehicle_type: '',
    cekici_plaka: '',
    dorse_plaka: '',
    city: '',
    district: '',
  });
  const [spotVehicleTypeOptions, setSpotVehicleTypeOptions] = useState<string[]>(DEFAULT_SPOT_VEHICLE_TYPE_OPTIONS);
  const [locationCheckingRowId, setLocationCheckingRowId] = useState<string | null>(null);
  const [kmCosts, setKmCosts] = useState({ ozmal: 12, spot: 18 });
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [rowContextMenu, setRowContextMenu] = useState<{ row: OperationRow; x: number; y: number } | null>(null);
  const [updatingRowStatusId, setUpdatingRowStatusId] = useState<string | null>(null);
  const [loadingShiftOptions, setLoadingShiftOptions] = useState(false);
  const [shiftOptions, setShiftOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [updatingShiftRowId, setUpdatingShiftRowId] = useState<string | null>(null);
  const handledShipmentParamRef = useRef<string>('');
  const dragColumnRef = useRef<keyof OperationRow | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isSuperAdmin = user?.role === 'super-admin';
  const canDeleteSupply = user?.role === 'admin' || user?.role === 'super-admin';

  useEffect(() => {
    settingsService.getSettingValue('settings_optimization', { ozmalKmCost: '12', spotKmCost: '18' }).then((val: any) => {
      setKmCosts({
        ozmal: Number(val?.ozmalKmCost || 12),
        spot: Number(val?.spotKmCost || 18),
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    staticDefinitionService.getByKey('shipment_vehicle_type')
      .then((response: any) => {
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        const values = items.map((item: any) => normalizeUpper(String(item.item_value || item.value || '').trim())).filter(Boolean);
        if (values.length > 0) setSpotVehicleTypeOptions(values);
      })
      .catch(() => {
        setSpotVehicleTypeOptions(DEFAULT_SPOT_VEHICLE_TYPE_OPTIONS);
      });
  }, []);

  useEffect(() => {
    staticDefinitionService.getByKey('archive_folder_numbers')
      .then((response: any) => {
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        setArchiveFolderOptions(items.map((item: any) => String(item.value || item.item_value || '').trim()).filter(Boolean));
      })
      .catch(() => setArchiveFolderOptions([]));
  }, []);

  useEffect(() => {
    const loadShifts = async () => {
      try {
        setLoadingShiftOptions(true);
        const response: any = await shiftService.getShifts();
        const items = Array.isArray(response?.data) ? response.data : Array.isArray(response?.data?.data) ? response.data.data : [];
        setShiftOptions(items
          .filter((item: any) => String(item?.status || 'active') !== 'inactive')
          .map((item: any) => ({ id: String(item.id), label: `${item.code || '-'} - ${item.name || 'Vardiya'}` })));
      } catch (error) {
        console.error('Load shifts failed:', error);
        setShiftOptions([]);
      } finally {
        setLoadingShiftOptions(false);
      }
    };
    void loadShifts();
  }, []);

  useEffect(() => {
    if (!rowContextMenu) return;
    const closeMenu = () => setRowContextMenu(null);
    window.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [rowContextMenu]);

   const showDriverDetailTab = operationTab === 'atandi-islem-bekliyor' || operationTab === 'yukleme-bekliyor' || operationTab === 'yukleme-devam' || operationTab === 'yukleme-tamamlandi' || operationTab === 'tasima-yolunda' || operationTab === 'teslim-noktasina-ulasildi' || operationTab === 'bosaltma-bekliyor' || operationTab === 'bosaltma-devam' || operationTab === 'bosaltma-tamamlandi' || operationTab === 'fatura-bekleyen' || operationTab === 'odeme-bekleyen' || operationTab === 'odeme-gerceklesen' || operationTab === 'arsiv';
   const showTrackingDetailTab = operationTab === 'yukleme-bekliyor' || operationTab === 'yukleme-devam' || operationTab === 'yukleme-tamamlandi' || operationTab === 'tasima-yolunda' || operationTab === 'teslim-noktasina-ulasildi' || operationTab === 'bosaltma-bekliyor' || operationTab === 'bosaltma-devam' || operationTab === 'bosaltma-tamamlandi' || operationTab === 'fatura-bekleyen' || operationTab === 'odeme-bekleyen' || operationTab === 'odeme-gerceklesen' || operationTab === 'arsiv';

  const renderWaitingStatus = (row: OperationRow) => activeBidRequestShipmentIds.includes(String(row.id))
    ? <span className="inline-flex rounded-full bg-violet-950/60 px-2.5 py-1 text-xs font-semibold text-violet-300">Teklif Bekliyor</span>
    : <span className="inline-flex rounded-full bg-pink-950/60 px-2.5 py-1 text-xs font-semibold text-pink-300">Tedarik Bekliyor</span>;

  const getWaitingStatusLabel = (row: OperationRow) => activeBidRequestShipmentIds.includes(String(row.id)) ? 'Teklif Bekliyor' : 'Tedarik Bekliyor';
  const getWaitingLogisticsLabel = (row: OperationRow) => activeBidRequestShipmentIds.includes(String(row.id)) ? 'Spot' : 'Belirlenmedi';

  const userColumnsKey = useMemo(() => buildUserScopedColumnsKey(), []);
  const userBasicViewKey = useMemo(() => buildUserScopedStorageKey(TRACKING_BASIC_VIEW_KEY), []);
  const userSavedTabsKey = useMemo(() => buildUserScopedStorageKey(TRACKING_SAVED_TABS_KEY), []);
  const sharedSavedTabsKey = useMemo(() => buildUserScopedStorageKey(TRACKING_SHARED_TABS_KEY).replace(/:[^:]+$/, ':shared'), []);

  const getTrackingComparableCellValue = (row: OperationRow, key: keyof OperationRow) => {
    const rawValue = row[key];
    if (key === 'durum') return `${rawValue || ''} ${String(row.durum || '')}`;
    if (key === 'islemDurum') return `${rawValue || ''} ${String(row.islemDurum || '')}`;
    if (key === 'accountingStatus') return `${rawValue || ''} ${String(row.accountingStatus || '')}`;
    return String(rawValue ?? '');
  };

  const getTrackingDateComparableValue = (value: string | number | null | undefined) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    const trMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
    if (trMatch) return `${trMatch[3]}-${trMatch[2]}-${trMatch[1]}`;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '';
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const allColumns = useMemo<ColumnDef[]>(() => [
    { key: 'archiveCode', label: 'ARŞİV NO', width: '120px', render: (row) => operationTab === 'arsiv' && row.archiveId ? <button type="button" onClick={(e) => { e.stopPropagation(); openArchiveEditModal(row); }} className="font-semibold text-amber-400 underline decoration-dotted underline-offset-2 hover:text-amber-300">{row.archiveCode || '-'}</button> : <span className="font-semibold text-amber-400">{row.archiveCode || '-'}</span> },
    { key: 'folderNumber', label: 'KLASÖR', width: '120px', render: (row) => row.folderName || '-' },
    { key: 'evrakSiraNo', label: 'EVRAK SIRA NO', width: '130px', render: (row) => row.evrakSiraNo || '-' },
    { key: 'invoiceNumber', label: 'FATURA NO', width: '140px', render: (row) => row.invoiceNumber || '-' },
    { key: 'hasFuelPurchase', label: 'YAKIT', width: '100px', render: (row) => row.hasFuelPurchase || '-' },
    {
      key: 'archivePdf' as keyof OperationRow,
      label: 'PDF',
      width: '100px',
      render: (row) => {
        if (!row.archiveId) return '-';
        const docs = archivePdfDocuments[row.archiveId] || [];
        if (docs.length > 0) {
          const doc = docs[0];
          const fileUrl = resolveFileUrl(doc.filePath);
          return (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPreviewArchivePdf({ url: fileUrl, name: doc.fileName || 'PDF' }); }}
                className="rounded border border-blue-700/40 bg-blue-950/40 px-2 py-1 text-[11px] font-semibold text-blue-200 hover:bg-blue-950/60"
                title="Görüntüle"
              >
                <Eye className="w-3 h-3" />
              </button>
              <a href={fileUrl} download className="rounded border border-emerald-700/40 bg-emerald-950/40 px-2 py-1 text-[11px] font-semibold text-emerald-200 hover:bg-emerald-950/60" title="İndir">
                <Download className="w-3 h-3" />
              </a>
            </div>
          );
        }
        return (
          <button
            type="button"
            disabled={uploadingArchivePdfId === row.archiveId}
            onClick={(e) => { e.stopPropagation(); void handleUploadArchivePdf(row.archiveId!); }}
            className="rounded border border-slate-700/40 bg-[#111111] px-2 py-1 text-[11px] font-semibold text-slate-300 hover:bg-white/5 disabled:opacity-50"
          >
            {uploadingArchivePdfId === row.archiveId ? '...' : 'PDF Yükle'}
          </button>
        );
      },
    },
    { key: 'sevkKodu', label: 'SİPARİŞ #', width: '120px', render: (row) => <span className="font-semibold text-slate-100">{row.sevkKodu}</span> },
    { key: 'planKodu', label: 'PLAN KODU', width: '120px', render: (row) => row.planKodu || '-' },
    { key: 'cekici', label: 'ÇEKİCİ', width: '150px' },
    { key: 'dorse', label: 'DORSE', width: '150px' },
    { key: 'surucuAdi', label: 'SÜRÜCÜ ADI', width: '180px' },
    {
      key: 'surucuTelefon',
      label: 'SÜRÜCÜ TELEFON',
      width: '210px',
      render: (row) => {
        if (!row.surucuTelefon) return '-';
        const digits = row.surucuTelefon.replace(/\D/g, '');
        return (
          <div className="flex items-center gap-2">
            <span>{row.surucuTelefon}</span>
            <a href={`tel:${digits}`} className="rounded border border-slate-700/40 p-1 text-slate-300 hover:bg-white/5" title="Ara">
              <Phone className="w-3.5 h-3.5" />
            </a>
            <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" className="rounded border border-slate-700/40 p-1 text-slate-300 hover:bg-white/5" title="WhatsApp">
              <MessageCircle className="w-3.5 h-3.5" />
            </a>
          </div>
        );
      },
    },
    { key: 'tcKimlikNo', label: 'TC KİMLİK NO', width: '150px' },
    { key: 'durum', label: 'DURUM', width: '160px', render: (row) => operationTab === 'atama-bekliyor' ? renderWaitingStatus(row) : renderSoftStatus(row.durum) },
    { key: 'faturaBasligi', label: 'FATURA BAŞLIĞI', width: '180px' },
    { key: 'disNakliye', label: 'DIŞ NAKLİYE', width: '130px', render: (row) => renderSoftStatus(row.disNakliye || '-') },
    { key: 'atamaYaniti', label: 'ATAMA YANITI', width: '150px', render: (row) => renderSoftStatus(row.atamaYaniti) },
    { key: 'yuklemeMusterisi', label: 'YÜKLEME MÜŞTERİSİ', width: '240px' },
    { key: 'bosaltmaMusterisi', label: 'TESLİM MÜŞTERİSİ', width: '240px' },
    {
      key: 'yuklemeIli',
      label: 'YÜKLEME İLİ',
      width: '220px',
    },
    { key: 'bosaltmaIli', label: 'TESLİM İLİ', width: '140px' },
    { key: 'nearestHub', label: 'EN YAKIN HUB', width: '220px' },
    { key: 'teslimIlcesi', label: 'TESLİM İLÇESİ', width: '150px' },
    { key: 'aracAtamaTarihi', label: 'ATAMA TARİHİ', width: '150px', render: (row) => formatDateTime(row.aracAtamaTarihi) },
    { key: 'yuklemeTarihi', label: 'YÜKLEME TARİHİ', width: '150px', render: (row) => formatDateTime(row.yuklemeTarihi) },
    {
      key: 'yuklenecekVardiya',
      label: 'YÜKLENECEK VARDİYA',
      width: '220px',
      render: (row) => (
        <select
          value={row.pickupShiftId || ''}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { e.stopPropagation(); void handlePickupShiftChange(row, e.target.value); }}
          disabled={loadingShiftOptions || updatingShiftRowId === row.id}
          className="h-9 w-full rounded-lg border border-slate-700/40 bg-[#111111] px-2 text-[11px] text-slate-100 outline-none focus:border-orange-400 disabled:opacity-60"
        >
          <option value="">Vardiya Seç</option>
          {shiftOptions.map((shift) => (
            <option key={shift.id} value={shift.id}>{shift.label}</option>
          ))}
        </select>
      ),
    },
    { key: 'terminTarihi', label: 'TERMIN TARİHİ', width: '160px', render: (row) => formatDateTime(row.terminTarihi) },
    { key: 'randevuluTeslim', label: 'RANDEVULU TESLİM', width: '150px', render: (row) => renderSoftStatus(row.randevuluTeslim || 'Standart') },
    { key: 'ugrmaSayisi', label: 'UĞRAMA SAYISI', width: '130px' },
    { key: 'navlun', label: 'NAVLUN', width: '120px', render: (row) => `₺${Number(row.navlun || 0).toLocaleString('tr-TR')}` },
    { key: 'seferKm', label: 'SEFER KM', width: '110px' },
    { key: 'tlKm', label: 'TL/KM', width: '100px', render: (row) => Number(row.tlKm || 0).toLocaleString('tr-TR') },
    { key: 'lojistikTuru', label: 'LOJİSTİK', width: '130px', render: (row) => operationTab === 'atama-bekliyor' ? renderSoftStatus(getWaitingLogisticsLabel(row)) : (row.lojistikTuru || '-') },
    { key: 'hizmetTuru', label: 'HİZMET', width: '120px' },
    { key: 'aracDurum', label: 'ARAÇ DURUM', width: '140px', render: (row) => renderSoftStatus(row.aracDurum) },
    { key: 'operasyonNot', label: 'NOT', width: '220px', render: (row) => <span className="text-[12px] text-slate-300">{row.operasyonNot || '-'}</span> },
  ], [operationTab, activeBidRequestShipmentIds, shiftOptions, loadingShiftOptions, updatingShiftRowId, detailRow]);

  useEffect(() => {
    try {
      const rawView = localStorage.getItem(userBasicViewKey);
      if (rawView) {
        const parsedView = JSON.parse(rawView);
        if (parsedView && typeof parsedView === 'object' && Array.isArray(parsedView.visibleColumns) && parsedView.visibleColumns.length > 0) {
          setVisibleColumns(withRequiredTrackingColumns(parsedView.visibleColumns.map(String)));
          return;
        }
      }
      const raw = localStorage.getItem(userColumnsKey);
      if (!raw) {
        setVisibleColumns(withRequiredTrackingColumns(DEFAULT_TRACKING_COLUMNS));
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        setVisibleColumns(withRequiredTrackingColumns(parsed));
      } else {
        setVisibleColumns(withRequiredTrackingColumns(DEFAULT_TRACKING_COLUMNS));
      }
    } catch {
      setVisibleColumns(withRequiredTrackingColumns(DEFAULT_TRACKING_COLUMNS));
    }
  }, [allColumns, userColumnsKey, userBasicViewKey]);

  useEffect(() => {
    if (!viewLoaded) return;
    localStorage.setItem(userColumnsKey, JSON.stringify(visibleColumns));
  }, [visibleColumns, userColumnsKey, viewLoaded]);

  useEffect(() => {
    try {
      const rawView = localStorage.getItem(userBasicViewKey);
      if (rawView) {
        const parsed = JSON.parse(rawView);
          if (parsed && typeof parsed === 'object') {
            if (Array.isArray(parsed.visibleColumns) && parsed.visibleColumns.length > 0) {
              setVisibleColumns(withRequiredTrackingColumns(parsed.visibleColumns.map(String)));
            }
            if (parsed.pageSize) setPageSize(Number(parsed.pageSize) || 25);
            if (parsed.sortColumn) setSortColumn(parsed.sortColumn as keyof OperationRow);
            if (parsed.sortDirection === 'desc') setSortDirection('desc');
            if (parsed.externalTransportFilter === 'evet' || parsed.externalTransportFilter === 'hayir' || parsed.externalTransportFilter === 'all') {
              setExternalTransportFilter(parsed.externalTransportFilter);
            }
            if (Array.isArray(parsed.filters)) {
              setFilters(parsed.filters.filter((rule: any) => rule && rule.column).map((rule: any, index: number) => ({
                id: String(rule.id || makeTrackingFilterId()),
                join: index === 0 ? 'and' : (rule.join === 'or' ? 'or' : 'and'),
                column: rule.column as keyof OperationRow,
                operator: rule.operator || 'contains',
                value: String(rule.value || ''),
              })));
            }
          }
      }
    } catch {
      // ignore saved view parse errors
    }

    try {
      const parseTabs = (raw: string | null, fallbackVisibility: 'private' | 'public') => {
        if (!raw) return [] as TrackingSavedViewTab[];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [] as TrackingSavedViewTab[];
        return parsed.filter((item) => item && String(item.name || '').trim()).map((item) => ({
            id: String(item.id || makeTrackingViewId()),
            name: String(item.name || 'Tab'),
            visibility: item.visibility === 'public' ? 'public' : fallbackVisibility,
            operationTab: item.operationTab || 'atama-bekliyor',
            search: String(item.search || ''),
            externalTransportFilter: item.externalTransportFilter === 'evet' || item.externalTransportFilter === 'hayir' ? item.externalTransportFilter : 'all',
            filters: Array.isArray(item.filters)
              ? item.filters
                .filter((rule: any) => rule && rule.column)
                .map((rule: any, index: number) => ({
                  id: String(rule.id || makeTrackingFilterId()),
                  join: index === 0 ? 'and' : (rule.join === 'or' ? 'or' : 'and'),
                  column: rule.column as keyof OperationRow,
                  operator: rule.operator || 'contains',
                  value: String(rule.value || ''),
                }))
              : (item.tableFilters && typeof item.tableFilters === 'object'
                ? Object.entries(item.tableFilters).filter(([, value]) => String(value || '').trim()).map(([column, value], index) => ({
                    id: makeTrackingFilterId(),
                    join: index === 0 ? 'and' : 'and',
                    column: column as keyof OperationRow,
                    operator: 'contains' as TrackingFilterOperator,
                    value: String(value || ''),
                  }))
                : []),
            sortColumn: item.sortColumn || null,
            sortDirection: item.sortDirection === 'desc' ? 'desc' : 'asc',
            pageSize: Number(item.pageSize || 25),
          }));
      };
      const privateTabs = parseTabs(localStorage.getItem(userSavedTabsKey), 'private');
      const publicTabs = parseTabs(localStorage.getItem(sharedSavedTabsKey), 'public');
      setSavedViewTabs([...publicTabs, ...privateTabs]);
    } catch {
      // ignore saved tab parse errors
    }
    setViewLoaded(true);
  }, [userBasicViewKey, userSavedTabsKey, sharedSavedTabsKey]);

  useEffect(() => {
    if (detailTab !== 'tracking') return;
    const id = setInterval(() => setLiveTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [detailTab]);

  const loadTrackingDocuments = async (shipmentId: string, currentOperationTab: string) => {
    if (currentOperationTab !== 'yukleme-tamamlandi' && currentOperationTab !== 'tasima-yolunda' && currentOperationTab !== 'teslim-noktasina-ulasildi' && currentOperationTab !== 'bosaltma-bekliyor' && currentOperationTab !== 'bosaltma-devam' && currentOperationTab !== 'bosaltma-tamamlandi' && currentOperationTab !== 'fatura-bekleyen' && currentOperationTab !== 'odeme-bekleyen' && currentOperationTab !== 'odeme-gerceklesen' && currentOperationTab !== 'arsiv') {
      setCompletionDocuments([]);
      setDeliveryDocuments([]);
      setShortageSurplusDocuments([]);
      setIncidentDocuments([]);
      setInvoiceDocuments([]);
      setPaymentReceiptDocuments([]);
      return;
    }

    try {
      const [completionRes, stageRes, deliveryRes, shortageRes, incidentRes, invoiceRes, receiptRes] = await Promise.all([
        documentService.getDocuments({ entityType: 'shipment', entityId: shipmentId, documentType: 'completion' }),
        documentService.getDocuments({ entityType: 'shipment', entityId: shipmentId, documentType: 'shipment-stage-document' }),
        (currentOperationTab === 'fatura-bekleyen' || currentOperationTab === 'odeme-bekleyen' || currentOperationTab === 'odeme-gerceklesen' || currentOperationTab === 'bosaltma-tamamlandi' || currentOperationTab === 'arsiv')
          ? documentService.getDocuments({ entityType: 'shipment', entityId: shipmentId, documentType: 'delivery-report' })
          : Promise.resolve({ data: [] }),
        (currentOperationTab === 'fatura-bekleyen' || currentOperationTab === 'odeme-bekleyen' || currentOperationTab === 'odeme-gerceklesen' || currentOperationTab === 'bosaltma-tamamlandi' || currentOperationTab === 'arsiv')
          ? documentService.getDocuments({ entityType: 'shipment', entityId: shipmentId, documentType: 'shortage-surplus-report' })
          : Promise.resolve({ data: [] }),
        (currentOperationTab === 'fatura-bekleyen' || currentOperationTab === 'odeme-bekleyen' || currentOperationTab === 'odeme-gerceklesen' || currentOperationTab === 'bosaltma-tamamlandi' || currentOperationTab === 'arsiv')
          ? documentService.getDocuments({ entityType: 'shipment', entityId: shipmentId, documentType: 'incident-report' })
          : Promise.resolve({ data: [] }),
        (currentOperationTab === 'fatura-bekleyen' || currentOperationTab === 'odeme-bekleyen' || currentOperationTab === 'odeme-gerceklesen' || currentOperationTab === 'arsiv')
          ? documentService.getDocuments({ entityType: 'shipment', entityId: shipmentId, documentType: 'invoice' })
          : Promise.resolve({ data: [] }),
        (currentOperationTab === 'odeme-bekleyen' || currentOperationTab === 'odeme-gerceklesen' || currentOperationTab === 'arsiv')
          ? documentService.getDocuments({ entityType: 'shipment', entityId: shipmentId, documentType: 'payment-receipt' })
          : Promise.resolve({ data: [] }),
      ]);
      const completionDocs = Array.isArray(completionRes?.data) ? completionRes.data : [];
      const stageDocs = Array.isArray(stageRes?.data) ? stageRes.data : [];
      const deliveryDocs = Array.isArray(deliveryRes?.data) ? deliveryRes.data : [];
      const shortageDocs = Array.isArray(shortageRes?.data) ? shortageRes.data : [];
      const incidentDocs = Array.isArray(incidentRes?.data) ? incidentRes.data : [];
      const invoiceDocs = Array.isArray(invoiceRes?.data) ? invoiceRes.data : [];
      const receiptDocs = Array.isArray(receiptRes?.data) ? receiptRes.data : [];
      const allCompletionDocs = [
        ...completionDocs,
        ...stageDocs.filter((d: any) => d.stage === 4 || d.stage === '4' || !d.stage),
      ];
      setCompletionDocuments(allCompletionDocs);
      setDeliveryDocuments(deliveryDocs);
      setShortageSurplusDocuments(shortageDocs);
      setIncidentDocuments(incidentDocs);
      setInvoiceDocuments(invoiceDocs);
      setPaymentReceiptDocuments(receiptDocs);
    } catch {
      setCompletionDocuments([]);
      setDeliveryDocuments([]);
      setShortageSurplusDocuments([]);
      setIncidentDocuments([]);
      setInvoiceDocuments([]);
      setPaymentReceiptDocuments([]);
    }
  };

  useEffect(() => {
    if ((detailTab !== 'tracking' && detailTab !== 'documents') || !detailRow?.id) return;
    loadTrackingDocuments(detailRow.id, operationTab);
  }, [detailTab, detailRow?.id, operationTab]);

  const handleTrackingDocumentUpload = async (files: FileList | null) => {
    if (!detailRow?.id || (operationTab !== 'yukleme-tamamlandi' && operationTab !== 'tasima-yolunda' && operationTab !== 'teslim-noktasina-ulasildi' && operationTab !== 'bosaltma-bekliyor' && operationTab !== 'fatura-bekleyen' && operationTab !== 'odeme-bekleyen' && operationTab !== 'odeme-gerceklesen') || !files || files.length === 0) return;

    setUploadingTrackingDocuments(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'shipment');
        formData.append('entityId', String(detailRow.id));
        formData.append('documentType', 'shipment-stage-document');
        formData.append('name', file.name);
        formData.append('stage', '4');
        await documentService.uploadDocument(formData);
      }
      await loadTrackingDocuments(detailRow.id, operationTab);
      toast.success('Evrak yüklendi');
    } catch (error) {
      console.error('Tracking document upload failed:', error);
      toast.error('Evrak yüklenemedi');
    } finally {
      setUploadingTrackingDocuments(false);
    }
  };

  const handleTrackingDocumentDelete = async (documentId: string | number) => {
    if (!detailRow?.id || (operationTab !== 'yukleme-tamamlandi' && operationTab !== 'tasima-yolunda' && operationTab !== 'teslim-noktasina-ulasildi' && operationTab !== 'bosaltma-bekliyor' && operationTab !== 'fatura-bekleyen' && operationTab !== 'odeme-bekleyen' && operationTab !== 'odeme-gerceklesen')) return;

    setDeletingTrackingDocumentId(documentId);
    try {
      await documentService.deleteDocument(documentId);
      await loadTrackingDocuments(detailRow.id, operationTab);
      toast.success('Evrak silindi');
    } catch (error) {
      console.error('Tracking document delete failed:', error);
      toast.error('Evrak silinemedi');
    } finally {
      setDeletingTrackingDocumentId(null);
    }
  };

  const loadRows = async () => {
    const [response, bidRequestsResponse]: any = await Promise.all([
      supplyService.getAll(),
      bidService.getBidRequests({ status: 'open' }).catch(() => ({ data: [] })),
    ]);
    const list = Array.isArray(response?.data) ? response.data : [];
    const bidRequests = Array.isArray(bidRequestsResponse?.data) ? bidRequestsResponse.data : [];
    setActiveBidRequestShipmentIds(bidRequests.map((item: any) => String(item.shipment_id || item.shipmentId || '')).filter(Boolean));

    const mapped: OperationRow[] = list.map((item: any, index: number) => {
      const seferKm = Number(item.sefer_km || item.distance_km || item.mesafe || item.distance || 0);
      const navlun = Number(item.fixed_price || item.navlun || item.final_price || item.maliyet || item.cost || 0);
      const bazFiyat = Number(item.base_price || item.basePrice || item.maliyet || item.cost || 0);
      const assignmentResponseStatus = String(item.assignment_response_status || 'accepted').trim();
      const lojistikTuru = item.arac_tipi || item.transport_model || '-';
        return {
          rowKey: [
            String(item.id || ''),
            String(item.sevk_kodu || item.shipment_no || ''),
          String(item.process_status || item.islem_durum || ''),
          assignmentResponseStatus,
          String(index),
        ].join('::'),
        id: String(item.id || ''),
        sevkKodu: item.sevk_kodu || item.shipment_no || '-',
        planKodu: item.plan_kodu || item.plan_code || '-',
        cekici: item.cekici || item.cekici_plaka || item.arac_plaka || item.assigned_vehicle_plate || '-',
        dorse: item.dorse_plaka || item.assigned_trailer_plate || '-',
        surucuAdi: item.surucu_bilgileri || item.driver_name || item.assigned_driver_name || '-',
        surucuTelefon: normalizePhone(item.telefon || item.source_phone || item.shipment_contact_phone || item.driver_phone || item.assigned_driver_phone),
        tcKimlikNo: item.tc_kimlik_no || item.tc_number || '-',
        faturaBasligi: item.fatura_basligi || item.invoice_title || '-',
        disNakliye: item.dis_nakliye || (Number(item.is_external_transport || 0) > 0 ? 'Evet' : 'Hayır'),
        yuklemeMusterisi: item.yukleme_musterisi_ad || item.pickup_name || '-',
        bosaltmaMusterisi: item.teslim_musterisi_ad || item.delivery_name || '-',
        yuklemeIli: item.yukleme_il || item.pickup_city || '-',
        yuklemeIlcesi: item.yukleme_ilce || item.pickup_district || '-',
        bosaltmaIli: item.teslim_il || item.delivery_city || '-',
        nearestHub: item.nearest_hub_name ? `${item.nearest_hub_name}${item.nearest_hub_km != null ? ` (${Number(item.nearest_hub_km).toFixed(1)} km)` : ''}` : '-',
        teslimIlcesi: item.teslim_ilce || item.delivery_district || '-',
        yuklemeAdres: item.yukleme_adres || item.pickup_address || '-',
        teslimAdres: item.teslim_adres || item.delivery_address || '-',
        aracAtamaTarihi: item.arac_atama_tarihi || item.assigned_at || '',
        yuklemeTarihi: item.yukleme_talep_tarihi || item.pickup_date || '',
        terminTarihi: item.delivery_date || item.istenen_teslim_tarihi || '',
        randevuluTeslim: item.delivery_date || item.istenen_teslim_tarihi ? 'Randevulu' : 'Standart',
        ugrmaSayisi: Number(item.stop_count || item.ugrama_sayisi || 1),
        navlun,
        bazFiyat,
        seferKm,
        tlKm: seferKm > 0 ? Number((navlun / seferKm).toFixed(2)) : 0,
        lojistikTuru,
        hizmetTuru: item.yuk_tipi || item.load_type || '-',
        operasyonNot: sanitizeShipmentNote(item.operasyon_not || item.aciklama || item.notlar || item.notes),
        sonNot: item.last_note || '-',
        sonNotYazan: item.last_note_author_name || '-',
        araNoktalar: (extractIntermediateStopsFromRoutePoints(item.route_points).length > 0
          ? extractIntermediateStopsFromRoutePoints(item.route_points)
          : extractIntermediateStops(item.operasyon_not || item.aciklama || item.notlar || item.notes || '')
        ).join(' | ') || '-',
        routePoints: Array.isArray(item.route_points) ? item.route_points : [],
        aracDurum: item.durum || item.status || '-',
        yuklemeCikisTarihi: item.pickup_departed_at || '',
        teslimVarisTarihi: item.delivery_arrived_at || '',
        odemeTarihi: item.payment_completed_at || item.paid_at || item.payment_date || item.odeme_tarihi || '',
        shipmentContactPhone: item.shipment_contact_phone || item.source_phone || '',
        pickupWarehouseId: item.pickup_warehouse_id ? String(item.pickup_warehouse_id) : null,
        pickupShiftId: item.pickup_shift_id != null ? String(item.pickup_shift_id) : null,
        yuklenecekVardiya: item.pickup_shift_id != null ? String(item.pickup_shift_id) : '-',
        createdBy: item.created_by != null ? String(item.created_by) : null,
        createdAt: item.created_at || item.createdAt || '',
        updatedAt: item.updated_at || item.updatedAt || '',
        islemDurum: item.islem_durum || item.process_status || item.durum || item.status || '-',
        durum: item.durum || item.status || '-',
        accountingStatus: item.accounting_status || '-',
        assignmentResponseStatus,
        atamaYaniti: normalizeTransportModel(lojistikTuru) === 'own-fleet' ? mapAssignmentResponseLabel(assignmentResponseStatus) : '-',
        pickupLatitude: item.pickup_latitude != null ? Number(item.pickup_latitude) : null,
        pickupLongitude: item.pickup_longitude != null ? Number(item.pickup_longitude) : null,
        yuklemeKonum: item.pickup_latitude != null && item.pickup_longitude != null
          ? `${Number(item.pickup_latitude).toFixed(5)}, ${Number(item.pickup_longitude).toFixed(5)}`
          : (item.pickup_customer_latitude != null && item.pickup_customer_longitude != null
              ? `${Number(item.pickup_customer_latitude).toFixed(5)}, ${Number(item.pickup_customer_longitude).toFixed(5)}`
              : '-'),
        yukAgirlik: Number(item.yuk_agirlik || item.weight_kg || 0),
        yukHacim: Number(item.yuk_hacim || item.volume_m3 || 0),
        palletCount: Number(item.pallet_count || 0),
        istenenKasaTipi: item.arac_turu || item.vehicle_spec_type || item.aracTipi || item.vehicle_type || '',
      };
    });

    mapped.sort((a, b) => {
      const left = new Date(b.createdAt || b.updatedAt || b.aracAtamaTarihi || b.yuklemeTarihi || 0).getTime();
      const right = new Date(a.createdAt || a.updatedAt || a.aracAtamaTarihi || a.yuklemeTarihi || 0).getTime();
      return left - right;
    });

    setRows(mapped);
  };

  useEffect(() => {
    if (!user?.id || hasUserRole(user, 'admin')) return;
    userService.getUser(user.id).then((response: any) => {
      const item = response?.data || {};
      const allowedPickupWarehouseIds = Array.isArray(item.allowed_pickup_warehouse_ids)
        ? item.allowed_pickup_warehouse_ids.map(String)
        : typeof item.allowed_pickup_warehouse_ids === 'string'
          ? JSON.parse(item.allowed_pickup_warehouse_ids || '[]').map(String)
          : [];
      setTrackingScope({
        allowedPickupWarehouseIds,
        canViewNonWarehousePickups: Boolean(item.can_view_non_warehouse_pickups),
      });
    }).catch(() => setTrackingScope({ allowedPickupWarehouseIds: [], canViewNonWarehousePickups: false }));
  }, [user?.id, user?.role]);

  const loadAssignmentSources = async () => {
    setAssignmentSourcesLoading(true);
    try {
      const [vehiclesRes, internalDriversRes, applicationsRes, externalDriversRes, corporateAccountsRes] = await Promise.all([
        vehicleService.getVehicles().catch(() => ({ data: [] })),
        driverService.getDrivers({ type: 'internal', status: 'active' }).catch(() => ({ data: [] })),
        driverService.getApplications({ status: 'approved' }).catch(() => ({ data: [] })),
        driverService.getDrivers({ type: 'external', status: 'active' }).catch(() => ({ data: [] })),
        userService.getUsers({ role: 'corporate-external-transport', status: 'active' }).catch(() => ({ data: [] })),
      ]);

      setVehicles(Array.isArray(vehiclesRes?.data) ? vehiclesRes.data : []);
      setInternalDrivers(Array.isArray(internalDriversRes?.data) ? internalDriversRes.data : []);
      setApplications(Array.isArray(applicationsRes?.data) ? applicationsRes.data : []);
      setExternalDrivers(Array.isArray(externalDriversRes?.data) ? externalDriversRes.data : []);
      setCorporateCarrierAccounts(Array.isArray(corporateAccountsRes?.data) ? corporateAccountsRes.data : []);
    } finally {
      setAssignmentSourcesLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        await Promise.all([
          loadRows(),
          loadArchiveRows(),
          loadAssignmentSources(),
          settingsService.getSettings().then((res: any) => {
            const map: Record<string, string> = {};
            for (const row of res?.data || []) {
              if (row.setting_key.startsWith('optimization_')) {
                map[row.setting_key.replace('optimization_', '')] = row.setting_value;
              }
            }
            setKmCosts({
              ozmal: toNumber(map.kmCost) || toNumber(map.ozmalKmCost) || 12,
              spot: toNumber(map.spotKmCost) || 18,
            });
          }).catch(() => null),
        ]);
      } catch (error) {
        console.error('Tracking load failed:', error);
        toast.error('Operasyon verileri alınamadı');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  useAutoRefresh(() => Promise.all([
    loadRows().catch((error) => console.error('Tracking auto refresh failed:', error)),
    loadArchiveRows().catch(() => null),
  ]), { intervalMs: 30000 });

  useEffect(() => {
    const shipmentParam = String(searchParams.get('shipment') || '').trim();
    if (!shipmentParam) return;
    if (handledShipmentParamRef.current === shipmentParam) return;

    const archiveMatch = archiveDisplayRows.find((row) => String(row.id) === shipmentParam || String(row.sevkKodu) === shipmentParam);
    const activeMatch = archiveMatch || rows.find((row) => String(row.id) === shipmentParam || String(row.sevkKodu) === shipmentParam);
    if (!activeMatch) return;

    handledShipmentParamRef.current = shipmentParam;
    const targetTab = resolveTrackingOperationTabForRow(activeMatch, Boolean(archiveMatch));
    if (operationTab !== targetTab) setOperationTab(targetTab);
    if (search) setSearch('');
    if (externalTransportFilter !== 'all') setExternalTransportFilter('all');
    if (filters.length > 0) setFilters([]);
    if (!showDetailPanel) setShowDetailPanel(true);
    if (detailTab !== 'info') setDetailTab('info');
    if (!detailRow || detailRow.rowKey !== activeMatch.rowKey) setDetailRow(activeMatch);
  }, [searchParams, rows, archiveDisplayRows, operationTab, search, externalTransportFilter, filters, showDetailPanel, detailTab, detailRow]);

  useEffect(() => {
    if (!detailRow || !showTrackingDetailTab) return;

    let cancelled = false;
    const loadWaitingRecords = async () => {
      try {
        const response: any = await warehouseService.getWaitingRecords({ shipmentId: detailRow.id });
        const rows = Array.isArray(response?.data) ? response.data : [];
        if (!cancelled) {
          setWaitingRecordsByShipment((current) => ({ ...current, [detailRow.id]: rows }));
        }
      } catch (error) {
        console.error('Waiting records load failed:', error);
      }
    };

    void loadWaitingRecords();
    return () => {
      cancelled = true;
    };
  }, [detailRow, showTrackingDetailTab]);

  const renderDocumentGrid = (documents: any[], tone: 'blue' | 'emerald' | 'violet', emptyText: string) => {
    const toneClasses = {
      blue: {
        card: 'border-slate-700/30 bg-[#111111]',
        delete: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
      },
      emerald: {
        card: 'border-slate-700/30 bg-[#111111]',
        delete: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
      },
      violet: {
        card: 'border-slate-700/30 bg-[#111111]',
        delete: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
      },
    }[tone];

    return documents && documents.length > 0 ? (
      <div className="grid grid-cols-2 gap-2">
        {documents.map((doc: any, index: number) => {
          const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
          const rawPath = doc.file_path || doc.file_url || doc.filePath || doc.url || '';
          const url = rawPath.startsWith('http') ? rawPath : `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
          const fileName = doc.file_name || doc.fileName || doc.name || `Evrak ${index + 1}`;
          const isImage = (doc.file_type || doc.mime_type || '').startsWith('image/') || fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i);

          return (
            <div key={doc.id || `${fileName}-${index}`} className={`rounded-lg border overflow-hidden ${toneClasses.card}`}>
              {isImage ? (
                <button type="button" onClick={() => setPreviewImageUrl(url)} className="w-full text-left">
                  <img src={url} alt={fileName} className="h-32 w-full object-cover" />
                </button>
              ) : (
                <a href={url} target="_blank" rel="noopener noreferrer" className="flex h-32 items-center justify-center px-3 text-center text-sm font-medium text-blue-600 hover:text-blue-800">
                  {fileName}
                </a>
              )}
              <div className="flex items-center justify-between gap-2 px-2 py-1">
                <div className="min-w-0 truncate text-[10px] text-slate-400">{fileName}</div>
                {doc.id && (
                  <button
                    type="button"
                    onClick={() => handleTrackingDocumentDelete(doc.id)}
                    disabled={deletingTrackingDocumentId === doc.id}
                    className={`inline-flex items-center rounded border px-1.5 py-1 text-[10px] font-semibold disabled:opacity-60 ${toneClasses.delete}`}
                  >
                    {deletingTrackingDocumentId === doc.id ? 'Siliniyor' : 'Sil'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="rounded-lg border border-dashed border-slate-700/40 bg-[#111111] px-3 py-6 text-center text-[11px] text-slate-400">
        {emptyText}
      </div>
    );
  };

  const scopedRows = useMemo(() => {
    let result = [...rows];
    if (user?.role !== 'admin' && trackingScope.allowedPickupWarehouseIds.length > 0) {
      result = result.filter((row) => {
        const matchesWarehouse = row.pickupWarehouseId && trackingScope.allowedPickupWarehouseIds.includes(String(row.pickupWarehouseId));
        const isNonWarehousePickup = !row.pickupWarehouseId;
        const isOwnShipment = row.createdBy && String(row.createdBy) === String(user?.id || '');
        return Boolean(matchesWarehouse) || (trackingScope.canViewNonWarehousePickups && isNonWarehousePickup) || (isNonWarehousePickup && isOwnShipment);
      });
    }
    return result;
  }, [rows, trackingScope, user?.role, user?.id]);

  const scopedArchiveRows = useMemo(() => {
    let result = [...archiveDisplayRows];
    if (user?.role !== 'admin' && trackingScope.allowedPickupWarehouseIds.length > 0) {
      result = result.filter((row) => {
        const matchesWarehouse = row.pickupWarehouseId && trackingScope.allowedPickupWarehouseIds.includes(String(row.pickupWarehouseId));
        const isNonWarehousePickup = !row.pickupWarehouseId;
        const isOwnShipment = row.createdBy && String(row.createdBy) === String(user?.id || '');
        return Boolean(matchesWarehouse) || (trackingScope.canViewNonWarehousePickups && isNonWarehousePickup) || (isNonWarehousePickup && isOwnShipment);
      });
    }
    return result;
  }, [archiveDisplayRows, trackingScope, user?.role, user?.id]);

  const tabFilteredRows = useMemo(() => {
    let result = operationTab === 'arsiv' ? [...scopedArchiveRows] : [...scopedRows];

    if (operationTab === 'arsiv') {
      // archive rows are pre-mapped from archive records + shipment rows
    } else {
      result = result.filter((row) => resolveTrackingOperationTabForRow(row, false) === operationTab);
    }
    return result;
  }, [scopedRows, scopedArchiveRows, operationTab]);
 
  const sortedRows = useMemo(() => {
    if (!sortColumn) return tabFilteredRows;
    return [...tabFilteredRows].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? -1 : 1;
      if (bVal == null) return sortDirection === 'asc' ? 1 : -1;
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortDirection === 'asc' ? aStr.localeCompare(bStr, 'tr-TR') : bStr.localeCompare(aStr, 'tr-TR');
    });
  }, [tabFilteredRows, sortColumn, sortDirection]);

  const exportTrackingExcel = () => {
    const exportColumns = activeColumns;
    const data = sortedRows.map((row) => exportColumns.reduce<Record<string, string | number>>((acc, column) => {
      acc[column.label] = getTrackingExcelValue(row, column.key);
      return acc;
    }, {}));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Tracking');
    XLSX.writeFile(wb, `tracking-${operationTab}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const paginatedRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [sortedRows, page, pageSize]
  );

  const archiveSearchedRows = useMemo(() => {
    if (!search.trim()) return sortedRows;
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return sortedRows.filter((row) => {
      const haystack = [
        row.sevkKodu,
        row.cekici,
        row.dorse,
        row.surucuAdi,
        row.surucuTelefon,
        row.yuklemeMusterisi,
        row.bosaltmaMusterisi,
        row.yuklemeIli,
        row.bosaltmaIli,
      ].join(' ').toLocaleLowerCase('tr-TR');
      return haystack.includes(q);
    });
  }, [sortedRows, search]);

  const archivePaginatedRows = useMemo(
    () => archiveSearchedRows.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [archiveSearchedRows, page, pageSize]
  );

  const hasVisibleDetailPanel = showDetailPanel && !!detailRow;

  useEffect(() => {
    setPage(1);
  }, [search, operationTab, filters, externalTransportFilter]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [operationTab]);

  useEffect(() => {
    if (tabFilteredRows.length === 0) {
      setDetailRow(null);
      return;
    }

    if (!detailRow) {
      setDetailRow(tabFilteredRows[0]);
      return;
    }

    const nextMatch = tabFilteredRows.find((row) => row.rowKey === detailRow.rowKey);
    if (!nextMatch) {
      setDetailRow(tabFilteredRows[0]);
      return;
    }

    if (nextMatch !== detailRow) {
      setDetailRow(nextMatch);
    }
  }, [tabFilteredRows, detailRow]);

  const submitAssignment = async () => {
    if (!assignRow) return;

    if (assignType === 'own-fleet' && !selectedVehicleId) {
      toast.error('Araç seçmelisiniz');
      return;
    }
    if (assignType === 'spot' && !selectedApplicationId) {
      toast.error('Spot sürücü seçmelisiniz');
      return;
    }

    try {
      setAssigning(true);
      const selectedSpotDriver = assignType === 'spot'
        ? spotOptions.find((item) => item.applicationId === selectedApplicationId)
        : null;

      const response: any = await supplyService.assignSupply(assignRow.id, {
        assignmentType: assignType,
        vehicleId: assignType === 'own-fleet' ? selectedVehicleId : undefined,
        applicationId: assignType === 'spot' ? selectedApplicationId : undefined,
        driverId: assignType === 'spot' ? selectedSpotDriver?.driverId || undefined : undefined,
        agreedPrice: assignType === 'spot' ? Number(agreedPrice || 0) : undefined,
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Atama yapılamadı');
      }

      const selectedFleetVehicle = assignType === 'own-fleet'
        ? fleetOptions.find((item) => item.vehicleId === selectedVehicleId)
        : null;
      const notificationPhone = assignType === 'spot'
        ? selectedSpotDriver?.phone
        : selectedFleetVehicle?.driverPhone;

      await taskNotificationService.notifyAssignment({
        phoneNumber: notificationPhone,
        shipmentNo: assignRow.sevkKodu,
        loadingCustomer: assignRow.yuklemeMusterisi,
        loadingCity: assignRow.yuklemeIli,
        deliveryCustomer: assignRow.bosaltmaMusterisi,
        deliveryCity: assignRow.bosaltmaIli,
        vehicleType: assignType === 'spot' ? selectedSpotDriver?.vehicleType : selectedFleetVehicle?.vehicleType,
        weightKg: assignRow.yukAgirlik,
        distanceKm: assignRow.seferKm,
        agreedPrice: assignType === 'spot' ? Number(agreedPrice || 0) : assignRow.navlun,
        loadingDate: assignRow.yuklemeTarihi,
      }).catch((error) => console.error('Assignment notification failed:', error));

      await closeOpenBidRequestsForShipment(assignRow.id);

      toast.success(response.message || 'Atama tamamlandı');
      setShowAssignModal(false);
      setAssignRow(null);
      await loadRows();
    } catch (error: any) {
      console.error('Assign failed:', error);
      toast.error(error?.message || 'Atama yapılamadı');
    } finally {
      setAssigning(false);
    }
  };

  const handleManualProcessStatusChange = async (row: OperationRow, nextStatus: ManualProcessStatus) => {
    try {
      setUpdatingRowStatusId(row.id);
      const response: any = await supplyService.updateProcessStatus(row.id, nextStatus);
      if (!response?.success) {
        throw new Error(response?.message || 'Durum güncellenemedi');
      }
      toast.success(response.message || 'Durum güncellendi');
      setRowContextMenu(null);
      await loadRows();
    } catch (error: any) {
      console.error('Manual process status update failed:', error);
      toast.error(error?.message || 'Durum güncellenemedi');
    } finally {
      setUpdatingRowStatusId(null);
    }
  };

  const handleManualAccountingStatusChange = async (row: OperationRow, nextStatus: ManualAccountingStatus) => {
    try {
      setUpdatingRowStatusId(row.id);
      const response: any = await accountingService.setAccountingStatus(row.id, nextStatus);
      if (!response?.success) {
        throw new Error(response?.message || 'Muhasebe aşaması güncellenemedi');
      }
      toast.success(response.message || 'Muhasebe aşaması güncellendi');
      setRowContextMenu(null);
      await loadRows();
    } catch (error: any) {
      console.error('Manual accounting status update failed:', error);
      toast.error(error?.message || 'Muhasebe aşaması güncellenemedi');
    } finally {
      setUpdatingRowStatusId(null);
    }
  };

  const fleetOptions = useMemo(() => {
    const searchValue = fleetSearch.trim().toLocaleLowerCase('tr-TR');
    const activeAssignments = rows.filter((row) => String(row.id) !== String(assignRow?.id || '') && ACTIVE_ASSIGNMENT_STATUSES.has(String(row.islemDurum || '').trim()));
    const enriched = vehicles
      .filter((vehicle: any) => {
        const ownerType = String(vehicle.owner_type || 'own').trim().toLocaleLowerCase('tr-TR');
        return ownerType === 'own' || ownerType === 'özmal' || ownerType === 'ozmal';
      })
      .map((vehicle: any) => {
        const driver = internalDrivers.find((item: any) => String(item.id) === String(vehicle.assigned_driver_id));
        const assignPickupLat = assignRow ? assignRow.pickupLatitude : null;
        const assignPickupLng = assignRow ? assignRow.pickupLongitude : null;
        const distanceToPickupKm = assignRow ? haversineKm(assignPickupLat, assignPickupLng, vehicle.latitude != null ? Number(vehicle.latitude) : null, vehicle.longitude != null ? Number(vehicle.longitude) : null) : null;
        const tractorPlate = vehicle.cekici_plaka || vehicle.plate_number || '-';
        const activeTaskRows = activeAssignments.filter((row) => String(row.cekici || '').trim().toLocaleUpperCase('tr-TR') === String(tractorPlate || '').trim().toLocaleUpperCase('tr-TR'));
        const hasActiveTask = activeTaskRows.length > 0;
        const vehicleStatus = String(vehicle.status || '').trim().toLocaleLowerCase('tr-TR');
        const driverUserStatus = String(driver?.user_status || 'active').trim().toLocaleLowerCase('tr-TR');
        const driverOperationalStatus = String(driver?.status || 'available').trim().toLocaleLowerCase('tr-TR');
        const isVehicleActive = vehicleStatus === '' || vehicleStatus === 'active' || vehicleStatus === 'aktif';
        const isDriverActive = (driverUserStatus === '' || driverUserStatus === 'active' || driverUserStatus === 'aktif') && driverOperationalStatus !== 'unavailable';
        const vehicleType = vehicle.linked_trailer_type || vehicle.vehicle_type || driver?.vehicle_type || '-';
        const isCompatible = matchesRequestedVehicleTypes(assignRow?.istenenKasaTipi, vehicleType);
        return {
          vehicleId: String(vehicle.id),
          driverName: vehicle.driver_name || driver?.full_name || '-',
          driverPhone: driver?.phone || vehicle.driver_phone || '',
          tractorPlate,
          trailerPlate: vehicle.linked_trailer_plate || vehicle.dorse_plaka || driver?.dorse_plaka || '-',
          vehicleType,
          currentAddress: vehicle.current_address || '-',
          remainingWorkTime: driver?.available_drive_period || '-',
          workDuration: driver?.work_duration || vehicle.daily_work_time || '-',
          currentWeekHours: driver?.current_week_hours || '-',
          twoWeekHours: driver?.two_week_hours || '-',
          latitude: vehicle.latitude,
          longitude: vehicle.longitude,
          distanceToPickupKm,
          vehicleStatus,
          driverUserStatus,
          driverOperationalStatus,
          isVehicleActive,
          isDriverActive,
          activeTaskCount: activeTaskRows.length,
          activeTaskCodes: activeTaskRows.map((row) => row.sevkKodu || row.planKodu || row.id).filter(Boolean),
          hasActiveTask,
          isCompatible,
          isAssignable: isCompatible && isVehicleActive && isDriverActive && !hasActiveTask,
        };
      });

    const sorted = [...enriched].sort((a, b) => {
      if (a.isAssignable !== b.isAssignable) return a.isAssignable ? -1 : 1;
      if (a.hasActiveTask !== b.hasActiveTask) return a.hasActiveTask ? 1 : -1;
      if (a.isVehicleActive !== b.isVehicleActive) return a.isVehicleActive ? -1 : 1;
      if (a.distanceToPickupKm == null && b.distanceToPickupKm == null) return 0;
      if (a.distanceToPickupKm == null) return 1;
      if (b.distanceToPickupKm == null) return -1;
      return a.distanceToPickupKm - b.distanceToPickupKm;
    });

    const options = sorted;

    if (!searchValue) return options;
    return options.filter((item) => [item.driverName, item.driverPhone, item.tractorPlate, item.trailerPlate, item.vehicleType, item.currentAddress, item.activeTaskCodes.join(' ')].join(' ').toLocaleLowerCase('tr-TR').includes(searchValue));
  }, [vehicles, internalDrivers, fleetSearch, assignRow, rows]);

  const spotOptions = useMemo(() => {
    const searchValue = fleetSearch.trim().toLocaleLowerCase('tr-TR');
    const corporateCarrierMap = new Map(corporateCarrierAccounts.map((item: any) => [String(item.id), item.full_name || item.username || `Firma #${item.id}`]));
    const uniqueApplications = applications.filter((app: any, index: number, array: any[]) => {
      return index === array.findIndex((candidate) => String(candidate.id) === String(app.id));
    });

    const applicationOptions = uniqueApplications.map((app: any) => {
      const lat = app.latest_latitude != null ? Number(app.latest_latitude) : null;
      const lng = app.latest_longitude != null ? Number(app.latest_longitude) : null;
      const assignPickupLat = assignRow ? assignRow.pickupLatitude : null;
      const assignPickupLng = assignRow ? assignRow.pickupLongitude : null;
      const distanceToPickupKm = assignRow ? haversineKm(assignPickupLat, assignPickupLng, lat, lng) : null;
      return {
        applicationId: String(app.id),
        driverId: app.driver_id != null ? String(app.driver_id) : '',
        userId: app.user_id != null ? String(app.user_id) : app.userId != null ? String(app.userId) : '',
        externalTransportAccountUserId: app.external_transport_account_user_id != null ? String(app.external_transport_account_user_id) : '',
        externalTransportAccountName: app.external_transport_account_user_id != null ? (corporateCarrierMap.get(String(app.external_transport_account_user_id)) || `Firma #${app.external_transport_account_user_id}`) : '',
        driverName: app.full_name || app.name || '-',
        phone: app.phone || '-',
        tractorPlate: app.cekici_plaka || '-',
        trailerPlate: app.dorse_plaka || '-',
        vehicleType: app.vehicle_type || '-',
        currentAddress: lat != null && lng != null ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : '-',
        distanceToPickupKm,
      };
    });

    const externalDriverOptions = externalDrivers.map((driver: any) => {
      const lat = driver.latest_latitude != null ? Number(driver.latest_latitude) : null;
      const lng = driver.latest_longitude != null ? Number(driver.latest_longitude) : null;
      const assignPickupLat = assignRow ? assignRow.pickupLatitude : null;
      const assignPickupLng = assignRow ? assignRow.pickupLongitude : null;
      const distanceToPickupKm = assignRow ? haversineKm(assignPickupLat, assignPickupLng, lat, lng) : null;
      return {
        applicationId: `driver-${driver.id}`,
        driverId: String(driver.id || ''),
        userId: driver.user_id != null ? String(driver.user_id) : driver.userId != null ? String(driver.userId) : '',
        externalTransportAccountUserId: driver.external_transport_account_user_id != null ? String(driver.external_transport_account_user_id) : '',
        externalTransportAccountName: driver.external_transport_account_user_id != null ? (corporateCarrierMap.get(String(driver.external_transport_account_user_id)) || `Firma #${driver.external_transport_account_user_id}`) : '',
        driverName: driver.full_name || driver.name || '-',
        phone: driver.phone || '-',
        tractorPlate: driver.cekici_plaka || '-',
        trailerPlate: driver.dorse_plaka || '-',
        vehicleType: driver.vehicle_type || '-',
        currentAddress: lat != null && lng != null ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : '-',
        distanceToPickupKm,
      };
    });

    const dedupedMap = new Map<string, any>();
    [...applicationOptions, ...externalDriverOptions].forEach((item) => {
      const phoneKey = normalizePhone(item.phone || '');
      const plateKey = normalizePlate(item.tractorPlate || '');
      const key = phoneKey || plateKey || item.applicationId;
      if (!dedupedMap.has(key)) dedupedMap.set(key, item);
    });

    const enriched = [...dedupedMap.values()];

    const sorted = [...enriched].sort((a, b) => {
      if (a.distanceToPickupKm == null && b.distanceToPickupKm == null) return 0;
      if (a.distanceToPickupKm == null) return 1;
      if (b.distanceToPickupKm == null) return -1;
      return a.distanceToPickupKm - b.distanceToPickupKm;
    });

    const options = sorted.map((item) => ({
      ...item,
      isCompatible: matchesRequestedVehicleTypes(assignRow?.istenenKasaTipi, item.vehicleType),
    }));

    const companyFilteredOptions = options.filter((item) => selectedSpotCompanyId === 'all'
      ? true
      : selectedSpotCompanyId === 'individual'
        ? !item.externalTransportAccountUserId
        : item.externalTransportAccountUserId === selectedSpotCompanyId);

    if (!searchValue) return companyFilteredOptions;
    return companyFilteredOptions.filter((item) => [item.driverName, item.phone, item.tractorPlate, item.trailerPlate, item.vehicleType, item.currentAddress, item.externalTransportAccountName].join(' ').toLocaleLowerCase('tr-TR').includes(searchValue));
  }, [applications, externalDrivers, fleetSearch, assignRow, corporateCarrierAccounts, selectedSpotCompanyId]);

  const detailDriverInsight = useMemo(() => {
    if (!detailRow) return null;

    const matchedVehicle = vehicles.find((vehicle: any) => {
      return normalizePlate(vehicle.cekici_plaka || vehicle.plate_number) === normalizePlate(detailRow.cekici);
    });

    const matchedDriver = matchedVehicle
      ? internalDrivers.find((item: any) => String(item.id) === String(matchedVehicle.assigned_driver_id))
      : internalDrivers.find((item: any) => {
          const samePhone = normalizePhone(item.phone) && normalizePhone(item.phone) === normalizePhone(detailRow.surucuTelefon);
          const sameName = String(item.full_name || item.name || '').trim() && String(item.full_name || item.name || '').trim() === String(detailRow.surucuAdi || '').trim();
          return samePhone || sameName;
        });

    const matchedSpotApplication = applications.find((item: any) => {
      const samePhone = normalizePhone(item.phone) && normalizePhone(item.phone) === normalizePhone(detailRow.surucuTelefon);
      const sameName = String(item.full_name || item.name || '').trim() && String(item.full_name || item.name || '').trim() === String(detailRow.surucuAdi || '').trim();
      const samePlate = normalizePlate(item.cekici_plaka) && normalizePlate(item.cekici_plaka) === normalizePlate(detailRow.cekici);
      return samePhone || sameName || samePlate;
    });

    const pickupLat = detailRow.pickupLatitude;
    const pickupLng = detailRow.pickupLongitude;
    const deliveryLat = detailRow.deliveryLatitude != null ? Number(detailRow.deliveryLatitude) : null;
    const deliveryLng = detailRow.deliveryLongitude != null ? Number(detailRow.deliveryLongitude) : null;
    const vehicleLat = matchedVehicle?.latitude != null
      ? Number(matchedVehicle.latitude)
      : matchedSpotApplication?.latest_latitude != null
        ? Number(matchedSpotApplication.latest_latitude)
        : null;
    const vehicleLng = matchedVehicle?.longitude != null
      ? Number(matchedVehicle.longitude)
      : matchedSpotApplication?.latest_longitude != null
        ? Number(matchedSpotApplication.latest_longitude)
        : null;
    const distanceToPickupKm = haversineKm(pickupLat, pickupLng, vehicleLat, vehicleLng);
    const distanceToDeliveryKm = haversineKm(deliveryLat, deliveryLng, vehicleLat, vehicleLng);
    const deliveryStage = isDeliveryStage(detailRow.islemDurum || operationTab);
    const targetLabel = deliveryStage ? 'Boşaltma Noktası' : 'Yükleme Noktası';
    const targetLat = deliveryStage ? deliveryLat : pickupLat;
    const targetLng = deliveryStage ? deliveryLng : pickupLng;

    return {
      currentAddress: matchedVehicle?.current_address || (matchedSpotApplication && vehicleLat != null && vehicleLng != null ? `${vehicleLat.toFixed(5)}, ${vehicleLng.toFixed(5)}` : '-'),
      currentCoords: vehicleLat != null && vehicleLng != null ? `${vehicleLat.toFixed(5)}, ${vehicleLng.toFixed(5)}` : '-',
      currentLat: vehicleLat,
      currentLng: vehicleLng,
      distanceToPickupKm,
      distanceToDeliveryKm,
      targetLabel,
      targetCoords: targetLat != null && targetLng != null ? `${targetLat.toFixed(5)}, ${targetLng.toFixed(5)}` : '-',
      targetDistanceKm: deliveryStage ? distanceToDeliveryKm : distanceToPickupKm,
      directionsUrl: buildGoogleMapsDirectionsUrl({
        originLat: vehicleLat,
        originLng: vehicleLng,
        destinationLat: targetLat,
        destinationLng: targetLng,
      }),
      remainingDrivePeriod: matchedDriver?.available_drive_period || '-',
      workDuration: matchedDriver?.work_duration || matchedVehicle?.daily_work_time || '-',
      currentWeekHours: matchedDriver?.current_week_hours || '-',
      twoWeekHours: matchedDriver?.two_week_hours || '-',
      dailyDriveTime: matchedVehicle?.daily_drive_time || '-',
      dailyWorkTime: matchedVehicle?.daily_work_time || '-',
      dailyIdleTime: matchedVehicle?.daily_idle_time || '-',
      locationUpdatedAt: matchedVehicle?.location_updated_at ? formatDateTime(matchedVehicle.location_updated_at) : (matchedSpotApplication?.latest_location_at ? formatDateTime(matchedSpotApplication.latest_location_at) : '-'),
    };
  }, [detailRow, vehicles, internalDrivers, applications, operationTab]);

  useEffect(() => {
    if (!detailDriverInsight?.currentLat || !detailDriverInsight?.currentLng) {
      setReverseGeocodedDriverAddress(null);
      return;
    }

    let cancelled = false;
    const reverseGeocode = async () => {
      const key = await import('../utils/googleMaps').then(m => m.getGoogleMapsApiKey());
      if (!key) return;
      try {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${detailDriverInsight.currentLat},${detailDriverInsight.currentLng}&key=${key}`);
        const data = await response.json();
        if (!cancelled) {
          setReverseGeocodedDriverAddress(data?.results?.[0]?.formatted_address || null);
        }
      } catch {
        if (!cancelled) {
          setReverseGeocodedDriverAddress(null);
        }
      }
    };

    void reverseGeocode();
    return () => {
      cancelled = true;
    };
  }, [detailDriverInsight?.currentLat, detailDriverInsight?.currentLng]);

  const handleLocationCheck = async (row: OperationRow) => {
    try {
      setLocationCheckingRowId(row.id);
      const target = operationTab.startsWith('bosaltma') || operationTab === 'tasima-yolunda' || operationTab === 'teslim-noktasina-ulasildi' ? 'delivery' : 'pickup';
      const response: any = await supplyService.checkLocationStatus(row.id, target as any);
      const data = response?.data || response;
      if (!response?.success && !data?.statusLabel) {
        throw new Error(response?.message || 'Konum kontrolü başarısız');
      }
      const message = `${data.statusLabel}${data.distanceKm != null ? ` • Mesafe: ${data.distanceKm} km` : ''}${data.currentAddress ? ` • Konum: ${data.currentAddress}` : ''}`;
      toast.success(message);
    } catch (error: any) {
      console.error('Location check failed:', error);
      toast.error(error?.message || 'Konum kontrolü yapılamadı');
    } finally {
      setLocationCheckingRowId(null);
    }
  };

  const getAccountingAdvanceAction = (accountingStatus: string, shipmentId: string | number) => {
    const map: Record<string, { call: () => Promise<any> }> = {
      'delivery-approval-pending': { call: () => accountingService.approveDelivery(shipmentId) },
      'invoice-waiting': { call: () => { throw new Error('Fatura yüklenmeden ilerlenemez'); } },
      'invoice-approval-pending': { call: () => accountingService.approveInvoice(shipmentId) },
      'payment-pre-approval': { call: () => accountingService.setAccountingStatus(shipmentId, 'payment-pending') },
      'payment-pending': { call: () => accountingService.setAccountingStatus(shipmentId, 'payment-completed') },
    };
    return map[accountingStatus] || null;
  };

  const handleMoveProcess = async (row: OperationRow, direction: 'prev' | 'next') => {
    try {
      const processStatus = String(row.islemDurum || '').trim();
      const accountingStatus = String(row.accountingStatus || '').trim();
      const isInAccountingFlow = ACCOUNTING_ONLY_FLOW.includes(accountingStatus as any);

      const isDeliveryCompletionTransition = direction === 'next'
        && processStatus === 'bosaltma-tamamlandi'
        && !isInAccountingFlow;

      let response: any;

      if (isDeliveryCompletionTransition) {
        response = await accountingService.completeDelivery(row.id);
      } else if (direction === 'next' && isInAccountingFlow) {
        const action = getAccountingAdvanceAction(accountingStatus, row.id);
        if (!action) throw new Error('İleri süreç adımı bulunamadı');
        response = await action.call();
      } else if (direction === 'next') {
        response = await supplyService.advanceProcessStatus(row.id);
      } else {
        response = await supplyService.revertProcessStatus(row.id);
      }

      if (!response?.success) {
        throw new Error(response?.message || 'Süreç güncellenemedi');
      }
      toast.success(response.message || 'Süreç güncellendi');
      await loadRows();
    } catch (error: any) {
      console.error('Process move failed:', error);
      toast.error(error?.message || 'Süreç güncellenemedi');
    }
  };

  const openArchiveViewModal = (archiveRecord: any, row: OperationRow | null = null) => {
    setArchiveModalMode('view');
    setActiveArchiveRecord(archiveRecord);
    setArchiveShipmentRow(row);
    setArchiveForm({
      folderName: archiveRecord?.folderName || '',
      evrakSiraNo: archiveRecord?.evrakSiraNo || (archiveRecord?.folderNumber ? String(archiveRecord.folderNumber) : ''),
      invoiceNumber: archiveRecord?.invoiceNumber || '',
      hasFuelPurchase: Boolean(archiveRecord?.hasFuelPurchase),
    });
    setArchiveFolderStartFrom('');
    setPendingArchiveFile(null);
    setArchivePreviewCode(archiveRecord?.archiveCode || '');
    setArchiveModalOpen(true);
  };

  const openArchiveEditModal = (row: OperationRow) => {
    setArchiveModalMode('edit');
    setActiveArchiveRecord({
      id: row.archiveId,
      archiveCode: row.archiveCode,
      folderName: row.folderName || '-',
      invoiceNumber: row.invoiceNumber,
      hasFuelPurchase: row.hasFuelPurchase === 'Var',
      folderNumber: row.folderNumber,
      archivedAt: row.archivedAt,
      archivedByName: row.archivedByName,
      shipmentNo: row.sevkKodu,
    });
    setArchiveShipmentRow(row);
    setArchiveForm({
      folderName: row.folderName === '-' ? '' : (row.folderName || ''),
      evrakSiraNo: row.evrakSiraNo === '-' ? '' : (row.evrakSiraNo || ''),
      invoiceNumber: row.invoiceNumber === '-' ? '' : (row.invoiceNumber || ''),
      hasFuelPurchase: row.hasFuelPurchase === 'Var',
    });
    setArchiveFolderStartFrom('');
    setPendingArchiveFile(null);
    setArchivePreviewCode(row.archiveCode || '');
    setArchiveModalOpen(true);
  };

  const handleOpenArchiveModal = async (row: OperationRow) => {
    const existingArchive = archiveRecordByShipmentId[String(row.id)];
    if (existingArchive) {
      openArchiveViewModal(existingArchive, row);
      return;
    }

    try {
      const existingResponse: any = await archiveService.checkArchive(String(row.id));
      if (existingResponse?.data) {
        setArchiveRecordByShipmentId((current) => ({ ...current, [String(row.id)]: existingResponse.data }));
        openArchiveViewModal(existingResponse.data, row);
        return;
      }
    } catch {}

    setArchiveModalMode('create');
    setActiveArchiveRecord(null);
    setArchiveShipmentRow(row);
    setArchiveForm({ folderName: '', evrakSiraNo: '', invoiceNumber: '', hasFuelPurchase: false });
    setArchiveFolderStartFrom('');
    setPendingArchiveFile(null);
    setArchiveModalOpen(true);
    setArchivePreviewCode('');
    archiveService.getNextCode()
      .then((response: any) => setArchivePreviewCode(response?.data?.archiveCode || ''))
      .catch(() => setArchivePreviewCode(''));
    archiveService.getNextFolderNumber()
      .then((response: any) => setArchiveForm((current) => ({ ...current, evrakSiraNo: String(response?.data?.folderNumber || '') })))
      .catch(() => null);
  };

  const handleModalArchivePdfUpload = async () => {
    const archiveId = activeArchiveRecord?.id;
    if (!archiveId) { toast.error('Arşiv kaydı bulunamadı'); return; }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploadingModalArchivePdf(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'archive');
        formData.append('entityId', archiveId);
        formData.append('documentType', 'archive-pdf');
        formData.append('name', file.name);
        await documentService.uploadDocument(formData);
        toast.success('PDF yüklendi');
        const res: any = await documentService.getDocuments({ entityType: 'archive', entityId: String(archiveId) });
        setModalArchivePdfDocs(Array.isArray(res?.data) ? res.data : []);
        if (archivePdfDocuments[archiveId]) {
          setArchivePdfDocuments((prev) => ({ ...prev, [archiveId]: Array.isArray(res?.data) ? res.data : [] }));
        }
      } catch {
        toast.error('PDF yüklenemedi');
      } finally {
        setUploadingModalArchivePdf(false);
      }
    };
    input.click();
  };

  const handleArchiveSubmit = async () => {
    if (!archiveShipmentRow) return;
    if (!archiveForm.folderName) { toast.error('Klasör no seçiniz'); return; }
    if (!archiveForm.evrakSiraNo) { toast.error('Evrak sıra no giriniz'); return; }
    setArchiveSaving(true);
    try {
      if (archiveModalMode === 'edit' && activeArchiveRecord?.id) {
        const response: any = await archiveService.update(String(activeArchiveRecord.id), {
          invoiceNumber: archiveForm.invoiceNumber,
          folderName: archiveForm.folderName,
          evrakSiraNo: archiveForm.evrakSiraNo,
          hasFuelPurchase: archiveForm.hasFuelPurchase,
          folderNumber: archiveForm.evrakSiraNo,
        });
        if (!response?.success) throw new Error(response?.message || 'Arşiv güncelleme başarısız');
        const updatedRecord = {
          ...(activeArchiveRecord || {}),
          id: String(activeArchiveRecord.id),
          shipmentId: String(archiveShipmentRow.id),
          archiveCode: archivePreviewCode,
          folderName: archiveForm.folderName || null,
          evrakSiraNo: archiveForm.evrakSiraNo || null,
          invoiceNumber: archiveForm.invoiceNumber || null,
          hasFuelPurchase: archiveForm.hasFuelPurchase,
          folderNumber: archiveForm.evrakSiraNo || null,
          shipmentNo: archiveShipmentRow.sevkKodu || '-',
          pickupCity: archiveShipmentRow.yuklemeIli || '-',
          deliveryCity: archiveShipmentRow.bosaltmaIli || '-',
          driverName: archiveShipmentRow.surucuAdi || '-',
        };
        toast.success(response?.message || 'Arşiv kaydı güncellendi');
        setArchiveRecordByShipmentId((current) => ({ ...current, [String(archiveShipmentRow.id)]: updatedRecord }));
        setActiveArchiveRecord(updatedRecord);
      } else {
        const response: any = await archiveService.create({
          shipmentId: archiveShipmentRow.id,
          folderName: archiveForm.folderName,
          evrakSiraNo: archiveForm.evrakSiraNo,
          invoiceNumber: archiveForm.invoiceNumber,
          hasFuelPurchase: archiveForm.hasFuelPurchase,
          folderNumber: archiveForm.evrakSiraNo,
        });
        if (!response?.success) {
          throw new Error(response?.message || 'Arşivleme başarısız');
        }
        const createdRecord = {
          id: response?.data?.id,
          shipmentId: String(archiveShipmentRow.id),
          archiveCode: response?.data?.archiveCode || archivePreviewCode,
          folderName: archiveForm.folderName || null,
          evrakSiraNo: archiveForm.evrakSiraNo || null,
          invoiceNumber: archiveForm.invoiceNumber || null,
          hasFuelPurchase: archiveForm.hasFuelPurchase,
          folderNumber: archiveForm.evrakSiraNo || null,
          archivedByName: user?.name || null,
          archivedAt: new Date().toISOString(),
          shipmentNo: archiveShipmentRow.sevkKodu || '-',
          pickupCity: archiveShipmentRow.yuklemeIli || '-',
          deliveryCity: archiveShipmentRow.bosaltmaIli || '-',
          driverName: archiveShipmentRow.surucuAdi || '-',
        };
        toast.success('Fiziksel arşive taşındı');
        setArchiveRecordByShipmentId((current) => ({ ...current, [String(archiveShipmentRow.id)]: createdRecord }));
        setActiveArchiveRecord(createdRecord);
        if (pendingArchiveFile && createdRecord.id) {
          try {
            const formData = new FormData();
            formData.append('file', pendingArchiveFile);
            formData.append('entityType', 'archive');
            formData.append('entityId', String(createdRecord.id));
            formData.append('documentType', 'archive-pdf');
            formData.append('name', pendingArchiveFile.name);
            await documentService.uploadDocument(formData);
            toast.success('PDF yüklendi');
          } catch {
            toast.error('PDF yüklenemedi, sonra tekrar deneyin');
          }
        }
      }
      setArchiveModalOpen(false);
      setArchiveShipmentRow(null);
      setArchivePreviewCode('');
      setPendingArchiveFile(null);
      await loadArchiveRows();
    } catch (error: any) {
      toast.error(error?.message || 'Arşivleme başarısız');
    } finally {
      setArchiveSaving(false);
    }
  };

  const handleUnassignRow = async (row: OperationRow) => {
    if (!window.confirm(`#${row.sevkKodu} ataması geri alınsın mı?`)) return;
    try {
      const response: any = await supplyService.unassignSupply(row.id);
      if (!response?.success) {
        throw new Error(response?.message || 'Atama geri alınamadı');
      }
      toast.success(response.message || 'Atama geri alındı');
      await loadRows();
    } catch (error: any) {
      console.error('Unassign failed:', error);
      toast.error(error?.message || 'Atama geri alınamadı');
    }
  };

  const createOfferCollectionForRow = async (row: OperationRow) => {
    if (activeBidRequestShipmentIds.includes(String(row.id))) return { success: false, message: 'Kayit zaten teklif toplama surecinde' };

    try {
      const bidRequestResponse: any = await bidService.createBidRequest({
        shipmentId: row.id,
        sevkKodu: row.sevkKodu,
        loadingPoint: row.yuklemeMusterisi,
        loadingCity: row.yuklemeIli,
        loadingAddress: row.yuklemeAdres,
        deliveryPoint: row.bosaltmaMusterisi,
        deliveryCity: row.bosaltmaIli,
        deliveryAddress: row.teslimAdres,
        cargoType: row.hizmetTuru,
        cargoWeight: row.yukAgirlik,
        cargoVolume: row.yukHacim,
        distanceKm: row.seferKm,
        vehicleType: row.istenenKasaTipi || row.lojistikTuru,
        targetPrice: row.navlun,
        plannedLoadingTime: row.yuklemeTarihi,
        bidDeadline: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        minimumBid: row.navlun || 0,
        shipmentContactPhone: row.shipmentContactPhone || '',
        notes: row.operasyonNot,
        createdBy: 1,
      });

      const bidRequestId = bidRequestResponse?.data?.id || bidRequestResponse?.id;
      if (bidRequestId) {
        await supplyService.updateStatus(row.id, 'spot-teklif-bekleniyor');
        await taskNotificationService.notifyOfferCollection({
          loadingCustomer: row.yuklemeMusterisi,
          loadingCity: row.yuklemeIli,
          deliveryCity: row.bosaltmaIli,
          tonnage: row.yukAgirlik,
        }).catch((error) => console.error('Offer collection notification failed:', error));
        setActiveBidRequestShipmentIds((current) => [...new Set([...current, String(row.id)])]);
      }

      return { success: true };
    } catch (error: any) {
      console.error('Create offer collection failed:', error);
      return { success: false, message: error?.message || 'Kayıt teklif toplamaya alınamadı' };
    }
  };

  const handleBulkMoveToOfferCollection = async () => {
    if (selectedRowIds.length === 0) {
      toast.error('Lütfen en az bir kayıt seçin');
      return;
    }

    const selectedRows = tabFilteredRows.filter((row) => selectedRowIds.includes(row.id));
    setOfferCollectionConfirmRows(selectedRows);
    return;

  };

  const confirmOfferCollection = async () => {
    const selectedRows = offerCollectionConfirmRows;
    if (selectedRows.length === 0) return;

    try {
      setOfferCollectionLoading(true);
      setOfferCollectionLoadingCode(selectedRows.length === 1 ? (selectedRows[0].sevkKodu || '') : `${selectedRows.length} kayıt`);
      let successCount = 0;
      let firstErrorMessage = '';
      for (const row of selectedRows) {
        const result = await createOfferCollectionForRow(row);
        if (result?.success) successCount += 1;
        else if (!firstErrorMessage) firstErrorMessage = result?.message || '';
      }

      if (successCount > 0) {
        toast.success(`${successCount} kayıt teklif toplamaya alındı`);
        setSelectedRowIds([]);
        setOfferCollectionConfirmRows([]);
        await loadRows();
      } else {
        toast.error(firstErrorMessage || 'Kayıtlar teklif toplamaya alınamadı');
      }
    } finally {
      setOfferCollectionLoading(false);
      setOfferCollectionLoadingCode('');
    }
  };

  const handleSingleMoveToOfferCollection = async (row: OperationRow) => {
    setOfferCollectionConfirmRows([row]);
  };

  const executeSingleOfferCollection = async (row: OperationRow) => {
    try {
      setOfferCollectionLoading(true);
      setOfferCollectionLoadingCode(row.sevkKodu || '');
      const result = await createOfferCollectionForRow(row);
      if (!result?.success) {
        toast.error(result?.message || 'Kayıt teklif toplamaya alınamadı');
        return;
      }

      toast.success(result?.message || 'Kayıt teklif toplamaya alındı');
      setOfferCollectionConfirmRows([]);
      await loadRows();
    } finally {
      setOfferCollectionLoading(false);
      setOfferCollectionLoadingCode('');
    }
  };

  const handleRemoveFromOfferCollection = async (row: OperationRow) => {
    if (!window.confirm(`#${row.sevkKodu} teklif sürecinden çıkarılsın mı?`)) return;

    try {
      await closeOpenBidRequestsForShipment(row.id);
      await supplyService.updateStatus(row.id, 'tedarik-bekleniyor');
      toast.success('Kayıt teklif sürecinden çıkarıldı');
      await loadRows();
    } catch (error) {
      console.error('Remove from offer collection failed:', error);
      toast.error('Kayıt teklif sürecinden çıkarılamadı');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRowIds.length === 0) {
      toast.error('Lütfen en az bir kayıt seçin');
      return;
    }

    const selectedRows = tabFilteredRows.filter((row) => selectedRowIds.includes(row.id));
    if (!window.confirm(`${selectedRows.length} kayıt silinsin mi?`)) return;

    try {
      for (const row of selectedRows) {
        await supplyService.delete(row.id);
      }
      toast.success(`${selectedRows.length} kayıt silindi`);
      setSelectedRowIds([]);
      await loadRows();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Toplu silme sırasında hata oluştu');
    }
  };

  const closeOpenBidRequestsForShipment = async (shipmentId: string) => {
    try {
      const response: any = await bidService.getBidRequests({ status: 'open', shipmentId });
      const items = Array.isArray(response?.data) ? response.data : [];
      await Promise.all(items.map((item: any) => bidService.closeBidRequest(item.id)));
      setActiveBidRequestShipmentIds((current) => current.filter((id) => id !== String(shipmentId)));
    } catch (error) {
      console.error('Close bid requests failed:', error);
    }
  };

  const openAssignModalForRow = async (row: OperationRow) => {
    setAssignRow(row);
    setShowAssignModal(true);
    setAssignType('own-fleet');
    setSelectedVehicleId('');
    setSelectedApplicationId('');
    setAgreedPrice('');
    setFleetSearch('');
    await loadAssignmentSources();
  };

  const getSuggestedSpotAgreedPrice = (row?: OperationRow | null) => {
    const suggested = Number(row?.bazFiyat || 0) > 0 ? Number(row?.bazFiyat || 0) : Number(row?.navlun || 0);
    return suggested > 0 ? String(suggested) : '';
  };

  const resetNewSpotDriverForm = () => {
    setNewSpotDriverForm({
      full_name: '',
      phone: '',
      vehicle_type: '',
      cekici_plaka: '',
      dorse_plaka: '',
      city: '',
      district: '',
    });
  };

  const submitNewSpotDriver = async () => {
    if (!String(newSpotDriverForm.full_name || '').trim() || !String(newSpotDriverForm.phone || '').trim()) {
      toast.error('Ad soyad ve telefon zorunlu');
      return;
    }
    if (!isValidSpotPlate(String(newSpotDriverForm.cekici_plaka || ''))) {
      toast.error('Cekici plaka formati gecersiz');
      return;
    }
    if (String(newSpotDriverForm.dorse_plaka || '').trim() && !isValidSpotPlate(String(newSpotDriverForm.dorse_plaka || ''))) {
      toast.error('Dorse plaka formati gecersiz');
      return;
    }
    try {
      setCreatingSpotDriver(true);
      const response: any = await driverService.createApplication({
        ...newSpotDriverForm,
        full_name: normalizeUpper(String(newSpotDriverForm.full_name || '').trim()),
        phone: normalizePhoneInput(String(newSpotDriverForm.phone || '').trim()),
        vehicle_type: normalizeUpper(String(newSpotDriverForm.vehicle_type || '').trim()) || null,
        cekici_plaka: normalizePlate(String(newSpotDriverForm.cekici_plaka || '').trim()) || null,
        dorse_plaka: normalizePlate(String(newSpotDriverForm.dorse_plaka || '').trim()) || null,
        city: normalizeUpper(String(newSpotDriverForm.city || '').trim()) || null,
        district: normalizeUpper(String(newSpotDriverForm.district || '').trim()) || null,
      });
      await loadAssignmentSources();
      const createdId = String(response?.data?.id || '');
      if (createdId) setSelectedApplicationId(createdId);
      setShowCreateSpotDriverModal(false);
      resetNewSpotDriverForm();
      toast.success(response?.message || 'Spot sürücü eklendi');
    } catch (error: any) {
      toast.error(error?.message || 'Spot sürücü eklenemedi');
    } finally {
      setCreatingSpotDriver(false);
    }
  };

  useEffect(() => {
    if (!showAssignModal || assignType !== 'spot' || !assignRow) return;
    if (String(agreedPrice || '').trim()) return;
    setAgreedPrice(getSuggestedSpotAgreedPrice(assignRow));
  }, [showAssignModal, assignType, assignRow, agreedPrice]);

  const getProcessMoveState = (row?: OperationRow | null) => {
    const processStatus = String(row?.islemDurum || '').trim();
    const accountingStatus = String(row?.accountingStatus || '').trim();
    const isInAccountingFlow = ACCOUNTING_ONLY_FLOW.includes(accountingStatus as any);
    const canMoveToDeliveryApproval = processStatus === 'bosaltma-tamamlandi'
      && !isInAccountingFlow;

    const currentIndex = isInAccountingFlow
      ? PROCESS_FLOW_ORDER.indexOf(accountingStatus as typeof PROCESS_FLOW_ORDER[number])
      : PROCESS_FLOW_ORDER.indexOf(processStatus as typeof PROCESS_FLOW_ORDER[number]);

    const isLastStep = currentIndex >= PROCESS_FLOW_ORDER.length - 1;
    const isFirstStep = currentIndex <= 0;

    return {
      canAdvance: canMoveToDeliveryApproval || (currentIndex !== -1 && !isLastStep),
      canRevert: currentIndex !== -1 && !isFirstStep && (isSuperAdmin || processStatus === 'bosaltma-tamamlandi' || isInAccountingFlow) || operationTab === 'atandi-islem-bekliyor',
      canMoveToDeliveryApproval,
    };
  };

  const handleDeleteRow = async (row: OperationRow) => {
    if (!window.confirm(`#${row.sevkKodu} kaydı silinsin mi?`)) return;
    try {
      await supplyService.delete(row.id);
      toast.success('Kayıt silindi');
      await loadRows();
    } catch {
      toast.error('Kayıt silinemedi');
    }
  };

  const handlePickupShiftChange = async (row: OperationRow, nextShiftId: string) => {
    try {
      setUpdatingShiftRowId(row.id);
      await supplyService.update(row.id, { pickupShiftId: nextShiftId || null });
      setRows((current) => current.map((item) => item.id === row.id ? {
        ...item,
        pickupShiftId: nextShiftId || null,
        yuklenecekVardiya: nextShiftId || '-',
      } : item));
      if (detailRow?.id === row.id) {
        setDetailRow((current) => current ? {
          ...current,
          pickupShiftId: nextShiftId || null,
          yuklenecekVardiya: nextShiftId || '-',
        } : current);
      }
      toast.success('Yüklenecek vardiya güncellendi');
    } catch (error) {
      console.error('Pickup shift update failed:', error);
      toast.error('Yüklenecek vardiya güncellenemedi');
    } finally {
      setUpdatingShiftRowId(null);
    }
  };

  const handleSendDriverMessage = async (row: OperationRow) => {
    const message = window.prompt(`Sürücüye gönderilecek SMS mesajını girin (#${row.sevkKodu})`);
    if (!message?.trim()) return;
    try {
      const response: any = await shipmentService.sendDriverMessage(row.id, message.trim());
      if (!response?.success) {
        throw new Error(response?.message || 'Mesaj gönderilemedi');
      }
      setRows((current) => current.map((item) => item.id === row.id ? { ...item, sonNot: message.trim(), sonNotYazan: user?.name || user?.username || '-' } : item));
      if (detailRow?.id === row.id) {
        setDetailRow((current) => current ? { ...current, sonNot: message.trim(), sonNotYazan: user?.name || user?.username || '-' } : current);
      }
      toast.success(response.message || 'Sürücüye mesaj gönderildi');
    } catch (error: any) {
      console.error('Send driver message failed:', error);
      toast.error(error?.message || 'Sürücüye mesaj gönderilemedi');
    }
  };

  const handleAddShipmentNote = async (row: OperationRow) => {
    const note = window.prompt(`Sevkiyat notunu girin (#${row.sevkKodu})`, row.sonNot && row.sonNot !== '-' ? row.sonNot : '');
    if (!note?.trim()) return;
    try {
      const response: any = await shipmentService.addShipmentNote(row.id, note.trim());
      if (!response?.success) {
        throw new Error(response?.message || 'Not kaydedilemedi');
      }
      const updatedNote = response?.data?.last_note || note.trim();
      const updatedAuthor = response?.data?.last_note_author_name || user?.name || user?.username || '-';
      setRows((current) => current.map((item) => item.id === row.id ? { ...item, sonNot: updatedNote, sonNotYazan: updatedAuthor } : item));
      if (detailRow?.id === row.id) {
        setDetailRow((current) => current ? { ...current, sonNot: updatedNote, sonNotYazan: updatedAuthor } : current);
      }
      toast.success(response.message || 'Sevkiyat notu kaydedildi');
    } catch (error: any) {
      console.error('Add shipment note failed:', error);
      toast.error(error?.message || 'Sevkiyat notu kaydedilemedi');
    }
  };

  const saveView = () => {
    try {
      setSavingView(true);
      localStorage.setItem(userBasicViewKey, JSON.stringify({
        visibleColumns,
        pageSize,
        sortColumn,
        sortDirection,
        externalTransportFilter,
        filters,
      }));
      toast.success('Görünüm kaydedildi');
    } catch {
      toast.error('Görünüm kaydedilemedi');
    } finally {
      setSavingView(false);
    }
  };

  const resetView = () => {
    localStorage.removeItem(userBasicViewKey);
    setVisibleColumns(withRequiredTrackingColumns(DEFAULT_TRACKING_COLUMNS));
    setPageSize(25);
    setSortColumn(null);
    setSortDirection('asc');
    setSearch('');
    setFilters([]);
    setExternalTransportFilter('all');
    setShowTableFilters(false);
    setShowColumnPicker(false);
    setOperationTab('atama-bekliyor');
    setPage(1);
    setActiveViewTabId(TRACKING_MAIN_TAB_ID);
  };

  const applySavedViewTab = (tab: TrackingSavedViewTab) => {
    setOperationTab(tab.operationTab);
    setSearch(tab.search);
    setExternalTransportFilter(tab.externalTransportFilter);
    setFilters(tab.filters || []);
    setSortColumn(tab.sortColumn);
    setSortDirection(tab.sortDirection);
    setPageSize(Number(tab.pageSize || 25));
    setPage(1);
    setActiveViewTabId(tab.id);
  };

  const saveCurrentFiltersAsTab = () => {
    const name = String(newViewTabName || '').trim();
    if (!name) {
      toast.error('Tab adı girin');
      return;
    }
    const nextTab: TrackingSavedViewTab = {
      id: makeTrackingViewId(),
      name,
      visibility: newViewTabVisibility,
      operationTab,
      search,
      externalTransportFilter,
      filters,
      sortColumn,
      sortDirection,
      pageSize,
    };
    const nextTabs = [...savedViewTabs, nextTab];
    setSavedViewTabs(nextTabs);
    const targetKey = newViewTabVisibility === 'public' ? sharedSavedTabsKey : userSavedTabsKey;
    const existingRaw = localStorage.getItem(targetKey);
    const existing = existingRaw ? JSON.parse(existingRaw) : [];
    localStorage.setItem(targetKey, JSON.stringify([...(Array.isArray(existing) ? existing : []), nextTab]));
    setNewViewTabName('');
    setNewViewTabVisibility('private');
    setActiveViewTabId(nextTab.id);
    toast.success('Görünüm tabı kaydedildi');
  };

  const deleteActiveSavedTab = () => {
    if (activeViewTabId === TRACKING_MAIN_TAB_ID) return;
    const activeTab = savedViewTabs.find((tab) => tab.id === activeViewTabId);
    const nextTabs = savedViewTabs.filter((tab) => tab.id !== activeViewTabId);
    setSavedViewTabs(nextTabs);
    if (activeTab) {
      const targetKey = activeTab.visibility === 'public' ? sharedSavedTabsKey : userSavedTabsKey;
      const existingRaw = localStorage.getItem(targetKey);
      const existing = existingRaw ? JSON.parse(existingRaw) : [];
      localStorage.setItem(targetKey, JSON.stringify((Array.isArray(existing) ? existing : []).filter((tab: any) => String(tab.id) !== activeViewTabId)));
    }
    setActiveViewTabId(TRACKING_MAIN_TAB_ID);
    toast.success('Görünüm tabı silindi');
  };

  const addFilterRule = () => {
    const fallbackColumn = String(allColumns[0]?.key || 'sevkKodu') as keyof OperationRow;
    setFilters((current) => ([
      ...current,
      {
        id: makeTrackingFilterId(),
        join: current.length === 0 ? 'and' : 'and',
        column: fallbackColumn,
        operator: 'contains',
        value: '',
      },
    ]));
    setActiveViewTabId(TRACKING_MAIN_TAB_ID);
  };

  const updateFilterRule = (id: string, patch: Partial<TrackingFilterRule>) => {
    setFilters((current) => current.map((rule, index) => {
      if (rule.id !== id) return rule;
      const next = {
        ...rule,
        ...patch,
        join: index === 0 ? 'and' : (patch.join || rule.join),
        value: patch.operator === 'empty' || patch.operator === 'notEmpty' ? '' : (patch.value ?? rule.value),
      };
      if (patch.column && TRACKING_DATE_FILTER_COLUMNS.has(patch.column)) {
        next.operator = 'dateEquals';
        next.value = '';
      }
      return next;
    }));
    setActiveViewTabId(TRACKING_MAIN_TAB_ID);
  };

  const removeFilterRule = (id: string) => {
    setFilters((current) => current.filter((rule) => rule.id !== id));
    setActiveViewTabId(TRACKING_MAIN_TAB_ID);
  };

  const activeColumns = useMemo(
    () => {
      const scopedDefaults = operationTab === 'all'
        ? visibleColumns
        : (PROCESS_PAGE_COLUMNS[operationTab] || DEFAULT_TRACKING_COLUMNS);
      const scopedColumns = operationTab === 'all'
        ? visibleColumns
        : scopedDefaults.filter((columnKey) => visibleColumns.includes(String(columnKey)));
      const effectiveScopedColumns = scopedColumns.length > 0 ? scopedColumns : scopedDefaults;
      const base = allColumns.filter((column) => effectiveScopedColumns.includes(String(column.key)));
      return [...base, {
        key: 'operasyonNot' as keyof OperationRow,
        label: '',
        width: '80px',
        render: (row: OperationRow) => (
          <button
            type="button"
            disabled={loadingEditShipmentId === row.id}
            onClick={(e) => { e.stopPropagation(); handleEditRow(row); }}
            className="rounded-lg border border-slate-700/40 px-3 py-1 text-xs text-slate-300 hover:bg-orange-500 hover:text-white transition-colors disabled:opacity-60"
          >
            {loadingEditShipmentId === row.id ? 'Yukleniyor...' : 'Düzenle'}
          </button>
        ),
      }];
    },
    [allColumns, visibleColumns, operationTab, loadingEditShipmentId]
  );

  const getTrackingColumnWidth = (width?: string) => {
    const baseWidth = Number.parseInt(String(width || '').replace(/[^\d.]/g, ''), 10);
    if (!Number.isFinite(baseWidth) || baseWidth <= 0) return width;
    const scale = hasVisibleDetailPanel ? 0.84 : 0.92;
    return `${Math.max(72, Math.round(baseWidth * scale))}px`;
  };

  const currentPageIndex = PROCESS_PAGE_ORDER.indexOf(operationTab);

  const goToPrevProcessPage = () => {
    if (currentPageIndex <= 0) return;
    setOperationTab(PROCESS_PAGE_ORDER[currentPageIndex - 1]);
    setActiveViewTabId(TRACKING_MAIN_TAB_ID);
  };

  const goToNextProcessPage = () => {
    if (currentPageIndex >= PROCESS_PAGE_ORDER.length - 1) return;
    setOperationTab(PROCESS_PAGE_ORDER[currentPageIndex + 1]);
    setActiveViewTabId(TRACKING_MAIN_TAB_ID);
  };

  const processStats = useMemo(() => ({
    total: scopedRows.length,
    atamaBekliyor: scopedRows.filter((row) => isAtamaBekliyorRow(row)).length,
    atandiIslemBekliyor: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'atandi-islem-bekliyor').length,
    yuklemeBekliyor: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'yukleme-bekliyor').length,
    yuklemeDevam: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'yukleme-devam').length,
    yuklemeTamamlandi: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'yukleme-tamamlandi').length,
    yolda: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'tasima-yolunda').length,
    teslimNoktasinaUlasildi: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'teslim-noktasina-ulasildi').length,
    bosaltmaBekliyor: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'bosaltma-bekliyor').length,
    bosaltmaDevam: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'bosaltma-devam').length,
    tamamlanan: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'bosaltma-tamamlandi').length,
    faturaBekleyen: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'fatura-bekleyen').length,
    odemeBekleyen: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'odeme-bekleyen').length,
    odemeGerceklesen: scopedRows.filter((row) => resolveTrackingOperationTabForRow(row, false) === 'odeme-gerceklesen').length,
    arsiv: scopedArchiveRows.length,
  }), [scopedRows, scopedArchiveRows]);

  return (
    <div className="space-y-4 tracking-page">
      {user?.role !== 'admin' && (trackingScope.allowedPickupWarehouseIds.length > 0 || trackingScope.canViewNonWarehousePickups) && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-slate-900">
          <div className="font-semibold">Kullanıcı Yetki Filtresi Aktif</div>
          <div className="mt-1 text-xs text-slate-900">Bu kullanıcı yalnızca yetkili olduğu yükleme noktalarına ait siparişleri görebilir.</div>
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-slate-700/30 bg-[#151515] shadow-sm">
        <div className="bg-[#1a1a1a] px-4 py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-[13px] font-semibold text-slate-100">Akış Yönetimi</div>
              <div className="mt-1 text-[11px] text-slate-400">Süreç adımları arasında soldan sağa ilerleyin.</div>
            </div>
            <div className="rounded-full bg-[#111111] px-3 py-1 text-xs font-semibold text-slate-300 ring-1 ring-slate-700/40">
              {currentPageIndex + 1} / {PROCESS_PAGE_ORDER.length}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={goToPrevProcessPage}
              disabled={currentPageIndex <= 0}
              className="rounded-xl border border-slate-700/40 bg-[#111111] p-2.5 text-slate-300 hover:bg-white/5 disabled:opacity-40 shadow-sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 overflow-x-auto">
              <div className="grid min-w-[980px] grid-cols-7 gap-2 pb-1">
            {[
              ['atama-bekliyor', 'Atama Bekliyor'],
              ['atandi-islem-bekliyor', 'Atandı İşlem Bekliyor'],
              ['yukleme-bekliyor', 'Yükleme Bekliyor'],
              ['yukleme-devam', 'Yükleme Devam'],
              ['yukleme-tamamlandi', 'Yükleme Tamam'],
              ['tasima-yolunda', 'Teslim Yolunda'],
              ['teslim-noktasina-ulasildi', 'Teslim Noktasına Ulaşıldı'],
              ['bosaltma-bekliyor', 'Boşaltma Bekliyor'],
              ['bosaltma-devam', 'Boşaltma Devam'],
              ['bosaltma-tamamlandi', 'Boşaltma Tamam'],
              ['fatura-bekleyen', 'Fatura Bekleyen'],
              ['odeme-bekleyen', 'Ödeme Bekleyen'],
              ['odeme-gerceklesen', 'Ödemesi Gerçekleşen'],
              ['arsiv', 'Filo Arşivi'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                  onClick={() => { setOperationTab(key as typeof operationTab); setActiveViewTabId(TRACKING_MAIN_TAB_ID); }}
                className={`tracking-flow-step group relative min-w-[138px] rounded-xl border px-3 py-2.5 text-left transition-all ${operationTab === key ? 'tracking-flow-step-active border-amber-700/50 bg-amber-950/40 text-amber-100 shadow-sm' : 'border-slate-700/40 bg-[#111111] text-slate-200 shadow-sm hover:border-orange-500/30 hover:bg-white/5'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className={`tracking-flow-step-eyebrow text-[10px] font-semibold uppercase tracking-[0.08em] ${operationTab === key ? 'text-amber-300' : 'text-slate-500'}`}>
                      Adım {PROCESS_PAGE_ORDER.indexOf(key as typeof operationTab) + 1}
                    </div>
                    <div className="mt-1 text-[12px] font-semibold leading-tight">{label}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className={`tracking-flow-step-index flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold ${operationTab === key ? 'bg-amber-500/20 text-amber-200' : 'bg-slate-800 text-slate-300'}`}>
                      {PROCESS_PAGE_ORDER.indexOf(key as typeof operationTab) + 1}
                    </div>
                    {((processStats as any)[PROCESS_BADGE_KEYS[key]] ?? 0) > 0 && (
                      <span className={`tracking-flow-step-badge inline-flex min-w-6 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${operationTab === key ? 'bg-amber-100 text-amber-950' : 'bg-slate-800 text-slate-300'}`}>
                        {(processStats as any)[PROCESS_BADGE_KEYS[key]]}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`mt-2 h-1 rounded-full ${operationTab === key ? 'bg-amber-500/20' : 'bg-slate-800'}`}>
                  <div className={`h-1 rounded-full ${operationTab === key ? 'w-full bg-amber-400' : 'w-0 bg-orange-500/50 transition-all group-hover:w-1/2'}`} />
                </div>
              </button>
            ))}
              </div>
            </div>
            <button
              type="button"
              onClick={goToNextProcessPage}
              disabled={currentPageIndex >= PROCESS_PAGE_ORDER.length - 1}
              className="rounded-xl border border-slate-700/40 bg-[#111111] p-2.5 text-slate-300 hover:bg-white/5 disabled:opacity-40 shadow-sm"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-4 ${hasVisibleDetailPanel ? 'xl:grid-cols-[minmax(0,1fr)_380px]' : ''}`}>
        <div className="min-w-0 rounded-xl border border-slate-700/30 bg-[#151515] shadow-sm overflow-hidden">
          <div className="border-b border-slate-700/30 bg-[#1a1a1a] px-4 py-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-semibold text-slate-100">{operationTab === 'arsiv' ? 'Filo Arşivi' : 'Siparişler'}</h2>
                  {operationTab === 'arsiv' ? (
                    <div className="rounded-full bg-[#111111] px-3 py-1 text-xs font-semibold text-slate-300 ring-1 ring-slate-700/40">
                      {tabFilteredRows.length} kayıt
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  {operationTab === 'arsiv' ? (
                    <button type="button" onClick={() => void loadRows()} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-2 font-semibold text-slate-200 hover:bg-white/5 disabled:opacity-50">
                      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                      Yenile
                    </button>
                  ) : null}
                  <button type="button" onClick={saveView} disabled={savingView || !viewLoaded} className="inline-flex items-center gap-2 rounded-xl border border-blue-700/40 bg-blue-950/40 px-4 py-2 font-semibold text-blue-200 hover:bg-blue-950/60 disabled:opacity-50">
                    <Save className="h-4 w-4" />
                    Görünümü Kaydet
                  </button>
                  <button type="button" onClick={resetView} className="inline-flex items-center gap-2 rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-2 font-semibold text-slate-200 hover:bg-white/5">
                    <RotateCcw className="h-4 w-4" />
                    Varsayılan
                  </button>
                  {operationTab === 'arsiv' ? (
                    <button
                      type="button"
                      onClick={() => setShowColumnPicker((prev) => !prev)}
                      className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 font-semibold ${showColumnPicker ? 'border-orange-700/40 bg-orange-950/40 text-orange-200' : 'border-slate-700/40 bg-[#111111] text-slate-200 hover:bg-white/5'}`}
                    >
                      {showColumnPicker ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      Sütunlar
                    </button>
                  ) : null}
                  {operationTab === 'atama-bekliyor' ? (
                    <>
                      <button type="button" onClick={() => void handleBulkMoveToOfferCollection()} disabled={selectedRowIds.length === 0} className="rounded-xl bg-violet-600 px-4 py-2 font-semibold text-white shadow-sm hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50">
                        Teklif Topla ({selectedRowIds.length})
                      </button>
                      {canDeleteSupply ? (
                        <button type="button" onClick={() => void handleBulkDelete()} disabled={selectedRowIds.length === 0} className="rounded-xl bg-rose-600 px-4 py-2 font-semibold text-white shadow-sm hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50">
                          Toplu Sil ({selectedRowIds.length})
                        </button>
                      ) : null}
                    </>
                  ) : null}
                  <button type="button" onClick={() => { setEditingWizardShipment(null); setWizardOpen(true); }} className="rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-white shadow-sm hover:bg-emerald-600">
                    Sevk Siparişi Aç
                  </button>
                  <button type="button" onClick={() => void handleRecalculateNearestHubs()} disabled={recalculatingNearestHubs} className="rounded-xl bg-cyan-600 px-4 py-2 font-semibold text-white shadow-sm hover:bg-cyan-700 disabled:opacity-50">
                    {recalculatingNearestHubs ? 'Hub Hesaplaniyor...' : 'Eksik Hub Hesapla'}
                  </button>
                  <button type="button" onClick={exportTrackingExcel} className="inline-flex items-center gap-2 rounded-xl bg-slate-700 px-4 py-2 font-semibold text-white shadow-sm hover:bg-slate-600">
                    <FileSpreadsheet className="w-4 h-4" />
                    Excele Aktar
                  </button>
                  <button type="button" onClick={() => setBulkImportOpen(true)} title="Açılan ekranda örnek sipariş dosyasını indirebilirsiniz" className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 font-semibold text-white shadow-sm hover:bg-violet-700">
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel'den Toplu Sipariş
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveViewTabId(TRACKING_MAIN_TAB_ID)}
                  className={`rounded-xl border px-3 py-2 text-xs font-semibold ${activeViewTabId === TRACKING_MAIN_TAB_ID ? 'border-orange-700/40 bg-orange-950/40 text-orange-200' : 'border-slate-700/40 bg-[#111111] text-slate-300 hover:bg-white/5'}`}
                >
                  Ana Tablo
                </button>
                {savedViewTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => applySavedViewTab(tab)}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold ${activeViewTabId === tab.id ? 'border-blue-700/40 bg-blue-950/40 text-blue-200' : 'border-slate-700/40 bg-[#111111] text-slate-300 hover:bg-white/5'}`}
                  >
                    {tab.name}
                    {tab.visibility === 'public' ? ' (Public)' : ''}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {operationTab === 'arsiv' ? (
                  <label className="relative min-w-[280px] max-w-md flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setActiveViewTabId(TRACKING_MAIN_TAB_ID); }}
                      placeholder="Sipariş no, sürücü veya rota ara"
                      className="h-10 w-full rounded-xl border border-slate-700/40 bg-[#111111] pl-10 pr-4 text-[13px] text-slate-100 outline-none focus:border-orange-400"
                    />
                  </label>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={newViewTabName}
                    onChange={(e) => setNewViewTabName(e.target.value)}
                    placeholder="Görünüm tab adı"
                    className="h-10 rounded-xl border border-slate-700/40 bg-[#111111] px-3 text-[13px] text-slate-100 outline-none focus:border-orange-400"
                  />
                  <select value={newViewTabVisibility} onChange={(e) => setNewViewTabVisibility(e.target.value === 'public' ? 'public' : 'private')} className="h-10 rounded-xl border border-slate-700/40 bg-[#111111] px-3 text-[13px] text-slate-100 outline-none focus:border-orange-400">
                    <option value="private">Private</option>
                    <option value="public">Public</option>
                  </select>
                  <button type="button" onClick={saveCurrentFiltersAsTab} className="inline-flex items-center gap-2 rounded-xl border border-blue-700/40 bg-blue-950/40 px-3 py-2 text-[11px] font-medium text-blue-200 hover:bg-blue-950/60">
                    <Filter className="h-3.5 w-3.5" />
                    Hızlı Arama Kaydet
                  </button>
                  {activeViewTabId !== TRACKING_MAIN_TAB_ID ? (
                    <button type="button" onClick={deleteActiveSavedTab} className="rounded-lg border border-rose-700/40 bg-rose-950/40 px-3 py-2 text-[11px] font-medium text-rose-200 hover:bg-rose-950/60">
                      Tabı Sil
                    </button>
                  ) : null}
                  {operationTab === 'arsiv' ? (
                    <select
                      value={externalTransportFilter}
                      onChange={(e) => { setExternalTransportFilter(e.target.value as 'all' | 'evet' | 'hayir'); setActiveViewTabId(TRACKING_MAIN_TAB_ID); }}
                      className="h-10 rounded-xl border border-slate-700/40 bg-[#111111] px-3 text-[13px] text-slate-100 outline-none focus:border-orange-400"
                    >
                      <option value="all">Dış Nakliye: Tümü</option>
                      <option value="evet">Dış Nakliye: Evet</option>
                      <option value="hayir">Dış Nakliye: Hayır</option>
                    </select>
                  ) : null}
                  {operationTab === 'arsiv' ? (
                    <button type="button" onClick={() => setShowTableFilters((prev) => !prev)} className={`rounded-lg border px-3 py-2 text-[11px] font-medium ${showTableFilters ? 'border-blue-700/40 bg-blue-950/40 text-blue-200' : 'border-slate-700/40 bg-[#111111] text-slate-300 hover:bg-white/5'}`}>
                      Filtrele
                    </button>
                  ) : null}
                  {operationTab === 'arsiv' ? (
                    <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); setActiveViewTabId(TRACKING_MAIN_TAB_ID); }} className="h-10 rounded-xl border border-slate-700/40 bg-[#111111] px-3 text-[13px] text-slate-100 outline-none focus:border-orange-400">
                      <option value={10}>10 kayıt</option>
                      <option value={25}>25 kayıt</option>
                      <option value={50}>50 kayıt</option>
                      <option value={100}>100 kayıt</option>
                      <option value={250}>250 kayıt</option>
                    </select>
                  ) : null}
                </div>
              </div>

              {operationTab === 'arsiv' && showColumnPicker ? (
                <div className="rounded-2xl border border-slate-700/30 bg-[#151515] p-3">
                  <div className="mb-2 text-sm font-semibold text-slate-200">Sütun görünürlüğü ve sırası</div>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {[...visibleColumns.map((key) => allColumns.find((column) => String(column.key) === String(key))).filter(Boolean), ...allColumns.filter((column) => !visibleColumns.includes(String(column.key)))].map((column) => {
                      const col = column as ColumnDef;
                      const key = String(col.key);
                      const visible = visibleColumns.includes(key);
                      const index = visibleColumns.indexOf(key);
                      return (
                        <div key={key} className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${visible ? 'border-orange-700/30 bg-[#111111] text-slate-100' : 'border-slate-700/30 bg-[#101010] text-slate-500'}`}>
                          <button
                            type="button"
                            onClick={() => setVisibleColumns((current) => visible ? withRequiredTrackingColumns(current.filter((item) => item !== key)) : withRequiredTrackingColumns([...current, key]))}
                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                          >
                            {visible ? <Eye className="h-4 w-4 shrink-0" /> : <EyeOff className="h-4 w-4 shrink-0" />}
                            <span className="truncate">{col.label || key}</span>
                          </button>
                          {visible ? (
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => setVisibleColumns((current) => {
                                if (index <= 0) return current;
                                const next = [...current];
                                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                return withRequiredTrackingColumns(next);
                              })} disabled={index <= 0} className="rounded-lg border border-slate-700/40 p-1.5 text-slate-300 disabled:opacity-30">
                                <ArrowLeft className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" onClick={() => setVisibleColumns((current) => {
                                if (index === -1 || index >= current.length - 1) return current;
                                const next = [...current];
                                [next[index], next[index + 1]] = [next[index + 1], next[index]];
                                return withRequiredTrackingColumns(next);
                              })} disabled={index === -1 || index >= visibleColumns.length - 1} className="rounded-lg border border-slate-700/40 p-1.5 text-slate-300 disabled:opacity-30">
                                <ArrowRight className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {operationTab === 'arsiv' && showTableFilters ? (
                <div className="rounded-2xl border border-slate-700/30 bg-[#151515] p-3">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <Filter className="h-4 w-4" />
                      Filtre Kuralları
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => { setFilters([]); setActiveViewTabId(TRACKING_MAIN_TAB_ID); }} className="rounded-lg border border-slate-700/40 bg-[#111111] px-3 py-2 text-xs font-medium text-slate-300 hover:bg-white/5">
                        Temizle
                      </button>
                      <button type="button" onClick={addFilterRule} className="rounded-lg border border-blue-700/40 bg-blue-950/40 px-3 py-2 text-xs font-medium text-blue-200 hover:bg-blue-950/60">
                        Yeni Filtre Ekle
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {filters.map((rule, index) => (
                      <div key={rule.id} className="grid gap-2 xl:grid-cols-[90px_220px_180px_minmax(220px,1fr)_48px] xl:items-center">
                        <select value={index === 0 ? 'and' : rule.join} onChange={(e) => updateFilterRule(rule.id, { join: e.target.value as TrackingFilterJoin })} disabled={index === 0} className="h-10 rounded-lg border border-slate-700/40 bg-[#111111] px-2 text-sm text-slate-100 disabled:opacity-60">
                          <option value="and">Ve</option>
                          <option value="or">Veya</option>
                        </select>
                        <select value={String(rule.column)} onChange={(e) => updateFilterRule(rule.id, { column: e.target.value as keyof OperationRow })} className="h-10 rounded-lg border border-slate-700/40 bg-[#111111] px-2 text-sm text-slate-100">
                          {allColumns.filter((column) => column.key !== 'rowKey').map((column) => (
                            <option key={String(column.key)} value={String(column.key)}>{column.label || String(column.key)}</option>
                          ))}
                        </select>
                        <select value={rule.operator} onChange={(e) => updateFilterRule(rule.id, { operator: e.target.value as TrackingFilterOperator })} className="h-10 rounded-lg border border-slate-700/40 bg-[#111111] px-2 text-sm text-slate-100">
                          <option value="contains">İçinde</option>
                          <option value="notContains">İçermiyor</option>
                          <option value="equals">Eşit</option>
                          <option value="notEquals">Eşit Değil</option>
                          {TRACKING_DATE_FILTER_COLUMNS.has(rule.column) ? <option value="dateEquals">Tarih =</option> : null}
                          {TRACKING_DATE_FILTER_COLUMNS.has(rule.column) ? <option value="dateOnOrAfter">Tarih &gt;=</option> : null}
                          {TRACKING_DATE_FILTER_COLUMNS.has(rule.column) ? <option value="dateOnOrBefore">Tarih &lt;=</option> : null}
                          <option value="empty">Boş</option>
                          <option value="notEmpty">Dolu</option>
                        </select>
                        <input value={rule.value} onChange={(e) => updateFilterRule(rule.id, { value: e.target.value })} type={TRACKING_DATE_FILTER_COLUMNS.has(rule.column) && (rule.operator === 'dateEquals' || rule.operator === 'dateOnOrAfter' || rule.operator === 'dateOnOrBefore') ? 'date' : 'text'} disabled={rule.operator === 'empty' || rule.operator === 'notEmpty'} placeholder="Filtre değeri" className="h-10 rounded-lg border border-slate-700/40 bg-[#111111] px-2 text-sm text-slate-100 disabled:opacity-60" />
                        <button type="button" onClick={() => removeFilterRule(rule.id)} className="inline-flex h-10 items-center justify-center rounded-lg border border-rose-700/40 bg-rose-950/40 text-rose-200 hover:bg-rose-950/60">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    {filters.length === 0 ? <div className="rounded-xl border border-dashed border-slate-700/40 bg-[#111111] px-3 py-6 text-center text-sm text-slate-400">Henüz filtre kuralı yok.</div> : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="min-w-0 flex-1">
            {operationTab === 'arsiv' ? (
              <div className="p-0">
                {loading ? (
                  <div className="flex h-full items-center justify-center bg-[#111111] py-24">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div
                    ref={tableContainerRef}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowRight') {
                        e.currentTarget.scrollLeft += 100;
                      } else if (e.key === 'ArrowLeft') {
                        e.currentTarget.scrollLeft -= 100;
                      }
                    }}
                    className="workiom-grid-wrap tracking-grid-strong-borders tracking-grid-compact overflow-x-auto overflow-y-auto outline-none"
                  >
                    <table className="workiom-grid-table tracking-grid-fluid text-[12px]">
                      <thead>
                        <tr>
                          <th className="workiom-grid-index text-left">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              aria-label="Tum satirlari sec"
                              checked={archivePaginatedRows.length > 0 && archivePaginatedRows.every((row) => selectedRowIds.includes(row.id))}
                              onChange={(e) => {
                                const pageIds = archivePaginatedRows.map((row) => row.id);
                                setSelectedRowIds((current) => e.target.checked ? [...new Set([...current, ...pageIds])] : current.filter((id) => !pageIds.includes(id)));
                              }}
                            />
                          </th>
                          {activeColumns.map((column, index) => (
                            <th
                              key={String(column.key)}
                              className={`select-none text-left hover:bg-white/5 ${index === 0 ? 'workiom-grid-sticky-col' : ''}`}
                              style={{ width: getTrackingColumnWidth(column.width), maxWidth: getTrackingColumnWidth(column.width) }}
                            >
                              <div
                                draggable
                                onDragStart={(event) => {
                                  dragColumnRef.current = column.key as keyof OperationRow;
                                  event.dataTransfer.effectAllowed = 'move';
                                  event.dataTransfer.setData('text/plain', String(column.key));
                                }}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  const draggedKey = (event.dataTransfer.getData('text/plain') as keyof OperationRow) || dragColumnRef.current;
                                  const targetKey = String(column.key);
                                  if (!draggedKey || String(draggedKey) === targetKey) return;
                                  setVisibleColumns((current) => {
                                    const fromIndex = current.indexOf(String(draggedKey));
                                    const toIndex = current.indexOf(targetKey);
                                    if (fromIndex < 0 || toIndex < 0) return current;
                                    const next = [...current];
                                    const [moved] = next.splice(fromIndex, 1);
                                    next.splice(toIndex, 0, moved);
                                    return withRequiredTrackingColumns(next);
                                  });
                                  setActiveViewTabId(TRACKING_MAIN_TAB_ID);
                                  dragColumnRef.current = null;
                                }}
                                onDragEnd={() => { dragColumnRef.current = null; }}
                                className="flex cursor-move items-center gap-1"
                                title="Sürükleyip bırakarak kolon yerini değiştirin"
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (sortColumn === column.key) {
                                      setSortDirection((prev) => prev === 'asc' ? 'desc' : 'asc');
                                    } else {
                                      setSortColumn(column.key as keyof OperationRow);
                                      setSortDirection('asc');
                                    }
                                    setActiveViewTabId(TRACKING_MAIN_TAB_ID);
                                  }}
                                  className="inline-flex items-center gap-1 text-left"
                                >
                                  {column.label}
                                  {sortColumn === column.key ? <span className="text-[10px]">{sortDirection === 'asc' ? '↑' : '↓'}</span> : null}
                                </button>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {archivePaginatedRows.map((row, rowIndex) => {
                          const selected = detailRow?.rowKey === row.rowKey;
                          return (
                            <tr
                              key={row.rowKey}
                              className={`${selected ? 'bg-amber-500/10' : ''} cursor-pointer`}
                              onClick={() => { setDetailRow(row); setDetailTab('info'); setShowDetailPanel(true); }}
                              onDoubleClick={() => { void handleEditRow(row); }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setRowContextMenu({ row, x: e.clientX, y: e.clientY });
                              }}
                            >
                              <td className="workiom-grid-index">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300"
                                    checked={selectedRowIds.includes(row.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setSelectedRowIds((current) => e.target.checked ? [...new Set([...current, row.id])] : current.filter((id) => id !== row.id))}
                                  />
                                  <span>{(page - 1) * pageSize + rowIndex + 1}</span>
                                </div>
                              </td>
                              {activeColumns.map((column, colIndex) => (
                                <td key={String(column.key)} className={`${colIndex === 0 ? 'workiom-grid-sticky-col' : ''} ${selected ? 'bg-amber-500/10' : ''}`}>
                                  {column.render ? column.render(row) : (row[column.key] || '-')}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {archiveSearchedRows.length === 0 ? <div className="py-16 text-center text-sm text-gray-500">Seçili süreç için kayıt bulunamadı</div> : null}
                    <WorkiomTablePagination page={page} pageSize={pageSize} totalRows={archiveSearchedRows.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
                  </div>
                )}
              </div>
            ) : (
              <FlexibleTracking
                settingsKeyScope="tracking_embedded"
                title=""
                subtitle=""
                externalRows={tabFilteredRows}
                externalLoading={loading}
                onRowSelect={(row) => {
                  if (row) {
                    setDetailRow(row);
                    setDetailTab('info');
                    setShowDetailPanel(true);
                  }
                }}
              />
            )}
          </div>
        </div>

      {hasVisibleDetailPanel && detailRow && (
        <aside className="rounded-xl border border-slate-700/30 bg-[#151515] shadow-sm overflow-hidden xl:sticky xl:top-4 h-fit">
          <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
            <div>
              <div className="text-[14px] font-semibold text-slate-100">{`Sipariş ${detailRow.sevkKodu}`}</div>
              <div className="mt-1 text-[11px] text-slate-400">Seçili siparişin detayları</div>
            </div>
            <button
              onClick={() => setShowDetailPanel(false)}
              className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-slate-200"
              title="Kapat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="border-b border-slate-700/30 px-5 py-3 bg-[#1a1a1a]">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => handleSendDriverMessage(detailRow)}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Sürücüye SMS Gönder
              </button>
              <button
                type="button"
                onClick={() => handleAddShipmentNote(detailRow)}
                className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Sevkiyat Notu Yaz
              </button>
              {operationTab === 'atama-bekliyor' ? (
                <>
                  {activeBidRequestShipmentIds.includes(String(detailRow.id)) || String(getEffectiveFlowStatus(detailRow) || '').trim() === 'spot-teklif-bekleniyor' ? (
                    <button
                      type="button"
                      onClick={() => void handleRemoveFromOfferCollection(detailRow)}
                      className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-100"
                    >
                      <X className="h-3.5 w-3.5" />
                      Teklif Sürecinden Çıkar
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleSingleMoveToOfferCollection(detailRow)}
                      className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-100"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      Teklif Topla
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openAssignModalForRow(detailRow)}
                    className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Sürücü Ata
                  </button>
                  {canDeleteSupply && <button
                    type="button"
                    onClick={() => handleDeleteRow(detailRow)}
                    className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] font-semibold text-red-700 hover:bg-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Sil
                  </button>}
                </>
              ) : (
                <>
                  {operationTab !== 'atama-bekliyor' && (detailRow.cekici !== '-' || detailRow.surucuAdi !== '-') && hasUserRole(user, 'admin', 'super-admin', 'dispatcher', 'planner', 'operations-manager') && <button
                    type="button"
                    onClick={() => handleUnassignRow(detailRow)}
                    className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100"
                  >
                    Atamayı Geri Al
                  </button>}
                  <button
                    type="button"
                    onClick={() => handleLocationCheck(detailRow)}
                    disabled={locationCheckingRowId === detailRow.id}
                    className="inline-flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-50"
                  >
                    {locationCheckingRowId === detailRow.id ? 'Kontrol...' : 'Konum Kontrol'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveProcess(detailRow, 'prev')}
                    disabled={!getProcessMoveState(detailRow).canRevert}
                    title={!getProcessMoveState(detailRow).canRevert && isSuperAdmin ? 'Süreç geri alınamaz' : undefined}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-700/40 bg-[#111111] px-3 py-1.5 text-[11px] font-semibold text-slate-200 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Önceki Süreç
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveProcess(detailRow, 'next')}
                    disabled={!getProcessMoveState(detailRow).canAdvance}
                    className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {getProcessMoveState(detailRow).canMoveToDeliveryApproval ? 'Teslim Onayına Gönder' : 'Sonraki Süreç'}
                  </button>
                  {PROCESS_PAGE_ORDER.indexOf(operationTab as typeof PROCESS_PAGE_ORDER[number]) >= 9 && operationTab !== 'arsiv' && (
                    <button
                      type="button"
                      onClick={() => void handleOpenArchiveModal(detailRow)}
                      className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100"
                    >
                      <Archive className="h-3.5 w-3.5" />
                      {archiveRecordByShipmentId[String(detailRow.id)] ? 'Fiziksel Arşive Gitti' : 'Fiziksel Arşive Taşı'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="border-b border-slate-700/30 px-3 pt-2">
            <div className="grid grid-cols-4 gap-1">
              {[
                ['info', 'Bilgi'],
                ['goods', 'Yük'],
                ...(showDriverDetailTab ? [['driver', 'Sürücü']] : []),
                ...(showTrackingDetailTab ? [['tracking', 'Takip']] : []),
                ...(showDocumentsTab ? [['documents', 'Evraklar']] : []),
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDetailTab(key as any)}
                  className={`border-b-2 px-2 py-3 text-center text-[11px] ${detailTab === key ? 'border-orange-500 font-semibold text-slate-100' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto p-5">
            {detailTab === 'info' ? (
              <div className="space-y-3 text-[12px]">
                {(() => {
                  const detailItems = [
                    ['Durum', operationTab === 'atama-bekliyor' ? getWaitingStatusLabel(detailRow) : (PROCESS_DETAIL_STATUS_LABELS[String(detailRow.islemDurum || '').trim()] || detailRow.durum || '-')],
                    ['Lojistik Türü', detailRow.lojistikTuru || '-'],
                    ['Yükleme Tarihi', formatDateTime(detailRow.yuklemeTarihi)],
                    ['Termin Tarihi', formatDateTime(detailRow.terminTarihi)],
                    ['Teslim Tipi', detailRow.randevuluTeslim || 'Standart'],
                    ['Çıkış / Yükleme', `${detailRow.yuklemeIli || '-'} ${detailRow.yuklemeIlcesi || ''}`.trim()],
                    ['Yükleyici', detailRow.yuklemeMusterisi || '-'],
                    ['Teslim Müşterisi', detailRow.bosaltmaMusterisi || '-'],
                    ['Yükleme Adresi', detailRow.yuklemeAdres && detailRow.yuklemeAdres !== '-' ? detailRow.yuklemeAdres : `${detailRow.yuklemeIli || '-'}${detailRow.yuklemeIlcesi ? ` / ${detailRow.yuklemeIlcesi}` : ''}`],
                    ['Teslim Adresi', detailRow.teslimAdres && detailRow.teslimAdres !== '-' ? detailRow.teslimAdres : `${detailRow.bosaltmaIli || '-'}${detailRow.teslimIlcesi ? ` / ${detailRow.teslimIlcesi}` : ''}`],
                    ['Uğrama Sayısı', String(detailRow.ugrmaSayisi || 1)],
                    ['Sipariş Notu', detailRow.operasyonNot || '-'],
                    ['Son Sevkiyat Notu', detailRow.sonNot || '-'],
                    ['Son Notu Yazan', detailRow.sonNotYazan || '-'],
                    ['Sürücü', detailRow.surucuAdi || '-'],
                    ['Sürücü Telefon', detailRow.surucuTelefon || '-'],
                    ['Fatura Başlığı', detailRow.faturaBasligi || '-'],
                    ['Dış Nakliye', detailRow.disNakliye || '-'],
                    ['Öncelik', operationTab === 'atama-bekliyor' ? 'Yüksek' : 'Normal'],
                  ] as const;

                  return detailItems.flatMap(([label, value]) => {
                    const items = [
                      <div key={`info-${String(label)}`} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                        <div className="text-[11px] text-slate-400">{label}</div>
                        <div className="mt-1 font-semibold text-slate-100">{value}</div>
                      </div>
                    ];

                    if (label === 'Uğrama Sayısı' && Array.isArray(detailRow.routePoints) && detailRow.routePoints.length > 0) {
                      items.push(
                        <div key="info-route-points" className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <div className="text-[11px] text-slate-400">Ara Nokta Tablosu</div>
                            <a
                              href={buildRouteStopMapsUrl(detailRow)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 rounded-lg border border-blue-700/30 bg-blue-900/20 px-3 py-1.5 text-[11px] font-semibold text-blue-300 hover:bg-blue-800/30"
                            >
                              <MapPinned className="h-3.5 w-3.5" />
                              Rota Goster
                            </a>
                          </div>
                          <div className="overflow-hidden rounded-lg border border-slate-700/40">
                            <table className="w-full text-left text-[12px]">
                              <thead className="bg-white/5 text-slate-300">
                                <tr>
                                  <th className="px-3 py-2">#</th>
                                  <th className="px-3 py-2">Adı</th>
                                  <th className="px-3 py-2">İl</th>
                                  <th className="px-3 py-2">İlçe</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detailRow.routePoints.map((point: any, index: number) => (
                                  <tr key={`${point.id || index}`} className="border-t border-slate-700/30 text-slate-100">
                                    <td className="px-3 py-2">{index + 1}</td>
                                    <td className="px-3 py-2">{point.customer_name || point.location_name || '-'}</td>
                                    <td className="px-3 py-2">{point.city || '-'}</td>
                                    <td className="px-3 py-2">{point.district || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    }

                    return items;
                  });
                })()}
              </div>
            ) : detailTab === 'goods' ? (
              <div className="space-y-3 text-[12px]">
                {[
                  ['Yük Tipi', detailRow.hizmetTuru || '-'],
                  ['Araç / Kasa Tipi', detailRow.istenenKasaTipi || '-'],
                  ['Ağırlık', detailRow.yukAgirlik ? `${Number(detailRow.yukAgirlik).toLocaleString('tr-TR')} Kg` : '-'],
                  ['Hacim', detailRow.yukHacim ? `${Number(detailRow.yukHacim).toLocaleString('tr-TR')} m3` : '-'],
                  ['Palet Sayısı', detailRow.palletCount ? String(detailRow.palletCount) : '-'],
                  ['Navlun', `₺${Number(detailRow.navlun || 0).toLocaleString('tr-TR')}`],
                  ['Sefer Km', detailRow.seferKm ? `${Number(detailRow.seferKm).toLocaleString('tr-TR')} km` : '-'],
                  ['Fatura Başlığı', detailRow.faturaBasligi || '-'],
                  ['Dış Nakliye', detailRow.disNakliye || '-'],
                ].map(([label, value]) => (
                  <div key={String(label)} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                    <div className="text-[11px] text-slate-400">{label}</div>
                    <div className="mt-1 font-semibold text-slate-100">{value}</div>
                  </div>
                ))}
              </div>
            ) : detailTab === 'driver' ? (
              <div className="space-y-3 text-[12px]">
                {[
                  ['Sürücü Adı', detailRow.surucuAdi || '-'],
                  ['Telefon', detailRow.surucuTelefon || '-'],
                  ['Çekici', detailRow.cekici || '-'],
                  ['Dorse', detailRow.dorse || '-'],
                  ['Atama Yanıtı', detailRow.atamaYaniti || '-'],
                  ['Lojistik Türü', detailRow.lojistikTuru || '-'],
                  ['Fatura Başlığı', detailRow.faturaBasligi || '-'],
                  ['Mevcut Konum', detailDriverInsight?.currentAddress || '-'],
                  ['Konum Koordinatı', detailDriverInsight?.currentCoords || '-'],
                  ['Konum Adresi', reverseGeocodedDriverAddress || '-'],
                  ['Hedef Nokta', detailDriverInsight?.targetLabel || '-'],
                  ['Hedef Koordinatı', detailDriverInsight?.targetCoords || '-'],
                  ['Hedefe Uzaklık', detailDriverInsight?.targetDistanceKm != null ? `${detailDriverInsight.targetDistanceKm} km` : '-'],
                  ['Konum Güncelleme', detailDriverInsight?.locationUpdatedAt || '-'],
                  ['Kalan Sürüş Süresi', detailDriverInsight?.remainingDrivePeriod || '-'],
                  ['Toplam Çalışma Süresi', detailDriverInsight?.workDuration || '-'],
                  ['Bu Hafta Sürüş', detailDriverInsight?.currentWeekHours || '-'],
                  ['2 Hafta Sürüş', detailDriverInsight?.twoWeekHours || '-'],
                  ['Günlük Sürüş', detailDriverInsight?.dailyDriveTime || '-'],
                  ['Günlük Çalışma', detailDriverInsight?.dailyWorkTime || '-'],
                  ['Günlük Rölanti', detailDriverInsight?.dailyIdleTime || '-'],
                ].map(([label, value]) => (
                  <div key={String(label)} className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3">
                    <div className="text-[11px] text-slate-400">{label}</div>
                    <div className="mt-1 font-semibold text-slate-100">{value}</div>
                  </div>
                ))}
                {detailDriverInsight?.directionsUrl ? (
                  <a
                    href={detailDriverInsight.directionsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center rounded-xl border border-blue-700/30 bg-blue-900/20 px-4 py-3 text-sm font-semibold text-blue-300 hover:bg-blue-800/30"
                  >
                    Google Maps'te Yol Tarifi Al
                  </a>
                ) : (
                  <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] px-4 py-3 text-sm text-slate-400">
                    Yol tarifi icin mevcut konum ve hedef koordinatlari gerekli.
                  </div>
                )}
              </div>
            ) : detailTab === 'tracking' ? (
              <TrackingTabContent
                operationTab={operationTab}
                waitingRows={waitingRecordsByShipment[detailRow.id] || []}
                liveTick={liveTick}
                formatDateTime={formatDateTime}
                shipmentId={detailRow.id}
                completionDocuments={completionDocuments}
                onPreviewImage={setPreviewImageUrl}
                onUploadDocuments={handleTrackingDocumentUpload}
                onDeleteDocument={handleTrackingDocumentDelete}
                uploadingDocuments={uploadingTrackingDocuments}
                deletingDocumentId={deletingTrackingDocumentId}
                pickupDepartedAt={detailRow.yuklemeCikisTarihi}
                deliveryArrivedAt={detailRow.teslimVarisTarihi}
                paymentCompletedAt={detailRow.odemeTarihi}
                paymentReceiptDocuments={paymentReceiptDocuments}
              />
            ) : detailTab === 'documents' ? (
              <div className="space-y-4 p-5">
                <div>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-200">Yükleme Evrakları</h3>
                    {operationTab !== 'arsiv' && (
                      <label className={`inline-flex cursor-pointer items-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 ${uploadingTrackingDocuments ? 'pointer-events-none opacity-60' : ''}`}>
                        <input
                          type="file"
                          multiple
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={(event) => {
                            handleTrackingDocumentUpload(event.target.files);
                            event.currentTarget.value = '';
                          }}
                        />
                        {uploadingTrackingDocuments ? 'Yükleniyor...' : 'Evrak Yükle'}
                      </label>
                    )}
                  </div>
                  {renderDocumentGrid(completionDocuments, 'blue', 'Henüz evrak yok.')}
                </div>
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-slate-200">Teslim Evrakı</h3>
                  {renderDocumentGrid(deliveryDocuments, 'emerald', 'Henüz teslimat evrağı yok.')}
                </div>
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-slate-200">Eksik / Fazla Evrak</h3>
                  {renderDocumentGrid(shortageSurplusDocuments, 'amber', 'Henüz eksik/fazla evrağı yok.')}
                </div>
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-slate-200">Tutanak</h3>
                  {renderDocumentGrid(incidentDocuments, 'rose', 'Henüz tutanak yok.')}
                </div>
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-slate-200">Fatura Görselleri</h3>
                  {renderDocumentGrid(invoiceDocuments, 'violet', 'Henüz fatura görseli yok.')}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] p-4 text-slate-300">Bu sipariş için yük kaydı bulunmuyor.</div>
            )}
          </div>
        </aside>
      )}
      </div>
      {archiveModalOpen && (archiveShipmentRow || activeArchiveRecord) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-[#151515] shadow-2xl border border-slate-700/30 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
              <div>
                <div className="text-sm text-slate-400">{archiveModalMode === 'view' ? 'Fiziksel Arşiv Kaydı' : archiveModalMode === 'edit' ? 'Fiziksel Arşiv Kaydını Düzenle' : 'Fiziksel Arşive Taşı'}</div>
                <div className="text-lg font-semibold text-slate-100 mt-1">#{archiveShipmentRow?.sevkKodu || activeArchiveRecord?.shipmentNo || '-'}</div>
              </div>
              <button onClick={() => { if (!archiveSaving) setArchiveModalOpen(false); }} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-slate-200"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Arşiv Kodu</label>
                <input value={archivePreviewCode || 'Yükleniyor...'} disabled className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-200" />
                <p className="mt-1 text-[11px] text-slate-500">Bu kod kayıt anında otomatik oluşturulacaktır.</p>
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Klasör No</label>
                <select value={archiveForm.folderName} disabled={archiveModalMode === 'view'} onChange={(e) => setArchiveForm((prev) => ({ ...prev, folderName: e.target.value }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400 disabled:text-slate-300">
                  <option value="">Klasör no seçin</option>
                  {archiveFolderOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Fatura No</label>
                <input value={archiveForm.invoiceNumber} disabled={archiveModalMode === 'view'} onChange={(e) => setArchiveForm((prev) => ({ ...prev, invoiceNumber: e.target.value }))} placeholder="Fatura numarası girin" className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400 disabled:text-slate-300" />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Yakıt Alımı</label>
                <select value={String(archiveForm.hasFuelPurchase)} disabled={archiveModalMode === 'view'} onChange={(e) => setArchiveForm((prev) => ({ ...prev, hasFuelPurchase: e.target.value === 'true' }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400 disabled:text-slate-300">
                  <option value="false">Yok</option>
                  <option value="true">Var</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">PDF Döküman</label>
                <div className="flex flex-wrap items-center gap-2">
                  {archiveModalMode !== 'create' && modalArchivePdfDocs.length > 0 ? (
                    modalArchivePdfDocs.map((doc, idx) => {
                      const fileUrl = resolveFileUrl(doc.filePath);
                      return (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPreviewArchivePdf({ url: fileUrl, name: doc.fileName || 'PDF' }); }}
                            className="rounded-lg border border-blue-700/40 bg-blue-950/40 px-3 py-1.5 text-[12px] font-semibold text-blue-200 hover:bg-blue-950/60"
                          >
                            <Eye className="w-3.5 h-3.5 inline mr-1" />Görüntüle
                          </button>
                          <a href={fileUrl} download className="rounded-lg border border-emerald-700/40 bg-emerald-950/40 px-3 py-1.5 text-[12px] font-semibold text-emerald-200 hover:bg-emerald-950/60">
                            <Download className="w-3.5 h-3.5 inline mr-1" />İndir
                          </a>
                        </div>
                      );
                    })
                  ) : null}
                  {archiveModalMode === 'create' ? (
                    pendingArchiveFile ? (
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] text-slate-300">{pendingArchiveFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setPendingArchiveFile(null)}
                          className="rounded p-1 text-rose-400 hover:bg-rose-950/30"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf,.jpg,.jpeg,.png';
                          input.onchange = () => {
                            const file = input.files?.[0];
                            if (file) setPendingArchiveFile(file);
                          };
                          input.click();
                        }}
                        className="rounded-lg border border-slate-700/40 bg-[#111111] px-3 py-1.5 text-[12px] font-semibold text-slate-200 hover:bg-white/5"
                      >
                        PDF Seç
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      disabled={uploadingModalArchivePdf}
                      onClick={(e) => { e.stopPropagation(); void handleModalArchivePdfUpload(); }}
                      className="rounded-lg border border-slate-700/40 bg-[#111111] px-3 py-1.5 text-[12px] font-semibold text-slate-200 hover:bg-white/5 disabled:opacity-50"
                    >
                      {uploadingModalArchivePdf ? 'Yükleniyor...' : (modalArchivePdfDocs.length > 0 ? 'Değiştir' : 'PDF Yükle')}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Evrak Sıra Numarası</label>
                <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_180px_auto]">
                  <input value={archiveForm.evrakSiraNo} disabled={archiveModalMode === 'view'} onChange={(e) => setArchiveForm((prev) => ({ ...prev, evrakSiraNo: e.target.value.replace(/\D/g, '') }))} placeholder="Sıra numarası girin" className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400 disabled:text-slate-300" />
                  <input value={archiveFolderStartFrom} disabled={archiveModalMode === 'view'} onChange={(e) => setArchiveFolderStartFrom(e.target.value.replace(/\D/g, ''))} placeholder="Başlangıç" className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-[13px] text-slate-100 outline-none focus:border-orange-400 disabled:text-slate-300" />
                  {archiveModalMode === 'create' && (
                    <button
                      type="button"
                      onClick={() => {
                        archiveService.getNextFolderNumber(archiveFolderStartFrom || null)
                          .then((response: any) => setArchiveForm((prev) => ({ ...prev, evrakSiraNo: String(response?.data?.folderNumber || '') })))
                          .catch(() => toast.error('Sıradaki numara alınamadı'));
                      }}
                      className="rounded-xl border border-amber-700/40 bg-amber-950/40 px-4 py-3 text-[12px] font-semibold text-amber-200 hover:bg-amber-950/60"
                    >
                      Sıradaki Numara
                    </button>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">İstersen başlangıç değeri verip sıradaki numarayı oradan türetebilirsin; mevcut eski dosyalar için manuel giriş de serbest.</p>
              </div>
              {archiveModalMode === 'view' && activeArchiveRecord?.archivedAt ? (
                <div className="rounded-xl border border-slate-700/30 bg-[#111111] px-4 py-3 text-[12px] text-slate-300">
                  <div><span className="text-slate-500">Arşivleyen:</span> {activeArchiveRecord.archivedByName || '-'}</div>
                  <div className="mt-1"><span className="text-slate-500">Arşiv Tarihi:</span> {new Date(activeArchiveRecord.archivedAt).toLocaleString('tr-TR')}</div>
                </div>
              ) : null}
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-700/30 bg-[#1a1a1a] px-5 py-4">
              <button type="button" onClick={() => { if (!archiveSaving) setArchiveModalOpen(false); }} disabled={archiveSaving} className="rounded-lg border border-slate-700/40 px-4 py-2 text-slate-200 hover:bg-white/5">{archiveModalMode === 'view' ? 'Kapat' : 'Vazgeç'}</button>
              {archiveModalMode !== 'view' && (
                <button type="button" onClick={handleArchiveSubmit} disabled={archiveSaving} className="rounded-lg bg-amber-600 px-4 py-2 text-white disabled:opacity-50">{archiveSaving ? (archiveModalMode === 'edit' ? 'Güncelleniyor...' : 'Arşivleniyor...') : (archiveModalMode === 'edit' ? 'Kaydet' : 'Arşive Taşı')}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showAssignModal && assignRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-[96vw] rounded-xl bg-[#151515] shadow-2xl border border-slate-700/30 overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
              <div>
                <div className="text-sm text-slate-400">Sürücü Atama</div>
                <div className="text-2xl font-semibold text-slate-100 mt-1">#{assignRow.sevkKodu}</div>
              </div>
              <button onClick={() => setShowAssignModal(false)} className="rounded-lg p-2 hover:bg-white/5 text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              <div className="rounded-xl border border-slate-700/30 bg-[#1a1a1a] p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                  <div className="rounded-xl bg-[#111111] border border-slate-700/30 p-3">
                    <div className="text-xs text-slate-400">Rota</div>
                    <div className="mt-1 text-sm font-semibold text-slate-100">{`${assignRow.yuklemeIli || '-'} -> ${assignRow.bosaltmaIli || '-'}`}</div>
                    <div className="mt-1 text-xs text-slate-400">{assignRow.yuklemeMusterisi || '-'} / {assignRow.bosaltmaMusterisi || '-'}</div>
                  </div>
                  <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                    <div className="text-xs text-slate-400">Mesafe</div>
                    <div className="mt-1 text-sm font-semibold text-slate-100">{assignRow.seferKm || 0} km</div>
                  </div>
                  <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                    <div className="text-xs text-slate-400">Yük Ağırlığı</div>
                    <div className="mt-1 text-sm font-semibold text-slate-100">{Number(assignRow.yukAgirlik || 0).toLocaleString('tr-TR')} kg</div>
                  </div>
                  <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                    <div className="text-xs text-slate-400">İstenen Kasa Tipi</div>
                    <div className="mt-1 text-sm font-semibold text-slate-100">{assignRow.istenenKasaTipi || '-'}</div>
                  </div>
                  <div className="rounded-xl border border-slate-700/30 bg-[#111111] p-3">
                    <div className="text-xs text-slate-400">Yükleme Talep Tarihi</div>
                    <div className="mt-1 text-sm font-semibold text-slate-100">{formatDateTime(assignRow.yuklemeTarihi)}</div>
                  </div>
                </div>
                {Number(assignRow.seferKm || 0) > 0 && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                      <div className="text-xs text-blue-600 font-semibold">Tahmini Özmal Maliyet</div>
                      <div className="mt-1 text-lg font-bold text-blue-900">{(Number(assignRow.seferKm) * kmCosts.ozmal).toLocaleString('tr-TR')} ₺</div>
                      <div className="text-xs text-blue-500">{kmCosts.ozmal.toLocaleString('tr-TR')} ₺/km × {assignRow.seferKm} km</div>
                    </div>
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                      <div className="text-xs text-amber-600 font-semibold">Tahmini Spot Maliyet</div>
                      <div className="mt-1 text-lg font-bold text-amber-900">{(Number(assignRow.seferKm) * kmCosts.spot).toLocaleString('tr-TR')} ₺</div>
                      <div className="text-xs text-amber-500">{kmCosts.spot.toLocaleString('tr-TR')} ₺/km × {assignRow.seferKm} km</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setAssignType('own-fleet')} className={`rounded-xl border px-4 py-3 text-left ${assignType === 'own-fleet' ? 'border-orange-500/40 bg-orange-950/30 text-orange-100' : 'border-slate-700/40 bg-[#111111] text-slate-200'}`}>
                  <div className="font-semibold">Özmal</div>
                  <div className={`mt-1 text-xs ${assignType === 'own-fleet' ? 'text-orange-200' : 'text-slate-400'}`}>Araç seçerek atama yap</div>
                </button>
                <button type="button" onClick={() => { setAssignType('spot'); setAgreedPrice((current) => String(current || '').trim() ? current : getSuggestedSpotAgreedPrice(assignRow)); }} className={`rounded-xl border px-4 py-3 text-left ${assignType === 'spot' ? 'border-blue-700/40 bg-blue-950/40 text-blue-100' : 'border-slate-700/40 bg-[#111111] text-slate-200'}`}>
                  <div className="font-semibold">Spot</div>
                  <div className={`mt-1 text-xs ${assignType === 'spot' ? 'text-blue-200' : 'text-slate-400'}`}>Sürücü ve anlaşma fiyatı gir</div>
                </button>
              </div>

              {assignType === 'own-fleet' ? (
                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-sm font-medium text-slate-200">Özmal Araç ve Sürücü Seçimi</label>
                      <button type="button" onClick={() => window.open('/drivers', '_blank', 'noopener,noreferrer')} className="rounded-lg border border-slate-700/40 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/5">+ Yeni Özmal Sürücü</button>
                  </div>
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-emerald-950/60 px-2.5 py-1 font-semibold text-emerald-300">Uygun Özmal: {fleetOptions.filter((item) => item.isAssignable).length}</span>
                    <span className="rounded-full bg-slate-800 px-2.5 py-1 font-semibold text-slate-200">Toplam: {fleetOptions.length}</span>
                    <span className="rounded-full bg-amber-950/60 px-2.5 py-1 font-semibold text-amber-300">Devam Eden Görevli: {fleetOptions.filter((item) => item.hasActiveTask).length}</span>
                  </div>
                  <input
                    value={fleetSearch}
                    onChange={(e) => setFleetSearch(e.target.value)}
                    placeholder="Sürücü, çekici, dorse, kasa tipi veya konum ara"
                    className="mb-3 w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100"
                  />
                    <div className="max-h-80 overflow-auto rounded-xl border border-slate-700/30 bg-[#111111]">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-[#1a1a1a]">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Sürücü</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Telefon</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Çekici</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Dorse</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Kasa Tipi</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Atamaya Uygun</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Aktiflik</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Devam Eden Görev</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Kalan Çalışma</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Çalışma Süresi</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Haftalık</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">2 Hafta</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Mevcut Konum</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Yüklemeye Km</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Seç</th>
                          </tr>
                        </thead>
                      <tbody>
                        {fleetOptions.map((item) => {
                          const selected = selectedVehicleId === item.vehicleId;
                          return (
                            <tr key={item.vehicleId} className={selected ? 'bg-blue-950/30' : 'bg-[#111111]'}>
                              <td className="px-3 py-2 font-medium text-slate-100">{item.driverName}</td>
                              <td className="px-3 py-2 text-slate-300">{item.driverPhone || '-'}</td>
                              <td className="px-3 py-2 text-slate-300">{item.tractorPlate}</td>
                              <td className="px-3 py-2 text-slate-300">{item.trailerPlate}</td>
                              <td className="px-3 py-2 text-slate-300">{item.vehicleType}</td>
                              <td className="px-3 py-2 text-slate-300">
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isAssignable ? 'bg-emerald-950/60 text-emerald-300' : 'bg-rose-950/60 text-rose-300'}`}>
                                  {item.isAssignable ? 'Uygun' : 'Uygun Değil'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-slate-300">
                                <div className="flex flex-col gap-1">
                                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isVehicleActive ? 'bg-emerald-950/60 text-emerald-300' : 'bg-rose-950/60 text-rose-300'}`}>Araç {item.isVehicleActive ? 'Aktif' : 'Pasif'}</span>
                                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isDriverActive ? 'bg-emerald-950/60 text-emerald-300' : 'bg-rose-950/60 text-rose-300'}`}>Sürücü {item.isDriverActive ? 'Aktif' : 'Pasif'}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-slate-300">
                                {item.hasActiveTask ? (
                                  <div className="max-w-[220px] space-y-1 text-xs text-amber-300">
                                    <div className="font-semibold">{item.activeTaskCount} aktif görev</div>
                                    <div className="truncate" title={item.activeTaskCodes.join(', ')}>{item.activeTaskCodes.join(', ')}</div>
                                  </div>
                                ) : (
                                  <span className="inline-flex rounded-full bg-emerald-950/60 px-2.5 py-1 text-xs font-semibold text-emerald-300">Görev Yok</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-slate-300">{item.remainingWorkTime}</td>
                              <td className="px-3 py-2 text-slate-300">{item.workDuration}</td>
                              <td className="px-3 py-2 text-slate-300">{item.currentWeekHours}</td>
                              <td className="px-3 py-2 text-slate-300">{item.twoWeekHours}</td>
                              <td className="max-w-[280px] truncate px-3 py-2 text-slate-300" title={item.currentAddress}>{item.currentAddress}</td>
                              <td className="px-3 py-2 text-slate-300">
                                {item.distanceToPickupKm != null ? (
                                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getDistanceBadgeClass(item.distanceToPickupKm)}`}>
                                    {item.distanceToPickupKm} km
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="px-3 py-2">
                                <button type="button" onClick={() => setSelectedVehicleId(item.vehicleId)} className={`rounded-lg px-3 py-1.5 text-xs font-medium ${selected ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'}`}>
                                  {selected ? 'Seçildi' : 'Seç'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {assignmentSourcesLoading ? (
                          <tr>
                            <td colSpan={15} className="px-3 py-6 text-center text-slate-400">Özmal araçlar yükleniyor...</td>
                          </tr>
                        ) : fleetOptions.length === 0 && (
                          <tr>
                            <td colSpan={15} className="px-3 py-6 text-center text-slate-400">Gösterilecek özmal araç bulunamadı</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-sm font-medium text-slate-200">Spot Sürücü Seçimi</label>
                      <button type="button" onClick={() => setShowCreateSpotDriverModal(true)} className="rounded-lg border border-slate-700/40 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/5">+ Yeni Spot Sürücü</button>
                    </div>
                    <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <select value={selectedSpotCompanyId} onChange={(e) => setSelectedSpotCompanyId(e.target.value)} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100">
                        <option value="all">Tüm Spot Kaynakları</option>
                        <option value="individual">Bireysel Spot Sürücüler</option>
                        {corporateCarrierAccounts.map((item: any) => (
                          <option key={item.id} value={String(item.id)}>{item.full_name || item.username || `Firma #${item.id}`}</option>
                        ))}
                      </select>
                      <input
                        value={fleetSearch}
                        onChange={(e) => setFleetSearch(e.target.value)}
                        placeholder="Sürücü, telefon, çekici, dorse, kasa tipi veya konum ara"
                        className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100"
                      />
                    </div>
                    <div className="max-h-80 overflow-auto rounded-xl border border-slate-700/30 bg-[#111111]">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-[#1a1a1a]">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Firma</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Sürücü</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Telefon</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Çekici</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Dorse</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Kasa Tipi</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Uyumluluk</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Mevcut Konum</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Yüklemeye Km</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Seç</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-300">Giriş Yap</th>
                          </tr>
                        </thead>
                        <tbody>
                          {spotOptions.map((item) => {
                            const selected = selectedApplicationId === item.applicationId;
                            return (
                              <tr key={item.applicationId} className={selected ? 'bg-blue-950/30' : 'bg-[#111111]'}>
                                <td className="px-3 py-2 text-slate-300">{item.externalTransportAccountName || <span className="text-slate-500">Bireysel</span>}</td>
                                <td className="px-3 py-2 font-medium text-slate-100">{item.driverName}</td>
                                <td className="px-3 py-2 text-slate-300">{item.phone}</td>
                                <td className="px-3 py-2 text-slate-300">{item.tractorPlate}</td>
                                <td className="px-3 py-2 text-slate-300">{item.trailerPlate}</td>
                                <td className="px-3 py-2 text-slate-300">{item.vehicleType}</td>
                                <td className="px-3 py-2 text-slate-300">
                                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.isCompatible ? 'bg-emerald-950/60 text-emerald-300' : 'bg-rose-950/60 text-rose-300'}`}>
                                    {item.isCompatible ? 'Uyumlu' : 'Uyumsuz'}
                                  </span>
                                </td>
                                <td className="max-w-[220px] truncate px-3 py-2 text-slate-300" title={item.currentAddress}>{item.currentAddress}</td>
                                <td className="px-3 py-2 text-slate-300">
                                  {item.distanceToPickupKm != null ? (
                                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getDistanceBadgeClass(item.distanceToPickupKm)}`}>
                                      {item.distanceToPickupKm} km
                                    </span>
                                  ) : (
                                    <span className="inline-flex rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">Konum Yok</span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <button type="button" onClick={() => setSelectedApplicationId(item.applicationId)} className={`rounded-lg px-3 py-1.5 text-xs font-medium ${selected ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'}`}>
                                    {selected ? 'Seçildi' : 'Seç'}
                                  </button>
                                </td>
                                <td className="px-3 py-2">
                                  {item.userId ? (
                                    <button
                                      type="button"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!window.confirm(`${item.driverName} hesabına geçilsin mi?`)) return;
                                        const response = await impersonateUser(item.userId);
                                        if (!response.success) {
                                          toast.error(response.message || 'Kullanıcı oturumu açılamadı');
                                          return;
                                        }
                                        const effectiveRoles = Array.from(new Set([response.user?.role, ...(response.user?.roles || [])].filter(Boolean)));
                                        window.location.href = effectiveRoles.includes('internal-driver') || effectiveRoles.includes('external-driver') ? '/driver-mobile-home' : '/';
                                      }}
                                      className="rounded-lg border border-emerald-700/40 bg-emerald-950/40 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-200 hover:bg-emerald-950/60"
                                      title="Bu kullanıcı olarak giriş yap"
                                    >
                                      Giriş Yap
                                    </button>
                                  ) : (
                                    <span className="text-[11px] text-slate-600">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {assignmentSourcesLoading ? (
                            <tr>
                              <td colSpan={11} className="px-3 py-6 text-center text-slate-400">Spot sürücüler yükleniyor...</td>
                            </tr>
                          ) : spotOptions.length === 0 && (
                            <tr>
                              <td colSpan={11} className="px-3 py-6 text-center text-slate-400">Gösterilecek spot sürücü bulunamadı</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-200">Anlaşılan Fiyat</label>
                    <input value={agreedPrice} onChange={(e) => setAgreedPrice(e.target.value)} type="number" className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Örn: 24500" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-700/30 bg-[#1a1a1a] px-5 py-4">
              <button type="button" onClick={() => setShowAssignModal(false)} className="rounded-lg border border-slate-700/40 px-4 py-2 text-slate-200 hover:bg-white/5">Vazgeç</button>
              <button type="button" onClick={submitAssignment} disabled={assigning} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{assigning ? 'Atanıyor...' : 'Ata ve Süreci Başlat'}</button>
            </div>
          </div>
        </div>
      )}

      {showCreateSpotDriverModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-700/40 bg-[#1a1a1a] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
              <div>
                <div className="text-lg font-semibold text-slate-100">Yeni Spot Sürücü</div>
                <div className="mt-1 text-sm text-slate-400">Kayıt onaylı oluşturulur ve listeye hemen eklenir.</div>
              </div>
              <button type="button" onClick={() => { if (!creatingSpotDriver) { setShowCreateSpotDriverModal(false); resetNewSpotDriverForm(); } }} className="rounded-lg border border-slate-700/40 px-3 py-1.5 text-slate-300 hover:bg-white/5">Kapat</button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-200">Ad Soyad</label>
                <input value={newSpotDriverForm.full_name} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, full_name: normalizeUpper(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Örn: AHMET YILMAZ" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Telefon</label>
                <input value={newSpotDriverForm.phone} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, phone: normalizePhoneInput(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Örn: 5321234567" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Kasa Tipi</label>
                <select value={newSpotDriverForm.vehicle_type} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, vehicle_type: e.target.value }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100">
                  <option value="">Kasa tipi seçin</option>
                  {spotVehicleTypeOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Çekici Plaka</label>
                <input value={newSpotDriverForm.cekici_plaka} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, cekici_plaka: normalizePlate(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Örn: 34ABC123" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Dorse Plaka</label>
                <input value={newSpotDriverForm.dorse_plaka} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, dorse_plaka: normalizePlate(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Örn: 34XYZ456" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">İl</label>
                <input value={newSpotDriverForm.city} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, city: normalizeUpper(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Örn: İSTANBUL" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">İlçe</label>
                <input value={newSpotDriverForm.district} onChange={(e) => setNewSpotDriverForm((current) => ({ ...current, district: normalizeUpper(e.target.value) }))} className="w-full rounded-xl border border-slate-700/40 bg-[#111111] px-4 py-3 text-slate-100" placeholder="Örn: TUZLA" />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-700/30 px-5 py-4">
              <button type="button" onClick={() => { setShowCreateSpotDriverModal(false); resetNewSpotDriverForm(); }} disabled={creatingSpotDriver} className="rounded-lg border border-slate-700/40 px-4 py-2 text-slate-200 hover:bg-white/5 disabled:opacity-50">Vazgeç</button>
              <button type="button" onClick={submitNewSpotDriver} disabled={creatingSpotDriver} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{creatingSpotDriver ? 'Kaydediliyor...' : 'Sürücüyü Ekle'}</button>
            </div>
          </div>
        </div>
      )}

      <ShipmentWizardModal
        isOpen={wizardOpen}
        onClose={() => { setWizardOpen(false); setEditingWizardShipment(null); }}
        onCreated={loadRows}
        editingShipment={editingWizardShipment}
      />

      {offerCollectionLoading ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-violet-600" />
            <div className="mt-4 text-lg font-semibold text-slate-900">Teklif talebi oluşturuluyor</div>
            <div className="mt-2 text-sm text-slate-500">{offerCollectionLoadingCode ? `#${offerCollectionLoadingCode} için işlem sürüyor. Lütfen bekleyin.` : 'Lütfen bekleyin.'}</div>
          </div>
        </div>
      ) : null}

      {offerCollectionConfirmRows.length > 0 ? (
        <div className="fixed inset-0 z-[94] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="text-lg font-semibold text-slate-900">Teklif Talebi Oluştur</div>
            <div className="mt-2 text-sm text-slate-600">{offerCollectionConfirmRows.length === 1 ? `#${offerCollectionConfirmRows[0].sevkKodu} sevkiyatı teklif toplamaya alınsın mı?` : `${offerCollectionConfirmRows.length} kayıt teklif toplamaya alınsın mı?`}</div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => !offerCollectionLoading && setOfferCollectionConfirmRows([])} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
              <button type="button" disabled={offerCollectionLoading} onClick={() => offerCollectionConfirmRows.length === 1 ? void executeSingleOfferCollection(offerCollectionConfirmRows[0]) : void confirmOfferCollection()} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">{offerCollectionLoading ? 'Açılıyor...' : 'Teklif Topla'}</button>
            </div>
          </div>
        </div>
      ) : null}

      <BulkShipmentImportModal
        isOpen={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onCreated={loadRows}
      />

      {rowContextMenu && (
        <div
          className="fixed z-[80] min-w-[260px] overflow-hidden rounded-xl border border-slate-700/30 bg-[#151515] shadow-2xl"
          style={{ left: rowContextMenu.x, top: rowContextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-slate-700/30 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Durum Değiştir</div>
            <div className="mt-1 text-sm font-semibold text-slate-100">#{rowContextMenu.row.sevkKodu}</div>
          </div>
          <div className="py-2">
            {MANUAL_PROCESS_STATUS_OPTIONS.map((option) => {
              const active = String(rowContextMenu.row.islemDurum || '').trim() === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleManualProcessStatusChange(rowContextMenu.row, option.value)}
                  disabled={updatingRowStatusId === rowContextMenu.row.id}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${active ? 'bg-amber-950/40 text-amber-200' : 'text-slate-200 hover:bg-white/5'} disabled:opacity-50`}
                >
                  <span>{option.label}</span>
                  {active ? <span className="text-[11px] font-semibold">Mevcut</span> : null}
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-700/30 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Muhasebe Aşaması</div>
          </div>
          <div className="py-2">
            {MANUAL_ACCOUNTING_STATUS_OPTIONS.map((option) => {
              const active = String(rowContextMenu.row.accountingStatus || '').trim() === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleManualAccountingStatusChange(rowContextMenu.row, option.value)}
                  disabled={updatingRowStatusId === rowContextMenu.row.id}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${active ? 'bg-blue-950/40 text-blue-200' : 'text-slate-200 hover:bg-white/5'} disabled:opacity-50`}
                >
                  <span>{option.label}</span>
                  {active ? <span className="text-[11px] font-semibold">Mevcut</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {previewImageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
        >
          <div className="relative max-h-[90vh] max-w-[90vw]">
            <img src={previewImageUrl} alt="Önizleme" className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewImageUrl(null); }}
              className="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#111111] text-slate-200 shadow-lg hover:bg-[#1a1a1a]"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      {previewArchivePdf && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
        >
          <div
            className="w-full max-w-4xl max-h-[90vh] rounded-xl bg-[#151515] border border-slate-700/30 overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-700/30 px-5 py-4">
              <div className="text-sm font-semibold text-slate-100 truncate">{previewArchivePdf.name}</div>
              <button type="button" onClick={() => setPreviewArchivePdf(null)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-900 p-4 flex items-center justify-center min-h-[60vh]">
              {previewArchivePdf.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img src={previewArchivePdf.url} alt={previewArchivePdf.name} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
              ) : (
                <embed src={previewArchivePdf.url} title={previewArchivePdf.name} className="w-full h-[70vh] rounded-lg bg-white" type="application/pdf" />
              )}
            </div>
            <div className="flex justify-end border-t border-slate-700/30 px-5 py-3">
              <a
                href={previewArchivePdf.url}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg border border-blue-700/40 bg-blue-950/40 px-4 py-2 text-sm font-semibold text-blue-200 hover:bg-blue-950/60"
              >
                Yeni Sekmede Aç
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
